import React, { useState, useEffect, useRef, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import * as XLSX from "xlsx";
import API_BASE_URL from "../../config/api";
import {
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  makeStyles,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  Tooltip,
} from "@material-ui/core";
import {
  Info as InfoIcon,
  GetApp as GetAppIcon,
} from "@material-ui/icons";
import { useLocation, useNavigate } from "react-router-dom";
import DatePickerField from "../DatePickerField"; // Import your custom DatePickerField
import { COLORS } from "../UserDashboard/dashboardConfig";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import pl from "date-fns/locale/pl"; // Import the Polish locale
import MwNowyHandlingTable from "./MwNowyHandling/MwNowyHandlingTable";
import MwNowyHandlingSingle from "./MwNowyHandling/MwNowyHandlingSingle";
import {
  MW_NOWY_HANDLING_COLUMNS,
  buildCompanyDict,
  normalizeCompany,
  parseMwNowyHandlingPaste,
  validateRows,
} from "./MwNowyHandling/schema";

registerLocale("pl", pl); // Register the Polish locale

// Komponent pomocniczy dla tooltipa przy Nr. Faktury
const InvoiceInfoTooltip = ({ templateMode = false }) => {
  const isTemplateContext =
    templateMode ||
    (typeof window !== "undefined" &&
      (window.location.search.includes("templateBuilder=1") ||
        window.location.pathname.includes("/form/templates")));

  return (
    <Tooltip
      title={
        isTemplateContext
          ? "Tryb szablonu: zapisywana jest tylko ilość faktur. Numery faktur nie są częścią szablonu."
          : "Możesz wpisać wartość NIEZNANY, wtedy walidacja nie zostanie uruchomiona dla nr faktur."
      }
      arrow
      placement="right"
      PopperProps={{
        modifiers: {
          flip: { enabled: false },
        },
      }}
    >
      <InfoIcon
        style={{
          fontSize: "18px",
          color: "#1976d2",
          cursor: "help",
          marginLeft: "6px",
          verticalAlign: "middle",
        }}
      />
    </Tooltip>
  );
};

const TemplateDynamicFieldTooltip = () => (
  <Tooltip
    title="Pole dynamiczne. Szablony nie obsługują tej wartości."
    arrow
    placement="right"
    PopperProps={{
      modifiers: {
        flip: { enabled: false },
      },
    }}
  >
    <InfoIcon
      style={{
        fontSize: "18px",
        color: "#9e9e9e",
        cursor: "help",
        marginLeft: "6px",
        verticalAlign: "middle",
      }}
    />
  </Tooltip>
);

const TemplateInvoiceBanner = () => (
  <div
    style={{
      backgroundColor: "#fff8e1",
      border: "1px solid #ffcc80",
      borderRadius: "6px",
      padding: "10px 12px",
      marginBottom: "12px",
      color: "#8a4b08",
      fontSize: "0.9rem",
      fontWeight: 500,
    }}
  >
    Tryb szablonu: zapisywana jest tylko ilość faktur. Numery faktur pozostają
    dynamiczne i nie są zapisywane w szablonie.
  </div>
);

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      width: miniDrawerWidth,
    },
  },
  drawerPaper: {
    width: drawerWidth,
    overflowX: "hidden",
    [theme.breakpoints.down("sm")]: {
      width: miniDrawerWidth,
    },
  },
  drawerItem: {
    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      justifyContent: "center",
    },
  },
  drawerItemIcon: {
    [theme.breakpoints.down("sm")]: {
      minWidth: 0,
    },
  },
  drawerItemText: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  content: {
    flexGrow: 1,
    minWidth: 0,
    padding: theme.spacing(3),
    overflowX: "auto",
  },
  formContainer: {
    maxWidth: "900px",
    width: "100%",
    margin: "0 auto",
    marginTop: theme.spacing(1),
    padding: theme.spacing(3, 4, 4),
    background: "#fff",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    // Wygląd kontrolek formularza zgodny z nowym designem (bez zmiany logiki pól).
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      background: "#fafafa",
      transition: "background-color 0.2s, box-shadow 0.2s",
      "&:hover": { background: "#f5f5f5" },
      "&.Mui-focused": {
        background: "#fff",
        boxShadow: "0 0 0 3px rgba(25,118,210,0.15)",
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.border },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#bdbdbd" },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: COLORS.primary,
        borderWidth: 1,
      },
    },
    "& .MuiInputLabel-outlined.Mui-focused, & .MuiFormLabel-root.Mui-focused": {
      color: COLORS.primary,
    },
    "& .MuiFormLabel-legend, & legend.MuiFormLabel-root": {
      fontSize: 13,
      fontWeight: 700,
      color: COLORS.primaryDark,
      marginBottom: theme.spacing(0.5),
    },
    "& .MuiRadio-colorPrimary.Mui-checked, & .MuiCheckbox-colorPrimary.Mui-checked": {
      color: COLORS.primary,
    },
    "& .MuiButton-root": {
      textTransform: "none",
      fontWeight: 700,
      borderRadius: 8,
      boxShadow: "none",
    },
    "& .MuiButton-containedPrimary": {
      backgroundColor: COLORS.primary,
      "&:hover": { backgroundColor: COLORS.primaryDark, boxShadow: "none" },
    },
    "& .MuiButton-outlinedPrimary": {
      borderColor: COLORS.primary,
      color: COLORS.primary,
      "&:hover": { backgroundColor: COLORS.primarySoft },
    },
  },
  formTitle: {
    margin: 0,
    textAlign: "left",
    fontSize: 28,
    fontWeight: 800,
    color: COLORS.primaryDark,
    lineHeight: 1.2,
  },
  formSubtitle: {
    marginTop: 4,
    marginBottom: theme.spacing(3),
    fontSize: 14,
    color: COLORS.textMuted,
  },
  marginBottom: {
    marginBottom: theme.spacing(2), // Add a margin bottom of 16px to all inputs
  },
  marginUpAndDown: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  stickySearchBox: {
    position: "sticky",
    top: 0,
    backgroundColor: "white",
    padding: "1rem",
    zIndex: 1,
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
    transition: "transform 0.3s ease, opacity 0.3s ease",
  },
  stickySearchBoxHidden: {
    transform: "translateY(-100%)",
    opacity: 0,
  },
}));
const drawerWidth = 220;
const miniDrawerWidth = 60;
const BASE_URL = API_BASE_URL;

const Form = ({
  embedded = false,
  templateBuilderMode = false,
  forcedTemplateId = null,
  onTemplateSave = null,
  templateSaveInProgress = false,
}) => {
  const classes = useStyles();
  setDefaultLocale("pl"); // Set the default locale to Polish
  const [isSubmitting, setIsSubmitting] = useState(false);

  // const [generatedRefNumber, setGeneratedRefNumber] = useState(null);
  const [importerFilteredList, setImporterFilteredList] = useState([]);
  const [exporterFilteredList, setExporterFilteredList] = useState([]);
  const [uzytkownicyList, setUzytkownicyList] = useState([]);
  const [lokalizacjaList, setLokalizacjaList] = useState([]);
  const [urzadTranzytowyList, setUrzadTranzytowyList] = useState([]);
  const [urzadPrzeznaczeniaList, setUrzadPrzeznaczeniaList] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isTemplateBuilderMode =
    templateBuilderMode || searchParams.get("templateBuilder") === "1";
  const [listType, setListType] = useState(null); // Track if it's Importer or Eksporter list
  const [listOpen, setListOpen] = useState(false); // Track if the list is open
  const [automatChecked, setAutomatChecked] = useState(false);

  // States for managing multiple MAN exporter dropdowns
  const [openManExporterDropdowns, setOpenManExporterDropdowns] = useState({}); // Track which MAN exporter dropdowns are open
  const [manExporterSearchTexts, setManExporterSearchTexts] = useState({}); // Track search text for each MAN exporter field

  // State for Magazyn Warszawa - Zwroty i Błędy entry type selection
  const [magazynZwrotyEntryType, setMagazynZwrotyEntryType] = useState("");

  // State for Magazyn Warszawa Handling entry type selection
  const [magazynHandlingEntryType, setMagazynHandlingEntryType] = useState("");

  // State for Magazyn Warszawa (Nowy Handling) - wybór jeden/wiele + liczba wpisów
  const [mwNowyHandlingEntryType, setMwNowyHandlingEntryType] = useState("");
  const [mwNowyHandlingCount, setMwNowyHandlingCount] = useState("");
  const [mwNowyHandlingRows, setMwNowyHandlingRows] = useState([]);
  const [mwNowyHandlingSingleRow, setMwNowyHandlingSingleRow] = useState({});
  const [mwNowyHandlingPasteText, setMwNowyHandlingPasteText] = useState("");
  const [mwNowyHandlingRevealSignal, setMwNowyHandlingRevealSignal] =
    useState(0);

  // States for search functionality
  const [importerSearchText, setImporterSearchText] = useState("");
  const [importerFullList, setImporterFullList] = useState([]); // Store the full list
  const [exporterSearchText, setExporterSearchText] = useState("");
  const [exporterFullList, setExporterFullList] = useState([]);

  // States for MAN BUS exporters
  const [manExportersList, setManExportersList] = useState([]);
  const [isManBusSelected, setIsManBusSelected] = useState(false);

  // States for Magazyn Warszawa companies
  const [magazynWarszawaCompaniesList, setMagazynWarszawaCompaniesList] =
    useState([]);
  const [magazynWarszawaFilteredList, setMagazynWarszawaFilteredList] =
    useState([]);
  const [magazynWarszawaSearchText, setMagazynWarszawaSearchText] =
    useState("");

  // Refs for dropdown containers
  const importerDropdownRef = useRef(null);
  const exporterDropdownRef = useRef(null);
  const hiddenInvoiceNumbersRef = useRef({});
  const lastAppliedTemplateIdRef = useRef(null);

  // Function to add a new set of "Nr. Tranzytu" and "Data wygaśnięcia dokumentów" fields
  const addFields = () => {
    setFormData((prevData) => ({
      ...prevData,
      fields: [
        ...prevData.fields,
        {
          eksporter: "",
          importer: "",
          nrTranzytu: "",
          expirationDate: "",
          invoiceNumbers: [""],
          statusRealizacji: "Do przygotowania",
        },
      ],
    }));
  };

  // Function to remove a specific set of fields by index
  const removeFields = (index) => {
    if (index > 0) {
      const updatedFields = [...formData.fields];
      updatedFields.splice(index, 1);
      setFormData({ ...formData, fields: updatedFields });

      // Clean up and renumber hidden invoice values for removed field
      const hiddenInvoices = { ...hiddenInvoiceNumbersRef.current };
      delete hiddenInvoices[index];
      Object.keys(hiddenInvoices).forEach((key) => {
        if (parseInt(key, 10) > index) {
          hiddenInvoices[parseInt(key, 10) - 1] = hiddenInvoices[key];
          delete hiddenInvoices[key];
        }
      });
      hiddenInvoiceNumbersRef.current = hiddenInvoices;

      // Clean up MAN exporter dropdowns state for removed field
      setOpenManExporterDropdowns((prev) => {
        const newState = { ...prev };
        delete newState[index];
        // Renumber remaining fields
        Object.keys(newState).forEach((key) => {
          if (parseInt(key) > index) {
            newState[parseInt(key) - 1] = newState[key];
            delete newState[key];
          }
        });
        return newState;
      });

      setManExporterSearchTexts((prev) => {
        const newState = { ...prev };
        delete newState[index];
        // Renumber remaining fields
        Object.keys(newState).forEach((key) => {
          if (parseInt(key) > index) {
            newState[parseInt(key) - 1] = newState[key];
            delete newState[key];
          }
        });
        return newState;
      });
    }
  };

  // Function to handle changes in a specific set of fields by index
  const handleFieldsChange = (fieldName, value, index) => {
    const updatedFields = [...formData.fields];
    updatedFields[index][fieldName] = value;
    setFormData({ ...formData, fields: updatedFields });

    // Walidacja daty wygaśnięcia T1
    if (fieldName === "expirationDate") {
      if (value) {
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        selectedDate.setHours(0, 0, 0, 0);

        if (selectedDate < today) {
          setExpirationDateErrors((prev) => ({
            ...prev,
            [index]:
              "Data wygaśnięcia T1 nie może być wcześniejsza niż dzisiaj",
          }));
        } else {
          setExpirationDateErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[index];
            return newErrors;
          });
        }
      } else {
        // Jeśli data została wyczyszczona, usuń błąd
        setExpirationDateErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[index];
          return newErrors;
        });
      }
    }
  };

  // State and functions for Status realizacji selects
  const [statusRealizacjiFields, setStatusRealizacjiFields] = useState([
    "Do przygotowania", // Default Status realizacji 1 field
  ]);

  const handleStatusRealizacjiChange = (value, index) => {
    const updatedFields = [...statusRealizacjiFields];
    updatedFields[index] = value;
    setStatusRealizacjiFields(updatedFields);
  };

  const token = localStorage.getItem("token");
  let username; // Deklaracja zmiennej na wyższym poziomie zakresu
  let userRole; // Dodana zmienna dla roli użytkownika

  if (token) {
    const decoded = jwtDecode(token);
    username = decoded.username;
    userRole = decoded.role; // Pobieranie roli z tokena
  }

  const [isFetching, setIsFetching] = useState(false);
  const [expirationDateErrors, setExpirationDateErrors] = useState({});
  const [formData, setFormData] = useState({
    nrRef: "",
    importer: "",
    eksporter: "",
    nrFaktury: "",
    nrTranzytu: "",
    dokPrzedPrzybyciem: "",
    osobaWysylajacaOdprawe: "",
    osobaPrzygotowujacaOdprawe: "",
    nrRejestracyjnyPojazdyKurier: "",
    lokalizacjaTowaru: "",
    rodzaj: "",
    rodzajTransportu: "",
    fakturaBox: false,
    listPrzewozowyBox: false,
    kosztyTransportuBox: false,
    informacjeOTowarzeBox: false,
    T1Box: false,
    swiadectwoBox: false,
    brakUpowaznieniaBox: false,
    eoriBox: false,
    brakFakturyEdytowalnejBox: false,
    brakPackingListEdytowalnegoBox: false,
    brakIncotermsBox: false,
    brakCertyfikatyBox: false,
    brakEksportZKrajuWywozuBox: false,
    brakExcelBox: false,
    DSKBox: false,
    brakInneBox: false,
    brakInne: "",
    createdBy: username,
    urzad_tranzytowy: "",
    urzad_przeznaczenia: "",
    fields: [
      {
        eksporter: "",
        importer: "",
        nrTranzytu: "",
        expirationDate: "",
        invoiceNumbers: [""],
        statusRealizacji: "Do przygotowania",
      },
    ],
    magazynWarszawaZwrotyIBledy: {
      nrRef: "",
      MRN: "",
      iloscPozycjiSAD: "",
      rewizja: "",
      nadawca: "",
      komentarz: "",
    },
    magazynZwrotyMultipleEntries: "",
    dataUtworzenia: null, // Pole daty utworzenia dla adminów
    komentarz: "", // Pole komentarza dla adminów
    magazynWarszawaHandling: {
      numerListu: "",
      nazwaFirmy: "",
      liczbaPaczek: "",
      liczbaPalet: "",
      kierunek: "",
    },
  });

  const stripTemplateDynamicFields = useCallback((payload) => {
    if (!payload || typeof payload !== "object") {
      return payload;
    }

    const sanitized = { ...payload };
    const removeDynamicFromFormData = (candidate) => {
      if (!candidate || typeof candidate !== "object") {
        return candidate;
      }

      const next = { ...candidate };
      delete next.osobaPrzygotowujacaOdprawe;
      delete next.osobaWysylajacaOdprawe;
      delete next.nrRejestracyjnyPojazdyKurier;
      delete next.nrFaktury;

      if (Array.isArray(next.fields)) {
        next.fields = next.fields.map((field) => {
          if (!field || typeof field !== "object") {
            return field;
          }

          const nextField = { ...field };
          const derivedInvoiceCount = Array.isArray(nextField.invoiceNumbers)
            ? nextField.invoiceNumbers.length
            : Number.isInteger(nextField.invoiceCount)
              ? Math.max(0, nextField.invoiceCount)
              : 0;
          nextField.invoiceCount = derivedInvoiceCount;
          delete nextField.invoiceNumbers;
          delete nextField.nrFaktury;
          return nextField;
        });
      }

      return next;
    };

    if (sanitized.formData && typeof sanitized.formData === "object") {
      sanitized.formData = removeDynamicFromFormData(sanitized.formData);
    }

    if (
      sanitized.formDataSnapshot &&
      typeof sanitized.formDataSnapshot === "object"
    ) {
      sanitized.formDataSnapshot = removeDynamicFromFormData(
        sanitized.formDataSnapshot,
      );
    }

    return sanitized;
  }, []);

  const applyTemplatePayloadToForm = useCallback(
    (payload) => {
      if (!payload || typeof payload !== "object") {
        toast.error("Szablon nie zawiera poprawnych danych.");
        return;
      }

      if (
        payload.formDataSnapshot &&
        typeof payload.formDataSnapshot === "object"
      ) {
        const snapshot = stripTemplateDynamicFields(payload).formDataSnapshot;
        const normalizedFields = Array.isArray(snapshot?.fields)
          ? snapshot.fields.map((field) => {
              if (!field || typeof field !== "object") {
                return field;
              }

              const invoiceCount = Number.isInteger(field.invoiceCount)
                ? Math.max(0, field.invoiceCount)
                : 0;

              return {
                ...field,
                invoiceNumbers: Array(invoiceCount).fill(""),
              };
            })
          : snapshot?.fields;

        setFormData((prev) => ({
          ...prev,
          ...snapshot,
          fields: normalizedFields,
          createdBy: prev.createdBy || username,
        }));

        if (Array.isArray(snapshot.fields) && snapshot.fields.length > 0) {
          setStatusRealizacjiFields(
            snapshot.fields.map(
              (field) => field.statusRealizacji || "Do przygotowania",
            ),
          );
        }

        hiddenInvoiceNumbersRef.current = {};
        return;
      }

      if (payload.formData && typeof payload.formData === "object") {
        const snapshot = stripTemplateDynamicFields(payload).formData;
        const normalizedFields = Array.isArray(snapshot?.fields)
          ? snapshot.fields.map((field) => {
              if (!field || typeof field !== "object") {
                return field;
              }

              const invoiceCount = Number.isInteger(field.invoiceCount)
                ? Math.max(0, field.invoiceCount)
                : 0;

              return {
                ...field,
                invoiceNumbers: Array(invoiceCount).fill(""),
              };
            })
          : snapshot?.fields;

        setFormData((prev) => ({
          ...prev,
          ...snapshot,
          fields: normalizedFields,
          createdBy: prev.createdBy || username,
        }));

        const statusFields = Array.isArray(payload.statusRealizacjiFields)
          ? payload.statusRealizacjiFields
          : Array.isArray(snapshot.fields)
            ? snapshot.fields.map(
                (field) => field.statusRealizacji || "Do przygotowania",
              )
            : [];

        if (statusFields.length > 0) {
          setStatusRealizacjiFields(statusFields);
        }

        hiddenInvoiceNumbersRef.current = {};
        return;
      }

      const mapped =
        payload.mapped && typeof payload.mapped === "object"
          ? payload.mapped
          : payload.values && typeof payload.values === "object"
            ? payload.values
            : payload;

      setFormData((prev) => ({
        ...prev,
        rodzajTransportu: mapped.rodzajTransportu || prev.rodzajTransportu,
        rodzaj: mapped.rodzaj || prev.rodzaj,
        importer: mapped.importer || prev.importer,
        eksporter: mapped.eksporter || prev.eksporter,
        dokPrzedPrzybyciem:
          mapped.dokPrzedPrzybyciem || prev.dokPrzedPrzybyciem,
        lokalizacjaTowaru: mapped.lokalizacjaTowaru || prev.lokalizacjaTowaru,
        adresatDokumentow: mapped.adresatDokumentow || prev.adresatDokumentow,
        emailKorespondencyjny:
          mapped.emailKorespondencyjny || prev.emailKorespondencyjny,
      }));
    },
    [stripTemplateDynamicFields, username],
  );

  const handleListOpen = (type) => {
    setListType(type);
    setListOpen(true);
  };

  const handleListClose = (event) => {
    // Check if the focus is moving to the search field or staying within the menu
    const relatedTarget = event?.relatedTarget;
    if (
      relatedTarget &&
      (relatedTarget.closest('[role="listbox"]') ||
        relatedTarget.closest(".MuiTextField-root"))
    ) {
      return; // Don't close if focus is moving within the menu
    }

    // Add a small delay to allow clicking on search field
    setTimeout(() => {
      setListOpen(false);
    }, 200);
  };

  // Function to filter importers based on search text
  const filterImporters = (searchText) => {
    setImporterSearchText(searchText);
    if (searchText === "") {
      setImporterFilteredList(importerFullList);
    } else {
      const filtered = importerFullList.filter((importer) =>
        importer.toLowerCase().includes(searchText.toLowerCase()),
      );
      setImporterFilteredList(filtered);
    }
  };

  // Function to filter exporters based on search text
  const filterExporters = (searchText) => {
    setExporterSearchText(searchText);
    if (searchText === "") {
      setExporterFilteredList(exporterFullList);
    } else {
      const filtered = exporterFullList.filter((exporter) =>
        exporter.toLowerCase().includes(searchText.toLowerCase()),
      );
      setExporterFilteredList(filtered);
    }
  };

  // Functions to manage individual MAN exporter dropdowns
  const openManExporterDropdown = (fieldIndex) => {
    setOpenManExporterDropdowns((prev) => ({
      ...prev,
      [fieldIndex]: true,
    }));
  };

  const closeManExporterDropdown = (fieldIndex) => {
    setOpenManExporterDropdowns((prev) => ({
      ...prev,
      [fieldIndex]: false,
    }));
  };

  const updateManExporterSearchText = (fieldIndex, searchText) => {
    setManExporterSearchTexts((prev) => ({
      ...prev,
      [fieldIndex]: searchText,
    }));
  };

  const getManExporterSearchText = (fieldIndex) => {
    return manExporterSearchTexts[fieldIndex] || "";
  };

  const isManExporterDropdownOpen = (fieldIndex) => {
    return openManExporterDropdowns[fieldIndex] || false;
  };

  // Get filtered MAN exporters for a specific field
  const getFilteredManExporters = (fieldIndex) => {
    const searchText = getManExporterSearchText(fieldIndex);
    if (searchText === "") {
      return manExportersList;
    } else {
      return manExportersList.filter((exporter) =>
        exporter.toLowerCase().includes(searchText.toLowerCase()),
      );
    }
  };

  // Functions for Magazyn Warszawa companies
  const handleMagazynWarszawaCompaniesFetch = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/list/listMagazynWarszawa`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      const companiesData = response.data;
      const companiesList = companiesData.map((item) => item.name);
      companiesList.sort((a, b) => a.localeCompare(b));
      setMagazynWarszawaCompaniesList(companiesList);
      setMagazynWarszawaFilteredList(companiesList);
    } catch (error) {
      console.error("Błąd przy pobieraniu listy firm Magazyn Warszawa:", error);
    }
  };

  const filterMagazynWarszawaCompanies = (searchText) => {
    setMagazynWarszawaSearchText(searchText);
    if (searchText === "") {
      setMagazynWarszawaFilteredList(magazynWarszawaCompaniesList);
    } else {
      const filtered = magazynWarszawaCompaniesList.filter((company) =>
        company.toLowerCase().includes(searchText.toLowerCase()),
      );
      setMagazynWarszawaFilteredList(filtered);
    }
  };

  // Dodanie nowej firmy do slownika Magazyn Warszawa (tabela listmagazynwarszawa).
  // Backend robi trimStart().toUpperCase(), wiec wysylamy juz przyciety tekst,
  // a lokalnie dokladamy te sama, kanoniczna postac.
  const handleAddMagazynWarszawaCompany = async (rawName) => {
    const name = (rawName || "").trim();
    if (!name) throw new Error("Pusta nazwa firmy");
    const canonical = name.toUpperCase();

    const response = await axios.post(
      `${BASE_URL}/add-record/listMagazynWarszawa`,
      { name },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      },
    );

    const merge = (list) => {
      if (list.some((c) => c.toUpperCase() === canonical)) return list;
      return [...list, canonical].sort((a, b) => a.localeCompare(b));
    };
    setMagazynWarszawaCompaniesList((prev) => merge(prev));
    setMagazynWarszawaFilteredList((prev) => merge(prev));

    toast.success(
      response?.data?.message || `Dodano firmę do bazy: ${canonical}`,
      { autoClose: 2500 },
    );
    return canonical;
  };

  const handleImportersFetch = async () => {
    setIsFetching(true);
    setTimeout(async () => {
      try {
        const response = await axios.get(`${BASE_URL}/list/importerImport`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });
        const importersData = response.data;

        const importersList = importersData.map((item) => item.name);

        importersList.sort((a, b) => {
          const prefix = "OS. F";
          const aStartsWithPrefix = a.startsWith(prefix);
          const bStartsWithPrefix = b.startsWith(prefix);

          if (aStartsWithPrefix && !bStartsWithPrefix) {
            return 1; // a idzie za b
          } else if (!aStartsWithPrefix && bStartsWithPrefix) {
            return -1; // a przed b
          } else {
            return a.localeCompare(b, "pl", { sensitivity: "base" });
          }
        });

        setImporterFullList(importersList); // Store the full list
        setImporterFilteredList(importersList); // Set the filtered list initially
      } catch (error) {
        console.error("Error fetching importers:", error);
      } finally {
        setIsFetching(false);
      }
    }, 100);
  };

  const handleExportersFetch = async () => {
    setIsFetching(true);
    setTimeout(async () => {
      try {
        const response = await axios.get(`${BASE_URL}/list/eksporterEksport`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });
        const exportersData = response.data;

        const exportersList = exportersData.map((item) => item.name);

        exportersList.sort((a, b) => {
          const prefix = "OS. F";
          const aStartsWithPrefix = a.startsWith(prefix);
          const bStartsWithPrefix = b.startsWith(prefix);

          if (aStartsWithPrefix && !bStartsWithPrefix) {
            return 1; // a idzie na koniec
          } else if (!aStartsWithPrefix && bStartsWithPrefix) {
            return -1; // b idzie na koniec
          } else {
            return a.localeCompare(b, "pl", { sensitivity: "base" });
          }
        });

        setExporterFullList(exportersList); // Store the full list
        setExporterFilteredList(exportersList); // Set the filtered list initially
      } catch (error) {
        console.error("Error fetching exporters:", error);
      } finally {
        setIsFetching(false);
      }
    }, 100);
  };

  const handleManExportersFetch = async () => {
    setIsFetching(true);
    setTimeout(async () => {
      try {
        const response = await axios.get(`${BASE_URL}/list/listMan`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });
        const manExportersData = response.data;

        const manExportersList = manExportersData.map((item) => item.name);

        // Sort alphabetically
        manExportersList.sort((a, b) => {
          return a.localeCompare(b, "pl", { sensitivity: "base" });
        });

        setManExportersList(manExportersList);
      } catch (error) {
        console.error("Error fetching MAN exporters:", error);
      } finally {
        setIsFetching(false);
      }
    }, 100);
  };

  const handleUsersChange = async () => {
    setTimeout(async () => {
      try {
        const response = await axios.get(`${BASE_URL}/list/uzytkownicy`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });

        // Sort the data alphabetically by name using localeCompare
        const sortedData = response.data.sort((a, b) => {
          return a.name.localeCompare(b.name, "pl", { sensitivity: "base" });
        });

        setUzytkownicyList(sortedData); // Set the filtered list
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    }, 100);
  };

  const handleLokalizacjaChange = async () => {
    setTimeout(async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/list/lokalizacjaTransportow`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        // Sort the data alphabetically by name using localeCompare
        const sortedData = response.data.sort((a, b) => {
          return a.name.localeCompare(b.name, "pl", { sensitivity: "base" });
        });

        setLokalizacjaList(sortedData); // Set the filtered list
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    }, 100);
  };

  const handleUrzadTranzytowyChange = async () => {
    setTimeout(async () => {
      try {
        const response = await axios.get(`${BASE_URL}/list/urzad_tranzytowy`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });

        // Sort the data alphabetically by name using localeCompare
        const sortedData = response.data.sort((a, b) => {
          return a.name.localeCompare(b.name, "pl", { sensitivity: "base" });
        });

        setUrzadTranzytowyList(sortedData); // Set the filtered list
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    }, 100);
  };

  const handleUrzadPrzeznaczeniaChange = async () => {
    setTimeout(async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/list/urzad_przeznaczenia`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        // Sort the data alphabetically by name using localeCompare
        const sortedData = response.data.sort((a, b) => {
          return a.name.localeCompare(b.name, "pl", { sensitivity: "base" });
        });

        setUrzadPrzeznaczeniaList(sortedData); // Set the filtered list
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    }, 100);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (
      isTemplateBuilderMode &&
      [
        "osobaPrzygotowujacaOdprawe",
        "osobaWysylajacaOdprawe",
        "nrRejestracyjnyPojazdyKurier",
      ].includes(name)
    ) {
      return;
    }

    // Dodaj obsługę Magazyn Warszawa
    if (name === "rodzajTransportu" && value === "Magazyn Warszawa") {
      setFormData({
        ...formData,
        rodzajTransportu: value,
        rodzaj: "Eksport",
        magazynWarszawa: {
          numerListu: "",
          nadawca: "",
          liczbaPaczek: "",
          liczbaPalet: "",
        },
      });
      handleMagazynWarszawaCompaniesFetch();
      return;
    }

    // Obsługa pól Magazyn Warszawa
    if (name.startsWith("magazynWarszawa.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        magazynWarszawa: {
          ...prev.magazynWarszawa,
          [field]: value,
        },
      }));
      return;
    }

    if (
      name === "rodzajTransportu" &&
      value === "Magazyn Warszawa Zwroty i Błędy"
    ) {
      setFormData({
        ...formData,
        rodzajTransportu: value,
        rodzaj: "Eksport",
        magazynWarszawaStan: {
          nrRef: "",
          MRN: "",
          iloscPozycjiSAD: "",
          rewizja: "",
        },
      });
      handleMagazynWarszawaCompaniesFetch();
      return;
    }

    if (name.startsWith("magazynWarszawaZwrotyIBledy.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        magazynWarszawaZwrotyIBledy: {
          ...prev.magazynWarszawaZwrotyIBledy,
          [field]: value,
        },
      }));
      return;
    }

    if (name === "rodzajTransportu" && value === "Magazyn Warszawa Handling") {
      setFormData({
        ...formData,
        rodzajTransportu: value,
        rodzaj: "Eksport",
        magazynWarszawaHandling: {
          numerListu: "",
          nazwaFirmy: "",
          liczbaPaczek: "",
          liczbaPalet: "",
          kierunek: "",
        },
      });
      handleMagazynWarszawaCompaniesFetch();
      return;
    }

    if (
      name === "rodzajTransportu" &&
      value === "Magazyn Warszawa Nowy Handling"
    ) {
      setFormData({
        ...formData,
        rodzajTransportu: value,
        rodzaj: "Eksport",
      });
      setMwNowyHandlingEntryType("");
      setMwNowyHandlingCount("");
      setMwNowyHandlingRows([]);
      setMwNowyHandlingSingleRow({});
      setMwNowyHandlingPasteText("");
      setMwNowyHandlingRevealSignal(0);
      handleMagazynWarszawaCompaniesFetch();
      return;
    }

    if (name.startsWith("magazynWarszawaHandling.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        magazynWarszawaHandling: {
          ...prev.magazynWarszawaHandling,
          [field]: value,
        },
      }));
      return;
    }

    setFormData((prevData) => {
      // Only update formData if value is different
      if (prevData[name] === value) return prevData;

      // Reset specific fields when "rodzaj" changes to a specific type
      if (name === "rodzaj") {
        return {
          ...prevData,
          [name]: value,
          importer: "",
          eksporter: "",
          importersList: [],
          eksporterList: [],
        };
      }

      // Handle importer change
      if (name === "importer") {
        // Check if MAN BUS is selected
        if (value === "MAN BUS SP. Z O.O.") {
          setIsManBusSelected(true);
          // Reset eksporter field when switching to MAN BUS
          return {
            ...prevData,
            [name]: value,
            eksporter: "",
          };
        } else {
          setIsManBusSelected(false);
          // Reset eksporter field when switching away from MAN BUS
          return {
            ...prevData,
            [name]: value,
            eksporter: "",
          };
        }
      }

      return { ...prevData, [name]: value };
    });

    // Call fetching functions based on the "rodzaj" selection
    if (name === "rodzaj") {
      if (
        value === "Import" ||
        value === "Import - wpis do rejestru" ||
        value === "Symulacja sad"
      )
        handleImportersFetch();
      else if (value === "Eksport") handleExportersFetch();
      else if (value === "Tranzyt") {
        handleUrzadTranzytowyChange();
        handleExportersFetch();
        handleUrzadPrzeznaczeniaChange();
      } else {
        handleImportersFetch();
        handleExportersFetch();
      }
    }

    // Call MAN exporters fetch when MAN BUS is selected as importer
    if (name === "importer" && value === "MAN BUS SP. Z O.O.") {
      handleManExportersFetch();
    }
  };

  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      setFormData((prevData) => ({
        ...prevData,
        adresatDokumentow: "Automat",
      }));
    }

    setAutomatChecked(isChecked);
  };

  const handleCheckboxChangeUwagi = (event) => {
    const { name, checked } = event.target;

    setFormData((prevData) => {
      const nextData = { ...prevData, [name]: checked };

      if (name === "brakInneBox" && !checked) {
        nextData.brakInne = "";
      }

      return nextData;
    });
  };

  const checkValuesInDatabase = async () => {
    // ✅ Jeśli istnieje faktura 'WIELE' lub 'NIEZNANY' → pomiń sprawdzanie
    const hasSpecialFaktura = formData.fields.some((field) =>
      field.invoiceNumbers?.some((nr) => {
        const normalized = nr.trim().toUpperCase();
        return normalized === "WIELE" || normalized === "NIEZNANY";
      }),
    );

    if (hasSpecialFaktura) {
      toast.info(
        "Pominięto sprawdzanie faktur w bazie (wykryto wartość WIELE lub NIEZNANY). Pamiętaj, że podanie tych wartości jest na twoją odpowiedzialność",
        {
          autoClose: 3000,
        },
      );
      return [];
    }

    const apiPromises = [];
    const existingValues = [];

    const globalEksporter = formData.eksporter?.trim().toUpperCase();

    for (let i = 0; i < formData.fields.length; i++) {
      const field = formData.fields[i];

      const nrFakturaRaw = field.invoiceNumbers?.join(",") || "";

      if (!nrFakturaRaw) continue;

      const nrFakturyValues = nrFakturaRaw.split(",");
      const eksporterValue =
        globalEksporter || field.eksporter?.trim().toUpperCase();

      if (!eksporterValue) {
        console.warn(`Brak eksportera dla wiersza ${i}, pomijam...`);
        continue;
      }

      for (const nrFaktury of nrFakturyValues) {
        const trimmedNrFaktury = nrFaktury.trim();
        if (!trimmedNrFaktury) continue;

        const apiPromise = axios
          .get(`${BASE_URL}/checkValues`, {
            params: {
              eksporter: eksporterValue,
              nrFaktury: trimmedNrFaktury,
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          })
          .then((response) => ({
            response,
            nrFaktury: trimmedNrFaktury,
            eksporter: eksporterValue,
          }));

        apiPromises.push(apiPromise);
      }
    }

    try {
      const results = await Promise.all(apiPromises);
      results.forEach(({ response, nrFaktury, eksporter }) => {
        if (response.data.exists) {
          existingValues.push({
            eksporter,
            nrFaktury,
            nrRef: response.data.nrRef,
          });
        }
      });

      return existingValues;
    } catch (error) {
      console.error("Error checking values:", error);
      return [];
    }
  };

  const validateForm = (formData, setIsSubmitting) => {
    const emailPattern =
      /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})([;, \s]+[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})*$/;

    const adresatDokumentowPattern = /^[0-9+]+$/;

    const errors = [
      {
        condition: !formData.emailKorespondencyjny,
        msg: "Proszę wpisać adres email do ticketu.",
      },
      {
        condition:
          formData.rodzajTransportu === "Na Kołach" &&
          formData.lokalizacjaTowaru === "W DRODZE",
        msg: `Nie może być wybrany rodzaj transportu "Na Kołach" oraz lokalizacja towaru/urząd celny wyjścia "W DRODZE"`,
      },
      {
        condition: !emailPattern.test(formData.emailKorespondencyjny),
        msg: "Wprowadź poprawny adres email lub listę adresów oddzielonych średnikiem, przecinkiem lub spacją.",
      },
      {
        condition: !formData.adresatDokumentow,
        msg: "Proszę wpisać/wybrać adresata dokumentów.",
      },
      {
        condition:
          formData.adresatDokumentow &&
          formData.adresatDokumentow !== "Automat" &&
          !adresatDokumentowPattern.test(formData.adresatDokumentow),
        msg: "Adresat dokumentów może zawierać tylko cyfry, znak + lub wartość 'Automat'.",
      },

      {
        condition: !formData.rodzajTransportu,
        msg: "Proszę wybrać rodzaj transportu.",
      },
      {
        condition: !formData.rodzaj,
        msg: "Proszę wybrać rodzaj zgłoszenie Import, Eksport lub Tranzyt.",
      },
      {
        condition: !formData.dokPrzedPrzybyciem,
        msg: "Proszę wybrać czy były dostarczone dokumenty przed przybyciem.",
      },
      {
        condition:
          formData.nrRejestracyjnyPojazdyKurier &&
          (formData.nrRejestracyjnyPojazdyKurier.length < 7 ||
            new Set(formData.nrRejestracyjnyPojazdyKurier).size === 1),
        msg: "Numer rejestracyjny pojazdu musi zawierać co najmniej 7 różnych znaków.",
      },
      {
        condition: Object.keys(expirationDateErrors).length > 0,
        msg: "Proszę poprawić daty wygaśnięcia T1. Daty nie mogą być wcześniejsze niż dzisiaj.",
      },
      {
        condition: formData.brakInneBox && !formData.brakInne.trim(),
        msg: 'Proszę opisać pole "Inny" w brakach do odprawy celnej.',
      },
    ];

    for (const err of errors) {
      if (err.condition) {
        alert(err.msg);
        setIsSubmitting(false);
        return false;
      }
    }

    const MAX_NR_FAKTURY_LENGTH = 65535; // db 'text' data type limit
    for (let i = 0; i < formData.fields.length; i++) {
      const field = formData.fields[i];
      const joined = Array.isArray(field.invoiceNumbers)
        ? field.invoiceNumbers.map((n) => n.trim()).join(", ")
        : "";
      if (joined.length > MAX_NR_FAKTURY_LENGTH) {
        alert(
          `Sekcja ${i + 1}: łączna długość numerów faktur (${joined.length} znaków) przekracza dopuszczalny limit ${MAX_NR_FAKTURY_LENGTH} znaków. Proszę zmniejszyć liczbę lub długość wpisanych numerów.`,
        );
        setIsSubmitting(false);
        return false;
      }
    }

    return true;
  };

  const handleImportSubmission = async ({
    formData,
    setIsSubmitting,
    username,
    BASE_URL,
    checkValuesInDatabase,
    navigate,
  }) => {
    const checkboxFields = [
      "fakturaBox",
      "listPrzewozowyBox",
      "kosztyTransportuBox",
      "informacjeOTowarzeBox",
      "T1Box",
      "swiadectwoBox",
      "brakUpowaznieniaBox",
      "eoriBox",
      "brakFakturyEdytowalnejBox",
      "brakPackingListEdytowalnegoBox",
      "brakIncotermsBox",
      "brakCertyfikatyBox",
      "brakEksportZKrajuWywozuBox",
      "brakExcelBox",
      "DSKBox",
    ];

    const convertedCheckboxes = checkboxFields.reduce((acc, field) => {
      acc[field] = formData[field] ? 1 : 0;
      return acc;
    }, {});

    const nrRefs = []; // 🔹 Zbieramy nrRef
    const successfulSaves = []; // Track successful operations for potential rollback

    const existingValues = await checkValuesInDatabase();
    if (existingValues.length > 0) {
      existingValues.forEach((value) => {
        alert(
          `Nie można zapisać danych, ponieważ istnieją już rekordy z podanymi wartościami:\nEksporter: ${value.eksporter}\nNr. Faktury: ${value.nrFaktury},\nIstniejący Nr Ref: ${value.nrRef}`,
        );
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Process each field sequentially to maintain transaction integrity
      for (const field of formData.fields) {
        const dynamicFieldsData = {
          nrFaktury: Array.isArray(field.invoiceNumbers)
            ? field.invoiceNumbers.map((i) => i.trim()).join(", ")
            : "",
          eksporter:
            field.eksporter?.toUpperCase() ||
            formData.eksporter?.toUpperCase() ||
            "",
          importer:
            formData.importer?.toUpperCase() ||
            field.importer?.toUpperCase() ||
            "",
          nrTranzytu: field.nrTranzytu,
          expirationDate: field.expirationDate,
          statusRealizacji: field.statusRealizacji,
        };

        let formDataWithDynamicFields = {
          ...formData,
          ...dynamicFieldsData,
          ...convertedCheckboxes,
          created_at: new Date().toLocaleString("pl-PL", {
            timeZone: "Europe/Warsaw",
          }),
        };

        // Set rodzajTransportu to "Kurier MU VOLKSWAGEN MOTOR POLSKA" if conditions are met
        if (
          formDataWithDynamicFields.rodzajTransportu === "Kurier" &&
          formDataWithDynamicFields.lokalizacjaTowaru ===
            "Volkswagen Motor Polska"
        ) {
          formDataWithDynamicFields = {
            ...formDataWithDynamicFields,
            rodzajTransportu: "Kurier MU VOLKSWAGEN MOTOR POLSKA",
          };
        }

        if (!formDataWithDynamicFields.statusRealizacji) {
          alert("Proszę wybrać status realizacji.");
          setIsSubmitting(false);
          return;
        }

        // 🔴 Call the new atomic endpoint that handles both saving and emailing
        try {
          const saveResponse = await axios.post(
            `${BASE_URL}/saveFormImportWithEmail`,
            formDataWithDynamicFields,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            },
          );

          const nrRef = saveResponse.data.nrRef;
          nrRefs.push(nrRef);
          successfulSaves.push(nrRef);
        } catch (error) {
          // If any operation fails, show error and stop processing
          const serverMessage =
            error?.response?.data?.message ||
            "Wystąpił błąd przy zapisie danych lub wysyłaniu emaila.";

          toast.error(serverMessage, { autoClose: 3500 });
          setIsSubmitting(false);
          return; // Stop processing remaining fields
        }
      }

      // 🔹 If multiple records were saved, mark them as łączona odprawa
      if (nrRefs.length > 1) {
        try {
          await axios.post(
            `${BASE_URL}/oznaczLaczone`,
            { nrRefs },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            },
          );
        } catch (laczonaError) {
          console.warn("Failed to mark as łączona odprawa:", laczonaError);
          // This is not critical - the forms are already saved and emails sent
        }
      }

      // All operations successful
      const nrRefFinal = nrRefs.join(", ");
      toast.success(
        `Poprawnie zapisano nowe zgłoszenie o numerze referencyjnym: ${nrRefFinal} i wysłano email!`,
        {
          autoClose: 3000,
          onClose: () => navigate("/user"),
        },
      );
    } catch (error) {
      console.error("Unexpected error in handleImportSubmission:", error);
      toast.error("Wystąpił nieoczekiwany błąd.", { autoClose: 3000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportSubmission = async ({
    formData,
    setIsSubmitting,
    username,
    BASE_URL,
    checkValuesInDatabase,
    navigate,
  }) => {
    const checkboxFields = [
      "fakturaBox",
      "listPrzewozowyBox",
      "kosztyTransportuBox",
      "informacjeOTowarzeBox",
      "T1Box",
      "swiadectwoBox",
      "brakUpowaznieniaBox",
      "eoriBox",
      "brakFakturyEdytowalnejBox",
      "brakPackingListEdytowalnegoBox",
      "brakIncotermsBox",
      "brakCertyfikatyBox",
      "brakEksportZKrajuWywozuBox",
      "brakExcelBox",
      "DSKBox",
    ];

    const convertedCheckboxes = checkboxFields.reduce((acc, field) => {
      acc[field] = formData[field] ? 1 : 0;
      return acc;
    }, {});

    const nrRefList = [];

    const existingValues = await checkValuesInDatabase();
    if (existingValues.length > 0) {
      existingValues.forEach((value) => {
        alert(
          `Nie można zapisać danych, ponieważ istnieją już rekordy z podanymi wartościami:\nEksporter: ${value.eksporter}\nNr. Faktury: ${value.nrFaktury},\nIstniejący Nr Ref: ${value.nrRef}`,
        );
      });
      setIsSubmitting(false);
      return;
    }

    try {
      for (const field of formData.fields) {
        const dynamicFieldsData = {
          nrFaktury: Array.isArray(field.invoiceNumbers)
            ? field.invoiceNumbers.map((i) => i.trim()).join(", ")
            : "",
          eksporter: formData.eksporter?.toUpperCase() || "",
          importer: field.importer?.toUpperCase() || "",
          nrTranzytu: field.nrTranzytu,
          expirationDate: field.expirationDate,
          statusRealizacji: field.statusRealizacji,
        };

        let formDataWithDynamicFields = {
          ...formData,
          ...dynamicFieldsData,
          ...convertedCheckboxes,
          created_at: new Date().toLocaleString("pl-PL", {
            timeZone: "Europe/Warsaw",
          }),
        };

        // Set rodzajTransportu to "Kurier MU VOLKSWAGEN MOTOR POLSKA" if conditions are met
        if (
          formDataWithDynamicFields.rodzajTransportu === "Kurier" &&
          formDataWithDynamicFields.lokalizacjaTowaru ===
            "Volkswagen Motor Polska"
        ) {
          formDataWithDynamicFields = {
            ...formDataWithDynamicFields,
            rodzajTransportu: "Kurier MU VOLKSWAGEN MOTOR POLSKA",
          };
        }

        if (!formDataWithDynamicFields.statusRealizacji) {
          alert("Proszę wybrać status realizacji.");
          setIsSubmitting(false);
          return;
        }

        try {
          const saveResponse = await axios.post(
            `${BASE_URL}/saveFormExportWithEmail`,
            formDataWithDynamicFields,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            },
          );

          const nrRef = saveResponse.data.nrRef;
          nrRefList.push(nrRef);
        } catch (error) {
          const serverMessage =
            error?.response?.data?.message ||
            "Wystąpił błąd przy zapisie danych lub wysyłaniu emaila.";

          toast.error(serverMessage, { autoClose: 3500 });
          setIsSubmitting(false);
          return;
        }
      }

      // Mark as łączona if multiple records
      if (nrRefList.length > 1) {
        try {
          await axios.post(
            `${BASE_URL}/oznaczLaczone`,
            { nrRefs: nrRefList },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            },
          );
        } catch (laczonaError) {
          console.warn("Failed to mark as łączona odprawa:", laczonaError);
        }
      }

      const nrRefFinal = nrRefList.join(", ");
      toast.success(
        `Poprawnie zapisano nowe zgłoszenie o numerze referencyjnym: ${nrRefFinal} i wysłano email!`,
        {
          autoClose: 3000,
          onClose: () => navigate("/user"),
        },
      );
    } catch (error) {
      console.error("Unexpected error in handleExportSubmission:", error);
      toast.error("Wystąpił nieoczekiwany błąd.", { autoClose: 3000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTranzytSubmission = async ({
    formData,
    setIsSubmitting,
    username,
    BASE_URL,
    checkValuesInDatabase,
    navigate,
  }) => {
    const checkboxFields = [
      "fakturaBox",
      "listPrzewozowyBox",
      "kosztyTransportuBox",
      "informacjeOTowarzeBox",
      "T1Box",
      "swiadectwoBox",
      "brakUpowaznieniaBox",
      "eoriBox",
      "brakFakturyEdytowalnejBox",
      "brakPackingListEdytowalnegoBox",
      "brakIncotermsBox",
      "brakCertyfikatyBox",
      "brakEksportZKrajuWywozuBox",
      "brakExcelBox",
      "DSKBox",
    ];

    const convertedCheckboxes = checkboxFields.reduce((acc, field) => {
      acc[field] = formData[field] ? 1 : 0;
      return acc;
    }, {});

    const nrRefList = [];

    const existingValues = await checkValuesInDatabase();
    if (existingValues.length > 0) {
      existingValues.forEach((value) => {
        alert(
          `Nie można zapisać danych, ponieważ istnieją już rekordy z podanymi wartościami:\nEksporter: ${value.eksporter}\nNr. Faktury: ${value.nrFaktury},\nIstniejący Nr Ref: ${value.nrRef}`,
        );
      });
      setIsSubmitting(false);
      return;
    }

    try {
      for (const field of formData.fields) {
        const dynamicFieldsData = {
          nrFaktury: Array.isArray(field.invoiceNumbers)
            ? field.invoiceNumbers.map((i) => i.trim()).join(", ")
            : "",
          eksporter:
            field.eksporter?.toUpperCase() ||
            formData.eksporter?.toUpperCase() ||
            "",
          importer:
            formData.importer?.toUpperCase() ||
            field.importer?.toUpperCase() ||
            "",
          nrTranzytu: field.nrTranzytu,
          expirationDate: field.expirationDate,
          statusRealizacji: field.statusRealizacji,
          wagaNetto: field.wagaNetto || "",
          wagaBrutto: field.wagaBrutto || "",
          waluta: field.waluta || "",
          gwarant: field.gwarant || "",
          urzad_tranzytowy: field.urzad_tranzytowy || "",
          urzad_przeznaczenia: field.urzad_przeznaczenia || "",
        };

        let formDataWithDynamicFields = {
          ...formData,
          ...dynamicFieldsData,
          ...convertedCheckboxes,
          created_at: new Date().toLocaleString("pl-PL", {
            timeZone: "Europe/Warsaw",
          }),
        };

        // Set rodzajTransportu to "Kurier MU VOLKSWAGEN MOTOR POLSKA" if conditions are met
        if (
          formDataWithDynamicFields.rodzajTransportu === "Kurier" &&
          formDataWithDynamicFields.lokalizacjaTowaru ===
            "Volkswagen Motor Polska"
        ) {
          formDataWithDynamicFields = {
            ...formDataWithDynamicFields,
            rodzajTransportu: "Kurier MU VOLKSWAGEN MOTOR POLSKA",
          };
        }

        if (!formDataWithDynamicFields.statusRealizacji) {
          alert("Proszę wybrać status realizacji.");
          setIsSubmitting(false);
          return;
        }

        try {
          const saveResponse = await axios.post(
            `${BASE_URL}/saveFormTranzytWithEmail`,
            formDataWithDynamicFields,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            },
          );

          const nrRef = saveResponse.data.nrRef;
          nrRefList.push(nrRef);
        } catch (error) {
          const serverMessage =
            error?.response?.data?.message ||
            "Wystąpił błąd przy zapisie danych lub wysyłaniu emaila.";

          toast.error(serverMessage, { autoClose: 3500 });
          setIsSubmitting(false);
          return;
        }
      }

      // Mark as łączona if multiple records
      if (nrRefList.length > 1) {
        try {
          await axios.post(
            `${BASE_URL}/oznaczLaczone`,
            { nrRefs: nrRefList },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            },
          );
        } catch (laczonaError) {
          console.warn("Failed to mark as łączona odprawa:", laczonaError);
        }
      }

      const nrRefFinal = nrRefList.join(", ");
      toast.success(
        `Poprawnie zapisano nowe zgłoszenie o numerze referencyjnym: ${nrRefFinal} i wysłano email!`,
        {
          autoClose: 3000,
          onClose: () => navigate("/user"),
        },
      );
    } catch (error) {
      console.error("Unexpected error in handleTranzytSubmission:", error);
      toast.error("Wystąpił nieoczekiwany błąd.", { autoClose: 3000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWprowadzenieNaSkald = async ({
    formData,
    setIsSubmitting,
    username,
    BASE_URL,
    checkValuesInDatabase,
    navigate,
  }) => {
    const checkboxFields = [
      "fakturaBox",
      "listPrzewozowyBox",
      "kosztyTransportuBox",
      "informacjeOTowarzeBox",
      "T1Box",
      "swiadectwoBox",
      "brakUpowaznieniaBox",
      "eoriBox",
      "brakFakturyEdytowalnejBox",
      "brakPackingListEdytowalnegoBox",
      "brakIncotermsBox",
      "brakCertyfikatyBox",
      "brakEksportZKrajuWywozuBox",
      "brakExcelBox",
      "DSKBox",
    ];

    const convertedCheckboxes = checkboxFields.reduce((acc, field) => {
      acc[field] = formData[field] ? 1 : 0;
      return acc;
    }, {});

    const nrRefList = [];

    const existingValues = await checkValuesInDatabase();
    if (existingValues.length > 0) {
      existingValues.forEach((value) => {
        alert(
          `Nie można zapisać danych, ponieważ istnieją już rekordy z podanymi wartościami:\nEksporter: ${value.eksporter}\nNr. Faktury: ${value.nrFaktury},\nIstniejący Nr Ref: ${value.nrRef}`,
        );
      });
      setIsSubmitting(false);
      return;
    }

    try {
      for (const field of formData.fields) {
        const dynamicFieldsData = {
          nrFaktury: Array.isArray(field.invoiceNumbers)
            ? field.invoiceNumbers.map((i) => i.trim()).join(", ")
            : "",
          eksporter: field.eksporter?.toUpperCase() || "",
          importer: formData.importer?.toUpperCase() || "",
          nrTranzytu: field.nrTranzytu,
          expirationDate: field.expirationDate,
          statusRealizacji: field.statusRealizacji,
        };

        let formDataWithDynamicFields = {
          ...formData,
          ...dynamicFieldsData,
          ...convertedCheckboxes,
          created_at: new Date().toLocaleString("pl-PL", {
            timeZone: "Europe/Warsaw",
          }),
        };

        // Set rodzajTransportu to "Kurier MU VOLKSWAGEN MOTOR POLSKA" if conditions are met
        if (
          formDataWithDynamicFields.rodzajTransportu === "Kurier" &&
          formDataWithDynamicFields.lokalizacjaTowaru ===
            "Volkswagen Motor Polska"
        ) {
          formDataWithDynamicFields = {
            ...formDataWithDynamicFields,
            rodzajTransportu: "Kurier MU VOLKSWAGEN MOTOR POLSKA",
          };
        }

        if (!formDataWithDynamicFields.statusRealizacji) {
          alert("Proszę wybrać status realizacji.");
          setIsSubmitting(false);
          return;
        }

        try {
          const saveResponse = await axios.post(
            `${BASE_URL}/saveFormExportWithEmail`, // Note: Using Export endpoint for "Wprowadzenie na skład"
            formDataWithDynamicFields,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            },
          );

          const nrRef = saveResponse.data.nrRef;
          nrRefList.push(nrRef);
        } catch (error) {
          const serverMessage =
            error?.response?.data?.message ||
            "Wystąpił błąd przy zapisie danych lub wysyłaniu emaila.";

          toast.error(serverMessage, { autoClose: 3500 });
          setIsSubmitting(false);
          return;
        }
      }

      // Mark as łączona if multiple records
      if (nrRefList.length > 1) {
        try {
          await axios.post(
            `${BASE_URL}/oznaczLaczone`,
            { nrRefs: nrRefList },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            },
          );
        } catch (laczonaError) {
          console.warn("Failed to mark as łączona odprawa:", laczonaError);
        }
      }

      const nrRefFinal = nrRefList.join(", ");
      toast.success(
        `Poprawnie zapisano nowe zgłoszenie o numerze referencyjnym: ${nrRefFinal} i wysłano email!`,
        {
          autoClose: 3000,
          onClose: () => navigate("/user"),
        },
      );
    } catch (error) {
      console.error("Unexpected error in handleWprowadzenieNaSkald:", error);
      toast.error("Wystąpił nieoczekiwany błąd.", { autoClose: 3000 });
    } finally {
      setIsSubmitting(false);
    }
  };
  // Konwersja danych wklejonych z arkusza Excel na wiersze tabeli "Nowy Handling".
  // Ustawia liczbę wpisów = liczba wklejonych wierszy i włącza walidację wszystkich
  // komórek (revealSignal), tak jak przy wpisywaniu ręcznym.
  const convertMwNowyHandlingPaste = (text) => {
    const parsed = parseMwNowyHandlingPaste(text);
    if (parsed.length === 0) {
      toast.warning("Brak danych do wczytania. Wklej zakres z arkusza Excel.");
      return;
    }

    const companyDict = buildCompanyDict(magazynWarszawaCompaniesList);
    const rows = parsed.map((row) => {
      const key = normalizeCompany(row.nazwaFirmy);
      return companyDict.has(key)
        ? { ...row, nazwaFirmy: companyDict.get(key) }
        : row;
    });

    setMwNowyHandlingRows(rows);
    setMwNowyHandlingCount(String(rows.length));
    setMwNowyHandlingRevealSignal((v) => v + 1);
    toast.success(`Wczytano ${rows.length} wierszy z Excela.`);
  };

  const handleMwNowyHandlingPasteEvent = (event) => {
    const text = event.clipboardData?.getData("text") ?? "";
    if (!text.trim()) return;
    event.preventDefault();
    setMwNowyHandlingPasteText(text);
    convertMwNowyHandlingPaste(text);
  };

  // Naglowek szablonu do pobrania - budowany z MW_NOWY_HANDLING_COLUMNS, wiec
  // zawsze ma taka sama kolejnosc kolumn jak parser wklejania (nie moze sie rozjechac).
  const buildMwNowyHandlingTemplateHeader = (col) => {
    if (col.type === "time") return `${col.label} (format: 10:30)`;
    if (col.type === "text" && col.exactLength) {
      return `${col.label} (dokładnie ${col.exactLength} znaków)`;
    }
    return col.label;
  };

  // Pobranie pustego szablonu Excel do uzupelnienia i wklejenia z powrotem w pole
  // "Wklej dane z Excela" (ten sam uklad kolumn co w tabeli / parserze wklejania).
  const handleDownloadMwNowyHandlingTemplate = () => {
    const headers = MW_NOWY_HANDLING_COLUMNS.map(
      buildMwNowyHandlingTemplateHeader,
    );
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    worksheet["!cols"] = headers.map(() => ({ wch: 28 }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Nowy Handling");
    XLSX.writeFile(
      workbook,
      "Magazyn_Warszawa_Nowy_Handling_szablon.xlsx",
    );
  };

  // Zapis "Magazyn Warszawa - Nowy Handling" (jeden wpis albo wiele na raz).
  // nrRef nadaje serwer, statusRealizacji = "Przyjęto na magazyn", createdBy =
  // username z tokenu. Walidacja duplikatu numeru listu jest po stronie serwera
  // (blokuje cały zapis, także przy wielu wpisach).
  const submitMwNowyHandling = async (mode) => {
    const rows =
      mode === "single" ? [mwNowyHandlingSingleRow] : mwNowyHandlingRows;

    if (mode === "multiple") {
      const expected = Number(mwNowyHandlingCount);
      if (!Number.isInteger(expected) || expected < 1) {
        toast.error("Podaj poprawną liczbę wpisów (min. 1).");
        return;
      }
      if (rows.length !== expected) {
        toast.error(`Uzupełnij wszystkie ${expected} wierszy tabeli.`);
        return;
      }
    }

    const companyDict = buildCompanyDict(magazynWarszawaCompaniesList);
    const { errorCount } = validateRows(rows, { companyDict });
    if (rows.length === 0 || errorCount > 0) {
      toast.error("Popraw błędy w formularzu przed zapisem.");
      return;
    }

    // Zapisz kanoniczną pisownię firmy ze słownika (na wypadek, gdyby komórka
    // nie zdążyła zrobić auto-snapu przy opuszczeniu pola).
    const canonicalCompany = (name) => {
      const key = normalizeCompany(name);
      return companyDict.has(key) ? companyDict.get(key) : String(name ?? "").trim();
    };

    const payloadRows = rows.map((row) => ({
      przyjazdAuta: String(row.przyjazdAuta ?? "").trim(),
      nrListu: String(row.nrListu ?? "").trim(),
      nazwaFirmy: canonicalCompany(row.nazwaFirmy),
      startSkanowania: String(row.startSkanowania ?? "").trim(),
    }));

    try {
      setIsSubmitting(true);
      const { data } = await axios.post(
        `${BASE_URL}/magazyn-warszawa-nowy-handling`,
        { rows: payloadRows, rodzaj: formData.rodzaj || "Eksport" },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      const refs = Array.isArray(data?.nrRefs) ? data.nrRefs.join(", ") : "";
      toast.success(
        `Zapisano ${data?.count ?? payloadRows.length} zgłoszenie/zgłoszeń Magazyn Warszawa. Nr ref: ${refs}`,
        {
          autoClose: 3000,
          onClose: () => navigate("/magazynWarszawaNowyHandling"),
        },
      );
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Wystąpił błąd podczas zapisu Magazyn Warszawa (Nowy Handling).";
      toast.error(message, { autoClose: 4000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (isTemplateBuilderMode) {
      if (typeof onTemplateSave === "function") {
        await onTemplateSave();
      } else {
        toast.info("Użyj przycisku zapisu szablonu nad formularzem.");
      }
      setIsSubmitting(false);
      return;
    }

    // Obsługa Magazyn Warszawa
    if (formData.rodzajTransportu === "Magazyn Warszawa") {
      const { numerListu, nadawca, liczbaPaczek, liczbaPalet } =
        formData.magazynWarszawa || {};
      if (!numerListu || !nadawca || !liczbaPaczek || !liczbaPalet) {
        alert("Wypełnij wszystkie pola Magazyn Warszawa.");
        setIsSubmitting(false);
        return;
      }
      try {
        const checkResponse = await axios.get(
          `${BASE_URL}/check-numer-listu-magazyn-warszawa`,
          {
            params: { numerListu, rodzajTransportu: formData.rodzajTransportu },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (checkResponse.data.exists) {
          toast.error(checkResponse.data.message, {
            autoClose: 3000,
          });
          setIsSubmitting(false);
          return;
        } else if (checkResponse.data.message) {
          // Opcjonalnie możesz pokazać informacyjny toast dla przypadku gdy zwrot = "Nie"
          toast.info(checkResponse.data.message, {
            autoClose: 2000,
          });
        }

        await axios.post(
          `${BASE_URL}/magazyn-warszawa`,
          {
            numerListu,
            nadawca,
            liczbaPaczek,
            liczbaPalet,
            username,
            rodzaj: formData.rodzaj,
            rodzajTransportu: formData.rodzajTransportu,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );
        toast.success("Zgłoszenie Magazyn Warszawa zapisane!", {
          autoClose: 2000,
          onClose: () => navigate("/magazynWarszawa"),
        });
      } catch (err) {
        toast.error("Błąd przy zapisie Magazyn Warszawa.", { autoClose: 3000 });
      }
      setIsSubmitting(false);
      return;
    }

    // Obsługa Magazyn Warszawa Handling
    if (formData.rodzajTransportu === "Magazyn Warszawa Handling") {
      // Check if it's multiple entries
      if (magazynHandlingEntryType === "multiple") {
        // Parse multiple entries from textarea
        const textData = formData.magazynHandlingMultipleEntries || "";
        if (!textData.trim()) {
          alert("Wprowadź dane do przetworzenia.");
          setIsSubmitting(false);
          return;
        }

        const lines = textData.trim().split("\n");
        const entries = [];

        // Parse each line
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue; // Skip empty lines

          const columns = line.split("\t"); // Split by tab

          // Different validation based on user role
          const expectedColumns = 5; // Both need minimum 5 columns

          if (columns.length < expectedColumns) {
            const columnDesc =
              userRole === "admin"
                ? "Numer Listu | Nazwa Firmy | Liczba Paczek | Liczba Palet | Kierunek | Komentarz (opcjonalny) | Data utworzenia (opcjonalna) | Username (opcjonalny)"
                : "Numer Listu | Nazwa Firmy | Liczba Paczek | Liczba Palet | Kierunek | Komentarz (opcjonalny)";
            alert(
              `Wiersz ${
                i + 1
              } ma nieprawidłową liczbę kolumn. Oczekiwano minimum ${expectedColumns} kolumn: ${columnDesc}.`,
            );
            setIsSubmitting(false);
            return;
          }

          let numerListu,
            nazwaFirmy,
            liczbaPaczek,
            liczbaPalet,
            kierunek,
            komentarz = "",
            dataUtworzenia = "",
            createdByUsername = "";

          if (userRole === "admin") {
            // Admin can have 5-8 columns
            [
              numerListu,
              nazwaFirmy,
              liczbaPaczek,
              liczbaPalet,
              kierunek,
              komentarz = "", // 6th column (optional)
              dataUtworzenia = "", // 7th column (optional)
              createdByUsername = "", // 8th column (optional)
            ] = columns;
          } else {
            // Regular user can have 5-6 columns
            [
              numerListu,
              nazwaFirmy,
              liczbaPaczek,
              liczbaPalet,
              kierunek,
              komentarz = "", // 6th column (optional)
            ] = columns;
          }

          // Basic validation for each entry
          if (
            !numerListu ||
            !nazwaFirmy ||
            !liczbaPaczek ||
            !liczbaPalet ||
            !kierunek
          ) {
            alert(`Wiersz ${i + 1}: Wszystkie podstawowe pola są wymagane.`);
            setIsSubmitting(false);
            return;
          }

          // Validate date format (YYYY-MM-DD) only if provided
          if (dataUtworzenia && dataUtworzenia.trim()) {
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(dataUtworzenia.trim())) {
              alert(
                `Wiersz ${
                  i + 1
                }: Data utworzenia musi być w formacie YYYY-MM-DD (np. 2024-11-24).`,
              );
              setIsSubmitting(false);
              return;
            }
          }

          entries.push({
            numerListu: numerListu.trim(),
            nazwaFirmy: nazwaFirmy.trim(),
            liczbaPaczek: liczbaPaczek.trim(),
            liczbaPalet: liczbaPalet.trim(),
            kierunek: kierunek.trim(),
            komentarz: komentarz.trim(),
            dataUtworzenia: dataUtworzenia.trim(),
            createdByUsername: createdByUsername.trim(),
          });
        }

        // Check for duplicates FIRST - reject duplicates, don't save them
        const duplicateEntries = [];
        const validEntries = [];

        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          try {
            const checkResponse = await axios.get(
              `${BASE_URL}/check-numer-listu-magazyn-warszawa-handling`,
              {
                params: {
                  numerListu: entry.numerListu,
                  rodzajTransportu: formData.rodzajTransportu,
                },
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                  "Content-Type": "application/json",
                },
              },
            );

            if (checkResponse.data.exists) {
              duplicateEntries.push({
                ...entry,
                lineNumber: i + 1,
                originalLine: lines[i],
              });
            } else {
              validEntries.push({
                ...entry,
                lineNumber: i + 1,
              });
            }
          } catch (err) {
            console.error("Błąd przy sprawdzaniu duplikatów:", err);
            // W przypadku błędu sprawdzania, traktujemy jako prawidłowy wpis
            validEntries.push({
              ...entry,
              lineNumber: i + 1,
            });
          }
        }

        // Save all valid entries (no duplicates) and track failures
        let successCount = 0;
        let errorCount = 0;
        const errors = [];
        const failedLines = [];

        for (let i = 0; i < validEntries.length; i++) {
          const entry = validEntries[i];
          try {
            const saveResponse = await axios.post(
              `${BASE_URL}/magazyn-warszawa-handling`,
              {
                ...entry,
                username,
                createdBy: entry.createdByUsername || username,
                rodzaj: formData.rodzaj,
                rodzajTransportu: formData.rodzajTransportu,
              },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                  "Content-Type": "application/json",
                },
              },
            );

            // Save comment if provided
            if (entry.komentarz && entry.komentarz.trim()) {
              try {
                await axios.post(
                  `${BASE_URL}/save-comment`,
                  {
                    customId: saveResponse.data.insertId,
                    comment: entry.komentarz.trim(),
                    username: username,
                  },
                  {
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                      "Content-Type": "application/json",
                    },
                  },
                );
              } catch (commentErr) {
                console.warn(
                  `Błąd przy zapisie komentarza dla wiersza ${entry.lineNumber}:`,
                  commentErr,
                );
              }
            }

            successCount++;
          } catch (err) {
            const errorMessage =
              err.response?.data?.message || "Błąd przy zapisie";
            errors.push(`Wiersz ${entry.lineNumber}: ${errorMessage}`);
            failedLines.push(lines[entry.lineNumber - 1]);
            errorCount++;
          }
        }

        // Combine duplicates and failed entries for textarea
        const allFailedLines = [
          ...duplicateEntries.map((item) => item.originalLine),
          ...failedLines,
        ];

        // Update textarea with failed entries (duplicates + save errors)
        if (allFailedLines.length > 0) {
          setFormData((prevData) => ({
            ...prevData,
            magazynHandlingMultipleEntries: allFailedLines.join("\n"),
          }));
        } else {
          // Clear textarea if all entries were successful
          setFormData((prevData) => ({
            ...prevData,
            magazynHandlingMultipleEntries: "",
          }));
        }

        // Prepare summary information
        const duplicateCount = duplicateEntries.length;
        const totalFailures = errorCount + duplicateCount;

        // Helper function for Polish grammar
        const getPolishForm = (count, singular, plural2to4, pluralMore) => {
          if (count === 1) return singular;
          if (count >= 2 && count <= 4) return plural2to4;
          return pluralMore;
        };

        // Show results
        if (successCount > 0 && totalFailures === 0) {
          // All entries saved successfully
          const recordForm = getPolishForm(
            successCount,
            "rekord",
            "rekordy",
            "rekordów",
          );
          const message = `Zapisano pomyślnie wszystkie ${successCount} ${recordForm}!`;

          toast.success(message, {
            autoClose: 2000,
            onClose: () => navigate("/magazynWarszawaHandling"),
          });
        } else if (successCount > 0 && totalFailures > 0) {
          // Some entries saved, some failed (duplicates + errors)
          const successForm = getPolishForm(
            successCount,
            "rekord",
            "rekordy",
            "rekordów",
          );

          let summaryMessage = `PODSUMOWANIE ZAPISU:\n\n✅ ZAPISANO POMYŚLNIE: ${successCount} ${successForm}`;

          if (duplicateCount > 0) {
            const duplicateForm = getPolishForm(
              duplicateCount,
              "duplikat",
              "duplikaty",
              "duplikatów",
            );
            const duplicateNumbers = duplicateEntries.map(
              (item) => item.numerListu,
            );
            summaryMessage += `\n\n❌ ODRZUCONO (duplikaty): ${duplicateCount} ${duplicateForm}\nNumery: ${duplicateNumbers.join(
              ", ",
            )}`;
          }

          if (errorCount > 0) {
            const errorForm = getPolishForm(
              errorCount,
              "błąd",
              "błędy",
              "błędów",
            );
            summaryMessage += `\n\n❌ BŁĘDY ZAPISU: ${errorCount} ${errorForm}\n${errors.join(
              "\n",
            )}`;
          }

          summaryMessage += `\n\n📝 Duplikaty zostały w polu do poprawy.`;

          toast.warning(summaryMessage, {
            autoClose: 10000,
            style: { whiteSpace: "pre-line" },
          });
        } else if (
          successCount === 0 &&
          duplicateCount > 0 &&
          errorCount === 0
        ) {
          // Only duplicates, no successful saves
          const duplicateForm = getPolishForm(
            duplicateCount,
            "duplikat",
            "duplikaty",
            "duplikatów",
          );
          const duplicateNumbers = duplicateEntries.map(
            (item) => item.numerListu,
          );

          toast.error(
            `Wszystkie wpisy to ${duplicateForm} (${duplicateCount}):\n${duplicateNumbers.join(
              "\n",
            )}\n\nNic nie zostało zapisane. Popraw numery i spróbuj ponownie.`,
            {
              autoClose: 8000,
              style: { whiteSpace: "pre-line" },
            },
          );
        } else {
          // Mix of duplicates and save errors, no successful saves
          let errorMessage = "Nie udało się zapisać żadnego wpisu:\n";

          if (duplicateCount > 0) {
            const duplicateForm = getPolishForm(
              duplicateCount,
              "duplikat",
              "duplikaty",
              "duplikatów",
            );
            const duplicateNumbers = duplicateEntries.map(
              (item) => item.numerListu,
            );
            errorMessage += `\n❌ ${duplicateCount} ${duplicateForm}:\n${duplicateNumbers.join(
              "\n",
            )}`;
          }

          if (errorCount > 0) {
            errorMessage += `\n❌ Błędy przy zapisie: ${errorCount}\n${errors.join(
              "\n",
            )}`;
          }

          toast.error(errorMessage, {
            autoClose: 8000,
            style: { whiteSpace: "pre-line" },
          });
        }

        setIsSubmitting(false);
        return;
      }

      // Single entry logic
      const { numerListu, nazwaFirmy, liczbaPaczek, liczbaPalet, kierunek } =
        formData.magazynWarszawaHandling || {};

      // Basic validation
      if (
        !numerListu ||
        !nazwaFirmy ||
        !liczbaPaczek ||
        !liczbaPalet ||
        !kierunek
      ) {
        alert("Wypełnij wszystkie wymagane pola Magazyn Warszawa Handling.");
        setIsSubmitting(false);
        return;
      }

      try {
        const checkResponse = await axios.get(
          `${BASE_URL}/check-numer-listu-magazyn-warszawa-handling`,
          {
            params: { numerListu, rodzajTransportu: formData.rodzajTransportu },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (checkResponse.data.exists) {
          toast.error(checkResponse.data.message, {
            autoClose: 3000,
          });
          setIsSubmitting(false);
          return;
        }

        const saveResponse = await axios.post(
          `${BASE_URL}/magazyn-warszawa-handling`,
          {
            numerListu,
            nazwaFirmy,
            liczbaPaczek,
            liczbaPalet,
            kierunek,
            rodzajTransportu: formData.rodzajTransportu,
            username,
            rodzaj: formData.rodzaj,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        // Save comment if provided
        const komentarz = formData.komentarz;
        if (komentarz && komentarz.trim()) {
          try {
            await axios.post(
              `${BASE_URL}/save-comment`,
              {
                customId: saveResponse.data.insertId,
                comment: komentarz.trim(),
                username: username,
              },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                  "Content-Type": "application/json",
                },
              },
            );
          } catch (commentErr) {
            console.warn("Błąd przy zapisie komentarza:", commentErr);
          }
        }

        toast.success("Zgłoszenie Magazyn Warszawa Handling zapisane!", {
          autoClose: 2000,
          onClose: () => navigate("/magazynWarszawaHandling"),
        });
      } catch (err) {
        toast.error("Błąd przy zapisie Magazyn Warszawa Handling.", {
          autoClose: 3000,
        });
      }
      setIsSubmitting(false);
      return;
    }

    if (formData.rodzajTransportu === "Magazyn Warszawa Zwroty i Błędy") {
      // Check if it's multiple entries
      if (magazynZwrotyEntryType === "multiple") {
        // Parse multiple entries from textarea
        const textData = formData.magazynZwrotyMultipleEntries || "";
        if (!textData.trim()) {
          alert("Wprowadź dane do przetworzenia.");
          setIsSubmitting(false);
          return;
        }

        const lines = textData.trim().split("\n");
        const entries = [];

        // Parse each line
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue; // Skip empty lines

          const columns = line.split("\t"); // Split by tab

          // Different validation based on user role
          const expectedColumns = userRole === "admin" ? 8 : 8; // Both need minimum 8 columns

          if (columns.length < expectedColumns) {
            const columnDesc =
              userRole === "admin"
                ? "Nadawca | Nr listu | Liczba paczek | Błąd dokumentacji | Rodzaj błędu | Zwrot | Powód | Wniosek | Komentarz (opcjonalny) | Data utworzenia (opcjonalna) | Username (opcjonalny)"
                : "Nadawca | Nr listu | Liczba paczek | Błąd dokumentacji | Rodzaj błędu | Zwrot | Powód | Wniosek | Komentarz (opcjonalny)";
            alert(
              `Wiersz ${
                i + 1
              } ma nieprawidłową liczbę kolumn. Oczekiwano minimum ${expectedColumns} kolumn: ${columnDesc}.`,
            );
            setIsSubmitting(false);
            return;
          }

          let nadawca,
            numerListu,
            liczbaPaczek,
            bladDokumentacji,
            rodzajBledu,
            zwrot,
            powod,
            wniosek,
            komentarz = "",
            dataUtworzenia = "",
            createdByUsername = "";

          if (userRole === "admin") {
            // Admin can have 8-11 columns
            [
              nadawca,
              numerListu,
              liczbaPaczek,
              bladDokumentacji,
              rodzajBledu,
              zwrot,
              powod,
              wniosek,
              komentarz = "", // 9th column (optional)
              dataUtworzenia = "", // 10th column (optional)
              createdByUsername = "", // 11th column (optional)
            ] = columns;
          } else {
            // Regular user can have 8-9 columns
            [
              nadawca,
              numerListu,
              liczbaPaczek,
              bladDokumentacji,
              rodzajBledu,
              zwrot,
              powod,
              wniosek,
              komentarz = "", // 9th column (optional)
            ] = columns;
          }

          // Basic validation for each entry
          if (
            !nadawca ||
            !numerListu ||
            !liczbaPaczek ||
            !bladDokumentacji ||
            !rodzajBledu ||
            !zwrot ||
            !wniosek
          ) {
            alert(`Wiersz ${i + 1}: Wszystkie podstawowe pola są wymagane.`);
            setIsSubmitting(false);
            return;
          }

          // Validate date format (YYYY-MM-DD) only if provided
          if (dataUtworzenia && dataUtworzenia.trim()) {
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(dataUtworzenia.trim())) {
              alert(
                `Wiersz ${
                  i + 1
                }: Data utworzenia musi być w formacie YYYY-MM-DD (np. 2024-11-24).`,
              );
              setIsSubmitting(false);
              return;
            }
          }

          // Additional validation for powod when zwrot is "Tak"
          if (zwrot === "Tak" && !powod) {
            alert(
              `Wiersz ${i + 1}: Gdy wybrano zwrot 'Tak', należy podać powód.`,
            );
            setIsSubmitting(false);
            return;
          }

          entries.push({
            nadawca: nadawca.trim(),
            numerListu: numerListu.trim(),
            liczbaPaczek: liczbaPaczek.trim(),
            bladDokumentacji: bladDokumentacji.trim(),
            rodzajBledu: rodzajBledu.trim(),
            zwrot: zwrot.trim(),
            powod: powod.trim(),
            wniosek: wniosek.trim(),
            komentarz: komentarz.trim(),
            dataUtworzenia: dataUtworzenia.trim(),
            createdByUsername: createdByUsername.trim(),
          });
        }

        // Process each entry
        let successCount = 0;
        let errorCount = 0;
        let duplicateCount = 0;
        const errors = [];
        const duplicates = [];
        const failedLines = []; // Store original lines that failed to save

        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          const originalLine = lines[i]; // Keep reference to original line

          try {
            // Check if numerListu already exists
            const checkResponse = await axios.get(
              `${BASE_URL}/check-numer-listu-magazyn-warszawa-zwroty-i-bledy`,
              {
                params: {
                  numerListu: entry.numerListu,
                  rodzajTransportu: formData.rodzajTransportu,
                },
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                  "Content-Type": "application/json",
                },
              },
            );

            // Determine if it's a duplicate
            const isDuplicate = checkResponse.data.isDuplicate || false;

            // Save entry (whether it's new or duplicate)
            const saveResponse = await axios.post(
              `${BASE_URL}/magazyn-warszawa-zwroty-i-bledy`,
              {
                ...entry,
                username,
                createdBy: entry.createdByUsername || username,
                rodzaj: formData.rodzaj,
                rodzajTransportu: formData.rodzajTransportu,
                duplikat: isDuplicate ? 1 : 0,
              },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                  "Content-Type": "application/json",
                },
              },
            );

            // Save comment if provided
            if (entry.komentarz && entry.komentarz.trim()) {
              try {
                await axios.post(
                  `${BASE_URL}/save-comment`,
                  {
                    customId: saveResponse.data.insertId,
                    comment: entry.komentarz.trim(),
                    username: username,
                  },
                  {
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                      "Content-Type": "application/json",
                    },
                  },
                );
              } catch (commentErr) {
                console.warn(
                  `Błąd przy zapisie komentarza dla wiersza ${i + 1}:`,
                  commentErr,
                );
                // Nie przerywamy procesu jeśli komentarz się nie zapisał
              }
            }

            successCount++;
            if (isDuplicate) {
              duplicateCount++;
              duplicates.push(
                `Wiersz ${i + 1}: ${entry.numerListu} (duplikat)`,
              );
            }
            // Don't add to failedLines - this line was successful
          } catch (err) {
            const errorMessage =
              err.response?.data?.message || "Błąd przy zapisie";
            errors.push(`Wiersz ${i + 1}: ${errorMessage}`);
            failedLines.push(originalLine); // Add failed line to keep in textarea
            errorCount++;
          }
        }

        // Update textarea with only failed entries
        if (errorCount > 0) {
          setFormData((prevData) => ({
            ...prevData,
            magazynZwrotyMultipleEntries: failedLines.join("\n"),
          }));
        } else {
          // Clear textarea if all entries were successful
          setFormData((prevData) => ({
            ...prevData,
            magazynZwrotyMultipleEntries: "",
          }));
        }

        // Helper function for Polish grammar
        const getPolishForm = (count, singular, plural2to4, pluralMore) => {
          if (count === 1) return singular;
          if (count >= 2 && count <= 4) return plural2to4;
          return pluralMore;
        };

        // Show results
        if (successCount > 0 && errorCount === 0) {
          // All entries saved successfully
          const recordForm = getPolishForm(
            successCount,
            "rekord",
            "rekordy",
            "rekordów",
          );

          let message = `Zapisano pomyślnie wszystkie ${successCount} ${recordForm}!`;
          if (duplicateCount > 0) {
            const duplicateForm = getPolishForm(
              duplicateCount,
              "duplikat",
              "duplikaty",
              "duplikatów",
            );
            message += `\n(w tym ${duplicateCount} ${duplicateForm})`;
          }

          const confirmWithDuplicates =
            duplicateCount > 0
              ? `${message}\n\nDUPLIKATY:
${duplicates.join("\n")}

Czy chcesz przejść do listy zapisanych wpisów?`
              : null;

          if (confirmWithDuplicates) {
            if (window.confirm(confirmWithDuplicates)) {
              navigate("/magazynWarszawaZwrotyIBledy");
            }
          } else {
            toast.success(message, {
              autoClose: 2000,
              onClose: () => navigate("/magazynWarszawaZwrotyIBledy"),
            });
          }
        } else if (successCount > 0 && errorCount > 0) {
          // Some entries saved, some failed
          const detailedErrors = errors.join("\n");
          const successForm = getPolishForm(
            successCount,
            "rekord",
            "rekordy",
            "rekordów",
          );
          const errorForm = getPolishForm(
            errorCount,
            "rekordzie",
            "rekordach",
            "rekordach",
          );

          let confirmMessage = `PODSUMOWANIE ZAPISU:
✅ Zapisano pomyślnie: ${successCount} ${successForm}`;

          if (duplicateCount > 0) {
            const duplicateForm = getPolishForm(
              duplicateCount,
              "duplikat",
              "duplikaty",
              "duplikatów",
            );
            confirmMessage += ` (w tym ${duplicateCount} ${duplicateForm})`;
          }

          confirmMessage += `
❌ Błędy w: ${errorCount} ${errorForm}

SZCZEGÓŁY BŁĘDÓW:
${detailedErrors}`;

          if (duplicateCount > 0) {
            confirmMessage += `

DUPLIKATY:
${duplicates.join("\n")}`;
          }

          confirmMessage += `

Czy chcesz przejść do listy zapisanych wpisów?`;

          if (window.confirm(confirmMessage)) {
            navigate("/magazynWarszawaZwrotyIBledy");
          }

          const toastSuccessForm = getPolishForm(
            successCount,
            "rekord",
            "rekordy",
            "rekordów",
          );
          const totalForm = getPolishForm(
            successCount + errorCount,
            "rekordu",
            "rekordów",
            "rekordów",
          );
          toast.success(
            `Zapisano pomyślnie ${successCount} ${toastSuccessForm} z ${
              successCount + errorCount
            } ${totalForm}`,
            {
              autoClose: 4000,
            },
          );
        } else if (errorCount > 0) {
          // All entries failed
          const detailedErrors = errors.join("\n");
          const errorForm = getPolishForm(
            errorCount,
            "wpis zawiera",
            "wpisy zawierają",
            "wpisów zawiera",
          );
          alert(`WSZYSTKIE ${errorCount} ${errorForm} BŁĘDY:
${detailedErrors}

Popraw błędy i spróbuj ponownie.`);
        }

        setIsSubmitting(false);
        return;
      }

      // Single entry logic (original code)
      const {
        numerListu,
        liczbaPaczek,
        bladDokumentacji,
        rodzajBledu,
        zwrot,
        powod,
        wniosek,
        nadawca,
        komentarz,
      } = formData.magazynWarszawaZwrotyIBledy || {};

      // Basic validation
      if (
        !numerListu ||
        !liczbaPaczek ||
        !bladDokumentacji ||
        !rodzajBledu ||
        !zwrot ||
        !wniosek ||
        !nadawca
      ) {
        alert("Wypełnij wszystkie wymagane pola.");
        setIsSubmitting(false);
        return;
      }

      // Additional validation for powod when zwrot is "Tak"
      if (zwrot === "Tak" && !powod) {
        alert("Gdy wybrano zwrot 'Tak', należy podać powód.");
        setIsSubmitting(false);
        return;
      }

      try {
        // Check if numerListu already exists for this specific transport type
        const checkResponse = await axios.get(
          `${BASE_URL}/check-numer-listu-magazyn-warszawa-zwroty-i-bledy`,
          {
            params: { numerListu, rodzajTransportu: formData.rodzajTransportu },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        // Determine if it's a duplicate and inform user
        const isDuplicate = checkResponse.data.isDuplicate || false;

        if (isDuplicate) {
          const confirmDuplicate = window.confirm(
            `UWAGA: ${checkResponse.data.message}\n\nCzy chcesz kontynuować zapis jako duplikat?`,
          );
          if (!confirmDuplicate) {
            setIsSubmitting(false);
            return;
          }
        }

        // Proceed with saving
        const saveResponse = await axios.post(
          `${BASE_URL}/magazyn-warszawa-zwroty-i-bledy`,
          {
            numerListu,
            liczbaPaczek,
            bladDokumentacji,
            rodzajBledu,
            zwrot,
            powod,
            wniosek,
            username,
            rodzaj: formData.rodzaj,
            rodzajTransportu: formData.rodzajTransportu,
            nadawca,
            duplikat: isDuplicate ? 1 : 0,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        // Save comment if provided
        if (komentarz && komentarz.trim()) {
          try {
            await axios.post(
              `${BASE_URL}/save-comment`,
              {
                customId: saveResponse.data.insertId,
                comment: komentarz.trim(),
                username: username,
              },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                  "Content-Type": "application/json",
                },
              },
            );
          } catch (commentErr) {
            console.warn("Błąd przy zapisie komentarza:", commentErr);
            // Nie przerywamy procesu jeśli komentarz się nie zapisał
          }
        }

        const successMessage = isDuplicate
          ? "Zgłoszenie Magazyn Warszawa Zwroty i Błędy zapisane jako duplikat!"
          : "Zgłoszenie Magazyn Warszawa Zwroty i Błędy zapisane!";

        toast.success(successMessage, {
          autoClose: 2000,
          onClose: () => navigate("/magazynWarszawaZwrotyIBledy"),
        });
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          "Błąd przy zapisie Magazyn Warszawa Zwroty i Błędy.";
        toast.error(errorMessage, {
          autoClose: 3000,
        });
      }

      setIsSubmitting(false);
      return;
    }

    if (!validateForm(formData, setIsSubmitting)) return;

    if (
      formData.rodzaj === "Import" ||
      formData.rodzaj === "Import - wpis do rejestru" ||
      formData.rodzaj === "Symulacja sad"
    ) {
      await handleImportSubmission({
        formData,
        setIsSubmitting,
        username,
        BASE_URL,
        checkValuesInDatabase,
        navigate,
      });
      return;
    }

    if (
      formData.rodzaj === "Eksport" ||
      formData.rodzaj === "Wyprowadzenie ze składu" ||
      formData.rodzaj === "Uszlachetnianie bierne" ||
      formData.rodzaj === "Powrotny wywóz" ||
      formData.rodzaj === "Odprawa czasowa eksport" ||
      formData.rodzaj === "Eksport - wpis do rejestru" ||
      formData.rodzaj === "Eksport PL i import inny kraj"
    ) {
      await handleExportSubmission({
        formData,
        setIsSubmitting,
        username,
        BASE_URL,
        checkValuesInDatabase,
        navigate,
      });
      return;
    }

    if (formData.rodzaj === "Tranzyt") {
      await handleTranzytSubmission({
        formData,
        setIsSubmitting,
        username,
        BASE_URL,
        checkValuesInDatabase,
        navigate,
      });
      return;
    }

    if (
      formData.rodzaj === "Wprowadzenie na skład" ||
      formData.rodzaj === "Uszlachetnianie czynne" ||
      formData.rodzaj === "Powrotny przywóz" ||
      formData.rodzaj === "Odprawa czasowa import"
    ) {
      await handleWprowadzenieNaSkald({
        formData,
        setIsSubmitting,
        username,
        BASE_URL,
        checkValuesInDatabase,
        navigate,
      });
      return;
    }
  };

  useEffect(() => {
    handleUsersChange();
    handleLokalizacjaChange();
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "formTemplateDraftSnapshot",
      JSON.stringify(
        stripTemplateDynamicFields({
          formData,
          statusRealizacjiFields,
        }),
      ),
    );
  }, [formData, statusRealizacjiFields, stripTemplateDynamicFields]);

  useEffect(() => {
    if (!isTemplateBuilderMode) {
      return;
    }

    const invoiceInputs = document.querySelectorAll('input[name^="nrFaktury"]');
    invoiceInputs.forEach((input) => {
      input.disabled = true;
      input.title = "Pole dynamiczne. Szablony nie obsługują numerów faktur.";
    });
  }, [isTemplateBuilderMode, formData.fields, formData.rodzaj]);

  useEffect(() => {
    const templateIdFromPayload =
      forcedTemplateId ||
      new URLSearchParams(location.search).get("templateId");
    const shouldInitializeFromTemplate =
      isTemplateBuilderMode || Boolean(templateIdFromPayload);

    if (!shouldInitializeFromTemplate || !formData.rodzaj) {
      return;
    }

    if (
      formData.rodzaj === "Import" ||
      formData.rodzaj === "Import - wpis do rejestru" ||
      formData.rodzaj === "Symulacja sad"
    ) {
      handleImportersFetch();
    } else if (formData.rodzaj === "Eksport") {
      handleExportersFetch();
    } else if (formData.rodzaj === "Tranzyt") {
      handleUrzadTranzytowyChange();
      handleExportersFetch();
      handleUrzadPrzeznaczeniaChange();
    } else {
      handleImportersFetch();
      handleExportersFetch();
    }

    if (
      formData.importer === "MAN BUS SP. Z O.O." &&
      manExportersList.length === 0
    ) {
      handleManExportersFetch();
    }
  }, [
    isTemplateBuilderMode,
    formData.rodzaj,
    formData.importer,
    manExportersList.length,
    forcedTemplateId,
    location.search,
  ]);

  useEffect(() => {
    const templateId =
      forcedTemplateId ||
      new URLSearchParams(location.search).get("templateId");

    if (!templateId) {
      return;
    }

    if (lastAppliedTemplateIdRef.current === templateId) {
      return;
    }

    let cancelled = false;

    const apply = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/dane-templates/${templateId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        applyTemplatePayloadToForm(response.data?.payload);

        if (!cancelled) {
          lastAppliedTemplateIdRef.current = templateId;
          toast.success("Zastosowano wybrany szablon.");
          if (!embedded) {
            navigate(
              isTemplateBuilderMode ? "/form?templateBuilder=1" : "/form",
              { replace: true },
            );
          }
        }
      } catch (error) {
        console.error("Error applying template from URL:", error);
        if (!cancelled) {
          toast.error("Nie udało się zastosować szablonu.");
        }
      }
    };

    apply();

    return () => {
      cancelled = true;
    };
  }, [
    location.search,
    forcedTemplateId,
    navigate,
    applyTemplatePayloadToForm,
    isTemplateBuilderMode,
    embedded,
  ]);

  // Check if MAN BUS is selected when importer changes
  useEffect(() => {
    if (formData.importer === "MAN BUS SP. Z O.O.") {
      setIsManBusSelected(true);
      if (manExportersList.length === 0) {
        handleManExportersFetch();
      }
    } else {
      setIsManBusSelected(false);
    }
  }, [formData.importer, manExportersList.length]);

  // Handle clicks outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        importerDropdownRef.current &&
        !importerDropdownRef.current.contains(event.target)
      ) {
        if (listOpen && listType === "importer") {
          setListOpen(false);
        }
      }
      if (
        exporterDropdownRef.current &&
        !exporterDropdownRef.current.contains(event.target)
      ) {
        if (listOpen && listType === "eksporter") {
          setListOpen(false);
        }
      }

      // Close MAN exporter dropdowns when clicking outside
      Object.keys(openManExporterDropdowns).forEach((fieldIndex) => {
        if (openManExporterDropdowns[fieldIndex]) {
          // Check if click is outside any MAN exporter dropdown
          const isOutsideClick = !event.target.closest(
            `[data-man-exporter-dropdown="${fieldIndex}"]`,
          );
          if (isOutsideClick) {
            closeManExporterDropdown(parseInt(fieldIndex));
          }
        }
      });

      // Close Magazyn Warszawa dropdowns when clicking outside
      if (
        listOpen &&
        (listType === "magazynWarszawaNadawca" ||
          listType === "magazynWarszawaHandlingNazwaFirmy" ||
          listType === "magazynWarszawaZwrotyNadawca")
      ) {
        const isOutsideClick = !event.target.closest(
          "[data-magazyn-warszawa-dropdown]",
        );
        if (isOutsideClick) {
          setListOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [listOpen, listType, openManExporterDropdowns]);

  const handleInvoiceCountChange = (index, count) => {
    const safeCount = Number.isFinite(count) ? Math.max(0, count) : 0;

    setFormData((prevData) => {
      const updatedFields = [...prevData.fields];
      const currentInvoices = [...(updatedFields[index]?.invoiceNumbers || [])];
      const hiddenInvoices = [
        ...(hiddenInvoiceNumbersRef.current[index] || []),
      ];

      if (safeCount < currentInvoices.length) {
        // Keep removed values in memory so they can be restored after increasing count.
        const removedInvoices = currentInvoices.slice(safeCount);
        hiddenInvoiceNumbersRef.current[index] = [
          ...removedInvoices,
          ...hiddenInvoices,
        ];
        updatedFields[index].invoiceNumbers = currentInvoices.slice(
          0,
          safeCount,
        );
        updatedFields[index].invoiceCount = safeCount;
      } else if (safeCount > currentInvoices.length) {
        const missing = safeCount - currentInvoices.length;
        const restored = hiddenInvoices.slice(0, missing);
        const stillHidden = hiddenInvoices.slice(missing);

        while (restored.length < missing) {
          restored.push("");
        }

        hiddenInvoiceNumbersRef.current[index] = stillHidden;
        updatedFields[index].invoiceNumbers = [...currentInvoices, ...restored];
        updatedFields[index].invoiceCount = safeCount;
      } else {
        updatedFields[index].invoiceNumbers = currentInvoices;
        updatedFields[index].invoiceCount = safeCount;
      }

      return { ...prevData, fields: updatedFields };
    });
  };

  const isMwNowyHandlingMulti =
    formData.rodzajTransportu === "Magazyn Warszawa Nowy Handling" &&
    mwNowyHandlingEntryType === "multiple" &&
    Number(mwNowyHandlingCount) >= 1;

  return (
    <div className={classes.root}>
      <main className={classes.content} style={{ padding: 0 }}>
        <Paper
          elevation={0}
          className={classes.formContainer}
          style={
            isMwNowyHandlingMulti ? { maxWidth: "min(1040px, 88vw)" } : undefined
          }
        >
          <Typography variant="h5" className={classes.formTitle}>
            {isTemplateBuilderMode
              ? "Formularz szablonu zgłoszenia"
              : "Dodaj nowe zgłoszenie"}
          </Typography>
          <div className={classes.formSubtitle}>
            {isTemplateBuilderMode
              ? "Zapisywana jest tylko ilość faktur - numery pozostają dynamiczne."
              : "Wypełnij dane, aby utworzyć nowe zgłoszenie. Pola oznaczone * są wymagane."}
          </div>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3} justifyContent="center">
              <Grid item xs={12} sm={6}>
                <FormControl
                  component="fieldset"
                  fullWidth
                  className={classes.marginUpAndDown}
                >
                  <FormLabel component="legend">Rodzaj transportu*</FormLabel>
                  <RadioGroup
                    name="rodzajTransportu"
                    value={formData.rodzajTransportu}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="Kurier"
                      control={<Radio color="primary" />}
                      label="Kurier"
                    />
                    <FormControlLabel
                      value="Na Kołach"
                      control={<Radio color="primary" />}
                      label="Na Kołach"
                    />
                    <FormControlLabel
                      value="W drodze"
                      control={<Radio color="primary" />}
                      label="W drodze"
                    />
                    <FormControlLabel
                      value="Magazyn Warszawa Zwroty i Błędy"
                      control={<Radio color="primary" />}
                      label="Magazyn Warszawa Zwroty i Błędy"
                    />
                    <FormControlLabel
                      value="Magazyn Warszawa Nowy Handling"
                      control={<Radio color="primary" />}
                      label="Magazyn Warszawa"
                    />
                  </RadioGroup>
                </FormControl>
                <FormControl
                  component="fieldset"
                  fullWidth
                  className={classes.marginBottom}
                  disabled={
                    formData.rodzajTransportu ===
                    "Magazyn Warszawa Nowy Handling"
                  }
                >
                  <FormLabel component="legend">
                    Jaki jest to rodzaj odprawy*
                  </FormLabel>
                  <RadioGroup
                    required // Add required attribute
                    aria-label="Czy zostały dostarczone dokumenty przed przybyciem?"
                    name="rodzaj"
                    value={formData.rodzaj}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="Import"
                      control={<Radio color="primary" />}
                      label="Import"
                    />
                    <FormControlLabel
                      value="Eksport"
                      control={<Radio color="primary" />}
                      label="Eksport"
                    />
                    <FormControlLabel
                      value="Tranzyt"
                      control={<Radio color="primary" />}
                      label="Tranzyt"
                    />
                    <FormControlLabel
                      value="Wyprowadzenie ze składu"
                      control={<Radio color="primary" />}
                      label="Wyprowadzenie ze składu"
                    />
                    <FormControlLabel
                      value="Wprowadzenie na skład"
                      control={<Radio color="primary" />}
                      label="Wprowadzenie na skład"
                    />
                    <FormControlLabel
                      value="Uszlachetnianie czynne"
                      control={<Radio color="primary" />}
                      label="Uszlachetnianie czynne"
                    />
                    <FormControlLabel
                      value="Uszlachetnianie bierne"
                      control={<Radio color="primary" />}
                      label="Uszlachetnianie bierne"
                    />
                    <FormControlLabel
                      value="Powrotny przywóz"
                      control={<Radio color="primary" />}
                      label="Powrotny przywóz"
                    />
                    <FormControlLabel
                      value="Powrotny wywóz"
                      control={<Radio color="primary" />}
                      label="Powrotny wywóz"
                    />
                    {/* <FormControlLabel
                      value="Eksport - wpis do rejestru"
                      control={<Radio color="primary" />}
                      label="Eksport - wpis do rejestru"
                    /> */}
                    <FormControlLabel
                      value="Import - wpis do rejestru"
                      control={<Radio color="primary" />}
                      label="Import - wpis do rejestru"
                    />
                    <FormControlLabel
                      value="Symulacja sad"
                      control={<Radio color="primary" />}
                      label="Symulacja sad"
                    />
                    <FormControlLabel
                      value="Eksport PL i import inny kraj"
                      control={<Radio color="primary" />}
                      label="Eksport PL i import inny kraj"
                    />
                    <FormControlLabel
                      value="Odprawa czasowa import"
                      control={<Radio color="primary" />}
                      label="Odprawa czasowa import"
                    />
                    <FormControlLabel
                      value="Odprawa czasowa eksport"
                      control={<Radio color="primary" />}
                      label="Odprawa czasowa eksport"
                    />
                  </RadioGroup>
                </FormControl>
                {/* --- TYLKO JEDEN WARUNEK --- */}
                {formData.rodzajTransportu ===
                "Magazyn Warszawa Nowy Handling" ? (
                  <>
                    <Grid item xs={12}>
                      <FormControl
                        component="fieldset"
                        fullWidth
                        className={classes.marginBottom}
                      >
                        <FormLabel component="legend">
                          Wybierz sposób wprowadzania danych*
                        </FormLabel>
                        <RadioGroup
                          name="mwNowyHandlingEntryType"
                          value={mwNowyHandlingEntryType}
                          onChange={(e) =>
                            setMwNowyHandlingEntryType(e.target.value)
                          }
                        >
                          <FormControlLabel
                            value="single"
                            control={<Radio color="primary" />}
                            label="Wprowadź jeden wpis"
                          />
                          <FormControlLabel
                            value="multiple"
                            control={<Radio color="primary" />}
                            label="Wprowadź wiele wpisów"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    {mwNowyHandlingEntryType === "single" && (
                      <>
                        <Grid item xs={12}>
                          <MwNowyHandlingSingle
                            value={mwNowyHandlingSingleRow}
                            onChange={setMwNowyHandlingSingleRow}
                            companies={magazynWarszawaCompaniesList}
                            onAddCompany={handleAddMagazynWarszawaCompany}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            type="button"
                            variant="contained"
                            color="primary"
                            fullWidth
                            className={classes.marginBottom}
                            disabled={isSubmitting}
                            onClick={() => submitMwNowyHandling("single")}
                          >
                            {isSubmitting ? "Zapisywanie..." : "Zapisz"}
                          </Button>
                        </Grid>
                      </>
                    )}

                    {mwNowyHandlingEntryType === "multiple" && (
                      <>
                        <Grid item xs={12}>
                          <TextField
                            required
                            type="number"
                            label="Ile nowych wpisów"
                            name="mwNowyHandlingCount"
                            value={mwNowyHandlingCount}
                            onChange={(e) =>
                              setMwNowyHandlingCount(e.target.value)
                            }
                            fullWidth
                            className={classes.marginBottom}
                            inputProps={{ min: 1, step: 1 }}
                            error={
                              mwNowyHandlingCount !== "" &&
                              Number(mwNowyHandlingCount) < 1
                            }
                            helperText={
                              mwNowyHandlingCount !== "" &&
                              Number(mwNowyHandlingCount) < 1
                                ? "Podaj liczbę większą lub równą 1"
                                : "Liczba wpisów do wprowadzenia"
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            type="button"
                            variant="outlined"
                            color="default"
                            startIcon={<GetAppIcon />}
                            className={classes.marginBottom}
                            onClick={handleDownloadMwNowyHandlingTemplate}
                          >
                            Pobierz szablon Excel
                          </Button>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            className={classes.marginBottom}
                          >
                            Pobierz szablon, uzupełnij dane w Excelu, a następnie
                            zaznacz i wklej je poniżej.
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            multiline
                            minRows={6}
                            maxRows={16}
                            fullWidth
                            className={classes.marginBottom}
                            label="Wklej dane z Excela"
                            placeholder={`Kolejność kolumn jak w tabeli:\n${MW_NOWY_HANDLING_COLUMNS.map(
                              (c) => c.label,
                            ).join("  |  ")}`}
                            value={mwNowyHandlingPasteText}
                            onChange={(e) =>
                              setMwNowyHandlingPasteText(e.target.value)
                            }
                            onPaste={handleMwNowyHandlingPasteEvent}
                            helperText="Wklej zakres z arkusza (kolumny oddzielone tabulatorem). Liczba wpisów ustawi się automatycznie, a tabela zwaliduje dane jak przy wpisywaniu ręcznym."
                          />
                          <Button
                            type="button"
                            variant="outlined"
                            color="primary"
                            className={classes.marginBottom}
                            onClick={() =>
                              convertMwNowyHandlingPaste(mwNowyHandlingPasteText)
                            }
                          >
                            Konwertuj do tabeli
                          </Button>
                        </Grid>
                      </>
                    )}
                  </>
                ) : formData.rodzajTransportu === "Magazyn Warszawa" ? (
                  <>
                    <TextField
                      required
                      label="Numer listu przewozowego"
                      name="magazynWarszawa.numerListu"
                      value={formData.magazynWarszawa?.numerListu || ""}
                      onChange={handleChange}
                      fullWidth
                      className={classes.marginBottom}
                      inputProps={{
                        maxLength: 14,
                        minLength: 14,
                      }}
                      error={
                        formData.magazynWarszawa?.numerListu &&
                        formData.magazynWarszawa.numerListu.length !== 14
                      }
                      helperText={
                        formData.magazynWarszawa?.numerListu &&
                        formData.magazynWarszawa.numerListu.length !== 14
                          ? "Numer listu przewozowego musi mieć dokładnie 14 znaków"
                          : ""
                      }
                    />
                    <div
                      style={{ position: "relative" }}
                      className={classes.marginBottom}
                      data-magazyn-warszawa-dropdown
                    >
                      <TextField
                        required
                        label="Nadawca (Nazwa firmy)"
                        name="magazynWarszawa.nadawca"
                        value={formData.magazynWarszawa?.nadawca || ""}
                        onChange={handleChange}
                        autoComplete="off"
                        fullWidth
                        disabled={isFetching}
                        onClick={() => handleListOpen("magazynWarszawaNadawca")}
                        onFocus={() => handleListOpen("magazynWarszawaNadawca")}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <span style={{ cursor: "pointer" }}>▼</span>
                          ),
                        }}
                      />
                      {listOpen && listType === "magazynWarszawaNadawca" && (
                        <div
                          style={{
                            position: "absolute",
                            zIndex: 1300,
                            width: "120%",
                            maxHeight: 450,
                            backgroundColor: "white",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            marginTop: "4px",
                            left: "-10%",
                          }}
                        >
                          <div
                            style={{
                              padding: "8px",
                              backgroundColor: "#f5f5f5",
                              borderBottom: "1px solid #eee",
                            }}
                          >
                            <TextField
                              placeholder="Szukaj firmy..."
                              value={magazynWarszawaSearchText}
                              onChange={(e) =>
                                filterMagazynWarszawaCompanies(e.target.value)
                              }
                              fullWidth
                              size="small"
                              variant="outlined"
                              autoFocus
                              InputProps={{
                                style: {
                                  fontSize: "14px",
                                  backgroundColor: "white",
                                },
                              }}
                            />
                          </div>
                          <div style={{ maxHeight: 350, overflowY: "auto" }}>
                            {magazynWarszawaFilteredList.map(
                              (company, index) => (
                                <div
                                  key={index}
                                  style={{
                                    padding: "8px 16px",
                                    cursor: "pointer",
                                    borderBottom: "1px solid #f0f0f0",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = "#f0f0f0";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  onClick={() => {
                                    setFormData((prevData) => ({
                                      ...prevData,
                                      magazynWarszawa: {
                                        ...prevData.magazynWarszawa,
                                        nadawca: company,
                                      },
                                    }));
                                    setMagazynWarszawaSearchText("");
                                    setListOpen(false);
                                  }}
                                >
                                  {company}
                                </div>
                              ),
                            )}
                            {magazynWarszawaFilteredList.length === 0 && (
                              <div
                                style={{
                                  padding: "16px",
                                  textAlign: "center",
                                  color: "#999",
                                }}
                              >
                                Brak wyników
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <TextField
                      required
                      label="Liczba paczek"
                      name="magazynWarszawa.liczbaPaczek"
                      value={formData.magazynWarszawa?.liczbaPaczek || ""}
                      onChange={handleChange}
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      fullWidth
                      className={classes.marginBottom}
                    />
                    <TextField
                      required
                      label="Liczba palet"
                      name="magazynWarszawa.liczbaPalet"
                      value={formData.magazynWarszawa?.liczbaPalet || ""}
                      onChange={handleChange}
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      fullWidth
                      className={classes.marginBottom}
                    />
                    <Grid item xs={12}>
                      <Button
                        type={isTemplateBuilderMode ? "button" : "submit"}
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={
                          isTemplateBuilderMode
                            ? templateSaveInProgress ||
                              typeof onTemplateSave !== "function"
                            : isSubmitting
                        }
                        onClick={
                          isTemplateBuilderMode
                            ? () => onTemplateSave && onTemplateSave()
                            : undefined
                        }
                      >
                        {isTemplateBuilderMode
                          ? templateSaveInProgress
                            ? "Zapisywanie szablonu..."
                            : "Zapisz szablon"
                          : isSubmitting
                            ? "Zapisywanie..."
                            : "Zapisz"}
                      </Button>
                    </Grid>
                  </>
                ) : formData.rodzajTransportu ===
                  "Magazyn Warszawa Handling" ? (
                  // Nowe pola dla Magazyn Warszawa odprawa inna agencja
                  <>
                    {/* Wybór typu wprowadzania danych */}
                    <Grid item xs={12}>
                      <FormControl
                        component="fieldset"
                        fullWidth
                        className={classes.marginBottom}
                      >
                        <FormLabel component="legend">
                          Wybierz sposób wprowadzania danych*
                        </FormLabel>
                        <RadioGroup
                          name="magazynHandlingEntryType"
                          value={magazynHandlingEntryType}
                          onChange={(e) =>
                            setMagazynHandlingEntryType(e.target.value)
                          }
                        >
                          <FormControlLabel
                            value="single"
                            control={<Radio color="primary" />}
                            label="Wprowadź jeden wpis"
                          />
                          <FormControlLabel
                            value="multiple"
                            control={<Radio color="primary" />}
                            label="Wprowadź wiele wpisów"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    {/* Formularz dla pojedynczego wpisu */}
                    {magazynHandlingEntryType === "single" && (
                      <>
                        <TextField
                          required
                          label="Numer listu przewozowego"
                          name="magazynWarszawaHandling.numerListu"
                          value={
                            formData.magazynWarszawaHandling?.numerListu || ""
                          }
                          onChange={handleChange}
                          fullWidth
                          className={classes.marginBottom}
                          inputProps={{
                            maxLength: 14,
                            minLength: 14,
                          }}
                          error={
                            formData.magazynWarszawaHandling?.numerListu &&
                            formData.magazynWarszawaHandling.numerListu
                              .length !== 14
                          }
                          helperText={
                            formData.magazynWarszawaHandling?.numerListu &&
                            formData.magazynWarszawaHandling.numerListu
                              .length !== 14
                              ? "Numer listu przewozowego musi mieć dokładnie 14 znaków"
                              : ""
                          }
                        />
                        <div
                          style={{ position: "relative" }}
                          className={classes.marginBottom}
                          data-magazyn-warszawa-dropdown
                        >
                          <TextField
                            required
                            label="Nadawca (Nazwa firmy)"
                            name="magazynWarszawaHandling.nazwaFirmy"
                            value={
                              formData.magazynWarszawaHandling?.nazwaFirmy || ""
                            }
                            onChange={handleChange}
                            autoComplete="off"
                            fullWidth
                            disabled={isFetching}
                            onClick={() =>
                              handleListOpen(
                                "magazynWarszawaHandlingNazwaFirmy",
                              )
                            }
                            onFocus={() =>
                              handleListOpen(
                                "magazynWarszawaHandlingNazwaFirmy",
                              )
                            }
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <span style={{ cursor: "pointer" }}>▼</span>
                              ),
                            }}
                          />
                          {listOpen &&
                            listType ===
                              "magazynWarszawaHandlingNazwaFirmy" && (
                              <div
                                style={{
                                  position: "absolute",
                                  zIndex: 1300,
                                  width: "120%",
                                  maxHeight: 450,
                                  backgroundColor: "white",
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                  marginTop: "4px",
                                  left: "-10%",
                                }}
                              >
                                <div
                                  style={{
                                    padding: "8px",
                                    backgroundColor: "#f5f5f5",
                                    borderBottom: "1px solid #eee",
                                  }}
                                >
                                  <TextField
                                    placeholder="Szukaj firmy..."
                                    value={magazynWarszawaSearchText}
                                    onChange={(e) =>
                                      filterMagazynWarszawaCompanies(
                                        e.target.value,
                                      )
                                    }
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    autoFocus
                                    InputProps={{
                                      style: {
                                        fontSize: "14px",
                                        backgroundColor: "white",
                                      },
                                    }}
                                  />
                                </div>
                                <div
                                  style={{ maxHeight: 350, overflowY: "auto" }}
                                >
                                  {magazynWarszawaFilteredList.map(
                                    (company, index) => (
                                      <div
                                        key={index}
                                        style={{
                                          padding: "8px 16px",
                                          cursor: "pointer",
                                          borderBottom: "1px solid #f0f0f0",
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.backgroundColor =
                                            "#f0f0f0";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor =
                                            "transparent";
                                        }}
                                        onClick={() => {
                                          setFormData((prevData) => ({
                                            ...prevData,
                                            magazynWarszawaHandling: {
                                              ...prevData.magazynWarszawaHandling,
                                              nazwaFirmy: company,
                                            },
                                          }));
                                          setMagazynWarszawaSearchText("");
                                          setListOpen(false);
                                        }}
                                      >
                                        {company}
                                      </div>
                                    ),
                                  )}
                                  {magazynWarszawaFilteredList.length === 0 && (
                                    <div
                                      style={{
                                        padding: "16px",
                                        textAlign: "center",
                                        color: "#999",
                                      }}
                                    >
                                      Brak wyników
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                        <TextField
                          required
                          label="Liczba Paczek"
                          name="magazynWarszawaHandling.liczbaPaczek"
                          value={
                            formData.magazynWarszawaHandling?.liczbaPaczek || ""
                          }
                          onChange={handleChange}
                          type="number"
                          inputProps={{ min: 0, step: 1 }}
                          fullWidth
                          className={classes.marginBottom}
                        />
                        <TextField
                          required
                          label="Liczba Palet"
                          name="magazynWarszawaHandling.liczbaPalet"
                          value={
                            formData.magazynWarszawaHandling?.liczbaPalet || ""
                          }
                          onChange={handleChange}
                          type="number"
                          inputProps={{ min: 0, step: 1 }}
                          fullWidth
                          className={classes.marginBottom}
                        />
                        <FormControl
                          required
                          fullWidth
                          className={classes.marginBottom}
                        >
                          <InputLabel>Kierunek</InputLabel>
                          <Select
                            name="magazynWarszawaHandling.kierunek"
                            value={
                              formData.magazynWarszawaHandling?.kierunek || ""
                            }
                            onChange={handleChange}
                          >
                            <MenuItem value="GB">GB</MenuItem>
                            <MenuItem value="CH">CH</MenuItem>
                            <MenuItem value="NO">NO</MenuItem>
                            <MenuItem value="BA">BA</MenuItem>
                            <MenuItem value="RS">RS</MenuItem>
                            <MenuItem value="Lotniczy">Lotniczy</MenuItem>
                          </Select>
                        </FormControl>

                        <TextField
                          label="Komentarz (opcjonalny)"
                          name="komentarz"
                          value={formData.komentarz || ""}
                          onChange={handleChange}
                          multiline
                          rows={3}
                          fullWidth
                          className={classes.marginBottom}
                          placeholder="Opcjonalny komentarz do wpisu..."
                        />

                        <Grid item xs={12}>
                          <Button
                            type={isTemplateBuilderMode ? "button" : "submit"}
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={
                              isTemplateBuilderMode
                                ? templateSaveInProgress ||
                                  typeof onTemplateSave !== "function"
                                : isSubmitting
                            }
                            onClick={
                              isTemplateBuilderMode
                                ? () => onTemplateSave && onTemplateSave()
                                : undefined
                            }
                          >
                            {isTemplateBuilderMode
                              ? templateSaveInProgress
                                ? "Zapisywanie szablonu..."
                                : "Zapisz szablon"
                              : isSubmitting
                                ? "Zapisywanie..."
                                : "Zapisz"}
                          </Button>
                        </Grid>
                      </>
                    )}

                    {/* Formularz dla wielu wpisów */}
                    {magazynHandlingEntryType === "multiple" && (
                      <>
                        <Grid item xs={12}>
                          <TextField
                            required
                            label={
                              userRole === "admin"
                                ? "Dane z Excel (Numer Listu | Nazwa Firmy | Liczba Paczek | Liczba Palet | Kierunek | Komentarz* | Data utworzenia* | Username*)"
                                : "Dane z Excel (Numer Listu | Nazwa Firmy | Liczba Paczek | Liczba Palet | Kierunek | Komentarz*)"
                            }
                            name="magazynHandlingMultipleEntries"
                            value={
                              formData.magazynHandlingMultipleEntries || ""
                            }
                            onChange={handleChange}
                            multiline
                            rows={10}
                            fullWidth
                            className={classes.marginBottom}
                            InputLabelProps={{
                              shrink: true,
                              style: {
                                fontSize: "14px",
                                lineHeight: "1.2",
                                whiteSpace: "normal",
                                width: "100%",
                              },
                            }}
                            InputProps={{
                              style: {
                                paddingTop:
                                  userRole === "admin" ? "40px" : "25px",
                              },
                            }}
                            placeholder={
                              userRole === "admin"
                                ? "Wklej dane z Excel z kolumnami oddzielonymi tabulatorami Przykład:12345678901234    Firma1    10    5    EKSPORT    Komentarz opcjonalny    2024-11-24    jan.kowalski&#10;98765432109876    Firma2    8     3    IMPORT    Komentarz    2024-11-25    anna.nowak"
                                : "Wklej dane z Excel z kolumnami oddzielonymi tabulatorami Przykład:12345678901234    Firma1    10    5    EKSPORT    Komentarz opcjonalny&#10;98765432109876    Firma2    8     3    IMPORT    Komentarz"
                            }
                            helperText={
                              userRole === "admin"
                                ? "Wklej dane skopiowane z Excel. Każdy wiersz powinien zawierać 5-8 kolumn oddzielonych tabulatorami: Numer Listu | Nazwa Firmy | Liczba Paczek | Liczba Palet | Kierunek | Komentarz (opcjonalny) | Data utworzenia (opcjonalna, YYYY-MM-DD) | Username (opcjonalny, zapisany jako createdBy)."
                                : "Wklej dane skopiowane z Excel. Każdy wiersz powinien zawierać 5-6 kolumn oddzielonych tabulatorami: Numer Listu | Nazwa Firmy | Liczba Paczek | Liczba Palet | Kierunek | Komentarz (opcjonalny)."
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            type={isTemplateBuilderMode ? "button" : "submit"}
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={
                              isTemplateBuilderMode
                                ? templateSaveInProgress ||
                                  typeof onTemplateSave !== "function"
                                : isSubmitting
                            }
                            onClick={
                              isTemplateBuilderMode
                                ? () => onTemplateSave && onTemplateSave()
                                : undefined
                            }
                          >
                            {isTemplateBuilderMode
                              ? templateSaveInProgress
                                ? "Zapisywanie szablonu..."
                                : "Zapisz szablon"
                              : isSubmitting
                                ? "Zapisywanie..."
                                : "Zapisz"}
                          </Button>
                        </Grid>
                      </>
                    )}
                  </>
                ) : formData.rodzajTransportu ===
                  "Magazyn Warszawa Zwroty i Błędy" ? (
                  <>
                    {/* Wybór typu wprowadzania danych */}
                    <Grid item xs={12}>
                      <FormControl
                        component="fieldset"
                        fullWidth
                        className={classes.marginBottom}
                      >
                        <FormLabel component="legend">
                          Wybierz sposób wprowadzania danych*
                        </FormLabel>
                        <RadioGroup
                          name="magazynZwrotyEntryType"
                          value={magazynZwrotyEntryType}
                          onChange={(e) =>
                            setMagazynZwrotyEntryType(e.target.value)
                          }
                        >
                          <FormControlLabel
                            value="single"
                            control={<Radio color="primary" />}
                            label="Wprowadź jeden wpis"
                          />
                          <FormControlLabel
                            value="multiple"
                            control={<Radio color="primary" />}
                            label="Wprowadź wiele wpisów"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    {/* Formularz dla pojedynczego wpisu */}
                    {magazynZwrotyEntryType === "single" && (
                      <>
                        <div
                          style={{ position: "relative" }}
                          className={classes.marginBottom}
                          data-magazyn-warszawa-dropdown
                        >
                          <TextField
                            required
                            label="Nadawca (Nazwa firmy)"
                            name="magazynWarszawaZwrotyIBledy.nadawca"
                            value={
                              formData.magazynWarszawaZwrotyIBledy?.nadawca ||
                              ""
                            }
                            onChange={handleChange}
                            autoComplete="off"
                            fullWidth
                            disabled={isFetching}
                            onClick={() =>
                              handleListOpen("magazynWarszawaZwrotyNadawca")
                            }
                            onFocus={() =>
                              handleListOpen("magazynWarszawaZwrotyNadawca")
                            }
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <span style={{ cursor: "pointer" }}>▼</span>
                              ),
                            }}
                          />
                          {listOpen &&
                            listType === "magazynWarszawaZwrotyNadawca" && (
                              <div
                                style={{
                                  position: "absolute",
                                  zIndex: 1300,
                                  width: "120%",
                                  maxHeight: 450,
                                  backgroundColor: "white",
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                  marginTop: "4px",
                                  left: "-10%",
                                }}
                              >
                                <div
                                  style={{
                                    padding: "8px",
                                    backgroundColor: "#f5f5f5",
                                    borderBottom: "1px solid #eee",
                                  }}
                                >
                                  <TextField
                                    placeholder="Szukaj firmy..."
                                    value={magazynWarszawaSearchText}
                                    onChange={(e) =>
                                      filterMagazynWarszawaCompanies(
                                        e.target.value,
                                      )
                                    }
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    autoFocus
                                    InputProps={{
                                      style: {
                                        fontSize: "14px",
                                        backgroundColor: "white",
                                      },
                                    }}
                                  />
                                </div>
                                <div
                                  style={{ maxHeight: 350, overflowY: "auto" }}
                                >
                                  {magazynWarszawaFilteredList.map(
                                    (company, index) => (
                                      <div
                                        key={index}
                                        style={{
                                          padding: "8px 16px",
                                          cursor: "pointer",
                                          borderBottom: "1px solid #f0f0f0",
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.backgroundColor =
                                            "#f0f0f0";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor =
                                            "transparent";
                                        }}
                                        onClick={() => {
                                          setFormData((prevData) => ({
                                            ...prevData,
                                            magazynWarszawaZwrotyIBledy: {
                                              ...prevData.magazynWarszawaZwrotyIBledy,
                                              nadawca: company,
                                            },
                                          }));
                                          setMagazynWarszawaSearchText("");
                                          setListOpen(false);
                                        }}
                                      >
                                        {company}
                                      </div>
                                    ),
                                  )}
                                  {magazynWarszawaFilteredList.length === 0 && (
                                    <div
                                      style={{
                                        padding: "16px",
                                        textAlign: "center",
                                        color: "#999",
                                      }}
                                    >
                                      Brak wyników
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                        <TextField
                          required
                          label="Numer listu przewozowego"
                          name="magazynWarszawaZwrotyIBledy.numerListu"
                          value={
                            formData.magazynWarszawaZwrotyIBledy?.numerListu ||
                            ""
                          }
                          onChange={handleChange}
                          fullWidth
                          className={classes.marginBottom}
                          inputProps={{
                            maxLength: 14,
                            minLength: 14,
                          }}
                          error={
                            formData.magazynWarszawaZwrotyIBledy?.numerListu &&
                            formData.magazynWarszawaZwrotyIBledy.numerListu
                              .length !== 14
                          }
                          helperText={
                            formData.magazynWarszawaZwrotyIBledy?.numerListu &&
                            formData.magazynWarszawaZwrotyIBledy.numerListu
                              .length !== 14
                              ? "Numer listu przewozowego musi mieć dokładnie 14 znaków"
                              : ""
                          }
                        />
                        <TextField
                          required
                          label="Liczba Paczek"
                          name="magazynWarszawaZwrotyIBledy.liczbaPaczek"
                          value={
                            formData.magazynWarszawaZwrotyIBledy
                              ?.liczbaPaczek || ""
                          }
                          onChange={handleChange}
                          type="number"
                          inputProps={{ min: 0, step: 1 }}
                          fullWidth
                          className={classes.marginBottom}
                        />
                        <FormControl
                          required
                          fullWidth
                          className={classes.marginBottom}
                        >
                          <InputLabel>Błąd dokumentacji</InputLabel>
                          <Select
                            name="magazynWarszawaZwrotyIBledy.bladDokumentacji"
                            value={
                              formData.magazynWarszawaZwrotyIBledy
                                ?.bladDokumentacji || ""
                            }
                            onChange={handleChange}
                          >
                            <MenuItem value="Tak">Tak</MenuItem>
                            <MenuItem value="Nie">Nie</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl
                          required
                          fullWidth
                          className={classes.marginBottom}
                        >
                          <InputLabel>Rodzaj błędu</InputLabel>
                          <Select
                            name="magazynWarszawaZwrotyIBledy.rodzajBledu"
                            value={
                              formData.magazynWarszawaZwrotyIBledy
                                ?.rodzajBledu || ""
                            }
                            onChange={handleChange}
                          >
                            <MenuItem value="Brak dokumentów">
                              Brak dokumentów
                            </MenuItem>
                            <MenuItem value="Upoważnienie">
                              Upoważnienie
                            </MenuItem>
                            <MenuItem value="Faktura">Faktura</MenuItem>
                            <MenuItem value="Inne">Inne</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl
                          required
                          fullWidth
                          className={classes.marginBottom}
                        >
                          <InputLabel>Zwrot</InputLabel>
                          <Select
                            name="magazynWarszawaZwrotyIBledy.zwrot"
                            value={
                              formData.magazynWarszawaZwrotyIBledy?.zwrot || ""
                            }
                            onChange={handleChange}
                          >
                            <MenuItem value="Tak">Tak</MenuItem>
                            <MenuItem value="Nie">Nie</MenuItem>
                          </Select>
                        </FormControl>
                        {formData.magazynWarszawaZwrotyIBledy?.zwrot ===
                          "Tak" && (
                          <FormControl
                            required
                            fullWidth
                            className={classes.marginBottom}
                          >
                            <InputLabel>Powód zwrotu</InputLabel>
                            <Select
                              name="magazynWarszawaZwrotyIBledy.powod"
                              value={
                                formData.magazynWarszawaZwrotyIBledy?.powod ||
                                ""
                              }
                              onChange={handleChange}
                            >
                              <MenuItem value="Dyspozycja">Dyspozycja</MenuItem>
                              <MenuItem value="Gabaryt">Gabaryt</MenuItem>
                              <MenuItem value="Inny">Inny</MenuItem>
                              <MenuItem value="Po upływie 10 dni magazynowania">
                                Po upływie 10 dni magazynowania
                              </MenuItem>
                            </Select>
                          </FormControl>
                        )}
                        <FormControl
                          required
                          fullWidth
                          className={classes.marginBottom}
                        >
                          <InputLabel>Wniosek</InputLabel>
                          <Select
                            name="magazynWarszawaZwrotyIBledy.wniosek"
                            value={
                              formData.magazynWarszawaZwrotyIBledy?.wniosek ||
                              ""
                            }
                            onChange={handleChange}
                          >
                            <MenuItem value="Tak">Tak</MenuItem>
                            <MenuItem value="Nie">Nie</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          label="Komentarz (opcjonalny)"
                          name="magazynWarszawaZwrotyIBledy.komentarz"
                          value={
                            formData.magazynWarszawaZwrotyIBledy?.komentarz ||
                            ""
                          }
                          onChange={handleChange}
                          multiline
                          rows={3}
                          fullWidth
                          className={classes.marginBottom}
                          placeholder="Wprowadź komentarz jeśli potrzebny..."
                        />
                        <Grid item xs={12}>
                          <Button
                            type={isTemplateBuilderMode ? "button" : "submit"}
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={
                              isTemplateBuilderMode
                                ? templateSaveInProgress ||
                                  typeof onTemplateSave !== "function"
                                : isSubmitting
                            }
                            onClick={
                              isTemplateBuilderMode
                                ? () => onTemplateSave && onTemplateSave()
                                : undefined
                            }
                          >
                            {isTemplateBuilderMode
                              ? templateSaveInProgress
                                ? "Zapisywanie szablonu..."
                                : "Zapisz szablon"
                              : isSubmitting
                                ? "Zapisywanie..."
                                : "Zapisz"}
                          </Button>
                        </Grid>
                      </>
                    )}

                    {magazynZwrotyEntryType === "multiple" && (
                      // Pole tekstowe dla wielu wpisów
                      <>
                        <Grid
                          item
                          xs={12}
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <TextField
                            required
                            label={
                              userRole === "admin"
                                ? "Dane z Excel (Nadawca | Nr listu | Liczba paczek | Błąd dokumentacji | Rodzaj błędu | Zwrot | Powód | Wniosek | Komentarz* | Data utworzenia* | Username*)"
                                : "Dane z Excel (Nadawca | Nr listu | Liczba paczek | Błąd dokumentacji | Rodzaj błędu | Zwrot | Powód | Wniosek | Komentarz*)"
                            }
                            name="magazynZwrotyMultipleEntries"
                            value={formData.magazynZwrotyMultipleEntries || ""}
                            onChange={handleChange}
                            multiline
                            rows={15}
                            className={classes.marginBottom}
                            InputLabelProps={{
                              shrink: true,
                              style: {
                                fontSize: "14px",
                                lineHeight: "1.2",
                                whiteSpace: "normal",
                                width: "100%",
                              },
                            }}
                            InputProps={{
                              style: {
                                paddingTop:
                                  userRole === "admin" ? "40px" : "25px",
                              },
                            }}
                            placeholder={
                              userRole === "admin"
                                ? "Wklej dane z Excel z kolumnami oddzielonymi tabulatorami Przykład: Firma ABC    12345678901234    15    Tak    Brak dokumentów    Nie        Tak    Komentarz    2024-11-24    jan.kowalski&#10;Firma XYZ    98765432109876    8     Nie    Inne           Tak    Dyspozycja    Nie    Inne    2024-11-25    anna.nowak"
                                : "Wklej dane z Excel z kolumnami oddzielonymi tabulatorami Przykład: Firma ABC    12345678901234    15    Tak    Brak dokumentów    Nie        Tak    Komentarz opcjonalny&#10;Firma XYZ    98765432109876    8     Nie    Inne           Tak    Dyspozycja    Nie    Inne"
                            }
                            helperText={
                              userRole === "admin"
                                ? "Wklej dane skopiowane z Excel. Każdy wiersz powinien zawierać 8-11 kolumn oddzielonych tabulatorami: Nadawca | Nr listu | Liczba paczek | Błąd dokumentacji | Rodzaj błędu | Zwrot | Powód | Wniosek | Komentarz (opcjonalny) | Data utworzenia (opcjonalna, YYYY-MM-DD) | Username (opcjonalny, zapisany jako createdBy)."
                                : "Wklej dane skopiowane z Excel. Każdy wiersz powinien zawierać 8-9 kolumn oddzielonych tabulatorami: Nadawca | Nr listu | Liczba paczek | Błąd dokumentacji | Rodzaj błędu | Zwrot | Powód | Wniosek | Komentarz (opcjonalny)."
                            }
                            style={{
                              width: "150%",
                              maxWidth: "none",
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            type={isTemplateBuilderMode ? "button" : "submit"}
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={
                              isTemplateBuilderMode
                                ? templateSaveInProgress ||
                                  typeof onTemplateSave !== "function"
                                : isSubmitting
                            }
                            onClick={
                              isTemplateBuilderMode
                                ? () => onTemplateSave && onTemplateSave()
                                : undefined
                            }
                          >
                            {isTemplateBuilderMode
                              ? templateSaveInProgress
                                ? "Zapisywanie szablonu..."
                                : "Zapisz szablon"
                              : isSubmitting
                                ? "Zapisywanie..."
                                : "Zapisz wiele wpisów"}
                          </Button>
                        </Grid>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {isTemplateBuilderMode && <TemplateInvoiceBanner />}
                    {formData.rodzaj === "Import" && (
                      <>
                        <div
                          ref={importerDropdownRef}
                          style={{ position: "relative" }}
                          className={`${classes.marginBottom} ${
                            isFetching ? classes.fadeOut : classes.fadeIn
                          }`}
                        >
                          <TextField
                            required
                            label="Importer"
                            name="importer"
                            value={formData.importer}
                            onChange={handleChange}
                            autoComplete="off"
                            fullWidth
                            disabled={isFetching}
                            onClick={() => handleListOpen("importer")}
                            onFocus={() => handleListOpen("importer")}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <span style={{ cursor: "pointer" }}>▼</span>
                              ),
                            }}
                          />
                          {listOpen && listType === "importer" && (
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 1300,
                                width: "120%",
                                maxHeight: 450,
                                backgroundColor: "white",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                marginTop: "4px",
                                left: "-10%",
                              }}
                            >
                              <div
                                style={{
                                  padding: "8px",
                                  backgroundColor: "#f5f5f5",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                <TextField
                                  placeholder="Szukaj importera..."
                                  value={importerSearchText}
                                  onChange={(e) =>
                                    filterImporters(e.target.value)
                                  }
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  autoFocus
                                  InputProps={{
                                    style: {
                                      fontSize: "14px",
                                      backgroundColor: "white",
                                    },
                                  }}
                                />
                              </div>
                              <div
                                style={{ maxHeight: 350, overflowY: "auto" }}
                              >
                                {importerFilteredList.map((importer, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      padding: "8px 16px",
                                      cursor: "pointer",
                                      borderBottom: "1px solid #f0f0f0",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.backgroundColor =
                                        "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.target.style.backgroundColor =
                                        "transparent")
                                    }
                                    onClick={() => {
                                      setFormData((prevData) => ({
                                        ...prevData,
                                        importer: importer,
                                      }));
                                      setImporterSearchText("");
                                      setListOpen(false);
                                    }}
                                  >
                                    {importer}
                                  </div>
                                ))}
                                {importerFilteredList.length === 0 && (
                                  <div
                                    style={{
                                      padding: "16px",
                                      textAlign: "center",
                                      color: "#999",
                                    }}
                                  >
                                    Brak wyników
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {formData.fields.map((field, index) => (
                          <div key={index}>
                            {/* Eksporter Fields */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "16px",
                                justifyContent: "space-between",
                              }}
                            >
                              {isManBusSelected ? (
                                <div
                                  style={{
                                    position: "relative",
                                    width: "100%",
                                    marginRight: "8px",
                                  }}
                                  data-man-exporter-dropdown={index}
                                >
                                  <TextField
                                    required
                                    label={`Eksporter ${index + 1}`}
                                    name={`eksporter${index}`}
                                    value={field.eksporter}
                                    onChange={handleChange}
                                    autoComplete="off"
                                    fullWidth
                                    disabled={isFetching}
                                    onClick={() =>
                                      openManExporterDropdown(index)
                                    }
                                    onFocus={() =>
                                      openManExporterDropdown(index)
                                    }
                                    InputProps={{
                                      readOnly: true,
                                      endAdornment: (
                                        <span style={{ cursor: "pointer" }}>
                                          ▼
                                        </span>
                                      ),
                                    }}
                                  />
                                  {isManExporterDropdownOpen(index) && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        zIndex: 1300,
                                        width: "120%",
                                        maxHeight: 450,
                                        backgroundColor: "white",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                        marginTop: "4px",
                                        left: "-10%",
                                      }}
                                    >
                                      <div
                                        style={{
                                          padding: "8px",
                                          backgroundColor: "#f5f5f5",
                                          borderBottom: "1px solid #eee",
                                        }}
                                      >
                                        <TextField
                                          placeholder="Szukaj eksportera MAN..."
                                          value={getManExporterSearchText(
                                            index,
                                          )}
                                          onChange={(e) =>
                                            updateManExporterSearchText(
                                              index,
                                              e.target.value,
                                            )
                                          }
                                          fullWidth
                                          size="small"
                                          variant="outlined"
                                          autoFocus
                                          InputProps={{
                                            style: {
                                              fontSize: "14px",
                                              backgroundColor: "white",
                                            },
                                          }}
                                        />
                                      </div>
                                      <div
                                        style={{
                                          maxHeight: 350,
                                          overflowY: "auto",
                                        }}
                                      >
                                        {getFilteredManExporters(index).map(
                                          (exporter, exporterIndex) => (
                                            <div
                                              key={exporterIndex}
                                              style={{
                                                padding: "8px 16px",
                                                cursor: "pointer",
                                                borderBottom:
                                                  "1px solid #f0f0f0",
                                              }}
                                              onMouseEnter={(e) => {
                                                e.target.style.backgroundColor =
                                                  "#f0f0f0";
                                              }}
                                              onMouseLeave={(e) => {
                                                e.target.style.backgroundColor =
                                                  "transparent";
                                              }}
                                              onClick={() => {
                                                handleFieldsChange(
                                                  "eksporter",
                                                  exporter,
                                                  index,
                                                );
                                                updateManExporterSearchText(
                                                  index,
                                                  "",
                                                );
                                                closeManExporterDropdown(index);
                                              }}
                                            >
                                              {exporter}
                                            </div>
                                          ),
                                        )}
                                        {getFilteredManExporters(index)
                                          .length === 0 && (
                                          <div
                                            style={{
                                              padding: "16px",
                                              textAlign: "center",
                                              color: "#999",
                                            }}
                                          >
                                            Brak wyników
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <TextField
                                  label={`Eksporter ${index + 1}`}
                                  name={`eksporter${index}`}
                                  value={field.eksporter}
                                  autoComplete="off"
                                  onChange={(e) =>
                                    handleFieldsChange(
                                      "eksporter",
                                      e.target.value,
                                      index,
                                    )
                                  }
                                  fullWidth
                                  style={{ marginRight: "8px" }}
                                />
                              )}
                            </div>

                            {/* Nr. Tranzytu Fields */}
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                marginBottom: "16px",
                              }}
                            >
                              <TextField
                                label={`Nr. Tranzytu ${index + 1}`}
                                name={`nrTranzytu${index}`}
                                value={field.nrTranzytu}
                                autoComplete="off"
                                onChange={(e) =>
                                  handleFieldsChange(
                                    "nrTranzytu",
                                    e.target.value,
                                    index,
                                  )
                                }
                                fullWidth
                                style={{ marginBottom: "8px" }}
                              />

                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  variant="subtitle1" // zamiast nieistniejącego h8
                                  className={classes.formTitle}
                                  style={{ marginRight: "16px" }} // lub np. 24px
                                >
                                  Data wygaśnięcia T1
                                </Typography>
                                <DatePickerField
                                  label={`Data wygaśnięcia dokumentów ${
                                    index + 1
                                  }`}
                                  name={`expirationDate${index}`}
                                  value={field.expirationDate}
                                  autoComplete="off"
                                  onChange={(date) =>
                                    handleFieldsChange(
                                      "expirationDate",
                                      date,
                                      index,
                                    )
                                  }
                                  fullWidth
                                  format="dd-MM-yyyy"
                                  style={{ flex: 1 }}
                                  error={!!expirationDateErrors[index]}
                                  helperText={expirationDateErrors[index]}
                                />
                              </div>
                              <TextField
                                type="text"
                                label="Podaj ilość faktur"
                                value={
                                  field.invoiceNumbers.length === 0
                                    ? ""
                                    : String(field.invoiceNumbers.length)
                                }
                                onChange={(e) => {
                                  let input = e.target.value;

                                  // Pozwól na puste pole
                                  if (input === "") {
                                    handleInvoiceCountChange(index, 0); // lub może: handleInvoiceCountChange(index, null);
                                    return;
                                  }

                                  // Usuń wiodące zera
                                  input = input.replace(/^0+/, "");

                                  // Jeśli pozostało coś po usunięciu zer i to jest liczba — ustaw
                                  const count = parseInt(input, 10);
                                  if (!isNaN(count)) {
                                    handleInvoiceCountChange(index, count);
                                  }
                                }}
                                inputProps={{
                                  inputMode: "numeric",
                                  pattern: "[0-9]*",
                                }}
                                fullWidth
                                style={{ marginBottom: "16px" }}
                              />

                              {field.invoiceNumbers.map(
                                (invoice, invoiceIdx) => (
                                  <div
                                    key={invoiceIdx}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <TextField
                                      required
                                      label={`Nr. Faktury ${invoiceIdx + 1}`}
                                      name={`nrFaktury${index}_${invoiceIdx}`}
                                      value={invoice}
                                      autoComplete="off"
                                      onChange={(e) => {
                                        const updatedFields = [
                                          ...formData.fields,
                                        ];
                                        updatedFields[index].invoiceNumbers[
                                          invoiceIdx
                                        ] = e.target.value;
                                        setFormData({
                                          ...formData,
                                          fields: updatedFields,
                                        });
                                      }}
                                      fullWidth
                                    />
                                    <InvoiceInfoTooltip />
                                  </div>
                                ),
                              )}

                              {/* Status Realizacji Field */}
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "16px",
                                  justifyContent: "space-between",
                                }}
                              >
                                <FormControl fullWidth>
                                  <InputLabel id={`select-list-label-${index}`}>
                                    Status realizacji {index + 1}*
                                  </InputLabel>
                                  <Select
                                    required
                                    labelId={`select-list-label-${index}`}
                                    id={`select-list-${index}`}
                                    value={statusRealizacjiFields[index]} // Use statusRealizacjiFields[index] for value
                                    onChange={(e) =>
                                      handleStatusRealizacjiChange(
                                        e.target.value,
                                        index,
                                      )
                                    }
                                    name={`statusRealizacji${index}`}
                                    className={classes.marginBottom}
                                  >
                                    <MenuItem value="Do przygotowania">
                                      Do przygotowania
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              </div>
                            </div>
                            {/* Nr. Faktury Field */}
                            {index > 0 && (
                              <Button
                                variant="contained"
                                color="secondary"
                                className={classes.marginBottom}
                                onClick={() => removeFields(index)}
                              >
                                -
                              </Button>
                            )}
                          </div>
                        ))}

                        {/* Common Add Fields button */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={addFields}
                          className={classes.marginBottom}
                        >
                          +
                        </Button>
                      </>
                    )}
                    {formData.rodzaj === "Eksport" && (
                      <>
                        <div
                          ref={exporterDropdownRef}
                          style={{ position: "relative" }}
                          className={`${classes.marginBottom} ${
                            isFetching ? classes.fadeOut : classes.fadeIn
                          }`}
                        >
                          <TextField
                            required
                            label="Eksporter"
                            name="eksporter"
                            value={formData.eksporter}
                            onChange={handleChange}
                            autoComplete="off"
                            fullWidth
                            disabled={isFetching}
                            onClick={() => handleListOpen("eksporter")}
                            onFocus={() => handleListOpen("eksporter")}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <span style={{ cursor: "pointer" }}>▼</span>
                              ),
                            }}
                          />
                          {listOpen && listType === "eksporter" && (
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 1300,
                                width: "120%",
                                maxHeight: 450,
                                backgroundColor: "white",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                marginTop: "4px",
                                left: "-10%",
                              }}
                            >
                              <div
                                style={{
                                  padding: "8px",
                                  backgroundColor: "#f5f5f5",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                <TextField
                                  placeholder="Szukaj eksportera..."
                                  value={exporterSearchText}
                                  onChange={(e) =>
                                    filterExporters(e.target.value)
                                  }
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  autoFocus
                                  InputProps={{
                                    style: {
                                      fontSize: "14px",
                                      backgroundColor: "white",
                                    },
                                  }}
                                />
                              </div>
                              <div
                                style={{ maxHeight: 350, overflowY: "auto" }}
                              >
                                {exporterFilteredList.map((exporter, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      padding: "8px 16px",
                                      cursor: "pointer",
                                      borderBottom: "1px solid #f0f0f0",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.backgroundColor =
                                        "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.target.style.backgroundColor =
                                        "transparent")
                                    }
                                    onClick={() => {
                                      setFormData((prevData) => ({
                                        ...prevData,
                                        eksporter: exporter,
                                      }));
                                      setExporterSearchText("");
                                      setListOpen(false);
                                    }}
                                  >
                                    {exporter}
                                  </div>
                                ))}
                                {exporterFilteredList.length === 0 && (
                                  <div
                                    style={{
                                      padding: "16px",
                                      textAlign: "center",
                                      color: "#999",
                                    }}
                                  >
                                    Brak wyników
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        {formData.fields.map((field, index) => (
                          <div key={index}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                marginBottom: "16px",
                              }}
                            >
                              <TextField
                                label={`Importer ${index + 1}`}
                                name={`importer${index}`}
                                value={field.importer}
                                autoComplete="off"
                                onChange={(e) =>
                                  handleFieldsChange(
                                    "importer",
                                    e.target.value,
                                    index,
                                  )
                                }
                                fullWidth
                                style={{ marginRight: "8px" }}
                              />
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                marginBottom: "16px",
                              }}
                            >
                              <TextField
                                type="text"
                                label="Podaj ilość faktur"
                                value={
                                  field.invoiceNumbers.length === 0
                                    ? ""
                                    : String(field.invoiceNumbers.length)
                                }
                                onChange={(e) => {
                                  let input = e.target.value;

                                  // Pozwól na puste pole
                                  if (input === "") {
                                    handleInvoiceCountChange(index, 0); // lub może: handleInvoiceCountChange(index, null);
                                    return;
                                  }

                                  // Usuń wiodące zera
                                  input = input.replace(/^0+/, "");

                                  // Jeśli pozostało coś po usunięciu zer i to jest liczba — ustaw
                                  const count = parseInt(input, 10);
                                  if (!isNaN(count)) {
                                    handleInvoiceCountChange(index, count);
                                  }
                                }}
                                inputProps={{
                                  inputMode: "numeric",
                                  pattern: "[0-9]*",
                                }}
                                fullWidth
                                style={{ marginBottom: "16px" }}
                              />
                              {field.invoiceNumbers.map(
                                (invoice, invoiceIdx) => (
                                  <div
                                    key={invoiceIdx}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <TextField
                                      required
                                      label={`Nr. Faktury ${invoiceIdx + 1}`}
                                      name={`nrFaktury${index}_${invoiceIdx}`}
                                      value={invoice}
                                      autoComplete="off"
                                      onChange={(e) => {
                                        const updatedFields = [
                                          ...formData.fields,
                                        ];
                                        updatedFields[index].invoiceNumbers[
                                          invoiceIdx
                                        ] = e.target.value;
                                        setFormData({
                                          ...formData,
                                          fields: updatedFields,
                                        });
                                      }}
                                      fullWidth
                                    />
                                    <InvoiceInfoTooltip />
                                  </div>
                                ),
                              )}

                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "16px",
                                  justifyContent: "space-between",
                                }}
                              >
                                <FormControl fullWidth>
                                  <InputLabel id={`select-list-label-${index}`}>
                                    Status realizacji {index + 1}*
                                  </InputLabel>
                                  <Select
                                    required
                                    labelId={`select-list-label-${index}`}
                                    id={`select-list-${index}`}
                                    value={statusRealizacjiFields[index]} // Use statusRealizacjiFields[index] for value
                                    onChange={(e) =>
                                      handleStatusRealizacjiChange(
                                        e.target.value,
                                        index,
                                      )
                                    }
                                    name={`statusRealizacji${index}`}
                                    className={classes.marginBottom}
                                  >
                                    <MenuItem value="Do przygotowania">
                                      Do przygotowania
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              </div>
                            </div>
                            {index > 0 && (
                              <Button
                                variant="contained"
                                color="secondary"
                                className={classes.marginBottom}
                                onClick={() => removeFields(index)}
                              >
                                -
                              </Button>
                            )}
                          </div>
                        ))}

                        <Button
                          variant="contained"
                          color="primary"
                          onClick={addFields}
                          className={classes.marginBottom}
                        >
                          +
                        </Button>
                      </>
                    )}
                    {formData.rodzaj === "Tranzyt" && (
                      <>
                        {/* Jeden importer globalnie */}
                        <TextField
                          label="Importer"
                          name="importer"
                          value={formData.importer}
                          onChange={handleChange}
                          fullWidth
                          autoComplete="off"
                          className={`${classes.marginBottom} ${
                            isFetching ? classes.fadeOut : classes.fadeIn
                          }`}
                          disabled={isFetching}
                        />

                        {/* Mapowana reszta pól */}
                        {formData.fields.map((field, index) => (
                          <div
                            key={index}
                            style={{
                              borderBottom: "1px solid #ccc",
                              paddingBottom: 16,
                              marginBottom: 24,
                            }}
                          >
                            {/* Eksporter */}
                            <div
                              ref={exporterDropdownRef}
                              style={{ position: "relative" }}
                              className={`${classes.marginBottom} ${
                                isFetching ? classes.fadeOut : classes.fadeIn
                              }`}
                            >
                              <TextField
                                required
                                label="Eksporter"
                                name="eksporter"
                                value={formData.eksporter}
                                onChange={handleChange}
                                autoComplete="off"
                                fullWidth
                                disabled={isFetching}
                                onClick={() => handleListOpen("eksporter")}
                                onFocus={() => handleListOpen("eksporter")}
                                InputProps={{
                                  readOnly: true,
                                  endAdornment: (
                                    <span style={{ cursor: "pointer" }}>▼</span>
                                  ),
                                }}
                              />
                              {listOpen && listType === "eksporter" && (
                                <div
                                  style={{
                                    position: "absolute",
                                    zIndex: 1300,
                                    width: "120%",
                                    maxHeight: 450,
                                    backgroundColor: "white",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                    marginTop: "4px",
                                    left: "-10%",
                                  }}
                                >
                                  <div
                                    style={{
                                      padding: "8px",
                                      backgroundColor: "#f5f5f5",
                                      borderBottom: "1px solid #eee",
                                    }}
                                  >
                                    <TextField
                                      placeholder="Szukaj eksportera..."
                                      value={exporterSearchText}
                                      onChange={(e) =>
                                        filterExporters(e.target.value)
                                      }
                                      fullWidth
                                      size="small"
                                      variant="outlined"
                                      autoFocus
                                      InputProps={{
                                        style: {
                                          fontSize: "14px",
                                          backgroundColor: "white",
                                        },
                                      }}
                                    />
                                  </div>
                                  <div
                                    style={{
                                      maxHeight: 350,
                                      overflowY: "auto",
                                    }}
                                  >
                                    {exporterFilteredList.map(
                                      (exporter, index) => (
                                        <div
                                          key={index}
                                          style={{
                                            padding: "8px 16px",
                                            cursor: "pointer",
                                            borderBottom: "1px solid #f0f0f0",
                                          }}
                                          onMouseEnter={(e) =>
                                            (e.target.style.backgroundColor =
                                              "#f5f5f5")
                                          }
                                          onMouseLeave={(e) =>
                                            (e.target.style.backgroundColor =
                                              "transparent")
                                          }
                                          onClick={() => {
                                            setFormData((prevData) => ({
                                              ...prevData,
                                              eksporter: exporter,
                                            }));
                                            setExporterSearchText("");
                                            setListOpen(false);
                                          }}
                                        >
                                          {exporter}
                                        </div>
                                      ),
                                    )}
                                    {exporterFilteredList.length === 0 && (
                                      <div
                                        style={{
                                          padding: "16px",
                                          textAlign: "center",
                                          color: "#999",
                                        }}
                                      >
                                        Brak wyników
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Nr faktury */}
                            <TextField
                              type="text"
                              label="Podaj ilość faktur"
                              value={
                                field.invoiceNumbers.length === 0
                                  ? ""
                                  : String(field.invoiceNumbers.length)
                              }
                              onChange={(e) => {
                                let input = e.target.value;

                                // Pozwól na puste pole
                                if (input === "") {
                                  handleInvoiceCountChange(index, 0); // lub może: handleInvoiceCountChange(index, null);
                                  return;
                                }

                                // Usuń wiodące zera
                                input = input.replace(/^0+/, "");

                                // Jeśli pozostało coś po usunięciu zer i to jest liczba — ustaw
                                const count = parseInt(input, 10);
                                if (!isNaN(count)) {
                                  handleInvoiceCountChange(index, count);
                                }
                              }}
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                              }}
                              fullWidth
                              style={{ marginBottom: "16px" }}
                            />
                            {field.invoiceNumbers.map((invoice, invoiceIdx) => (
                              <div
                                key={invoiceIdx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "8px",
                                }}
                              >
                                <TextField
                                  required
                                  label={`Nr. Faktury ${invoiceIdx + 1}`}
                                  name={`nrFaktury${index}_${invoiceIdx}`}
                                  value={invoice}
                                  autoComplete="off"
                                  onChange={(e) => {
                                    const updatedFields = [...formData.fields];
                                    updatedFields[index].invoiceNumbers[
                                      invoiceIdx
                                    ] = e.target.value;
                                    setFormData({
                                      ...formData,
                                      fields: updatedFields,
                                    });
                                  }}
                                  fullWidth
                                />
                                <InvoiceInfoTooltip />
                              </div>
                            ))}

                            {/* Waga Netto / Brutto */}
                            <TextField
                              label="Waga Netto"
                              value={field.wagaNetto}
                              onChange={(e) =>
                                handleFieldsChange(
                                  "wagaNetto",
                                  e.target.value,
                                  index,
                                )
                              }
                              fullWidth
                              className={classes.marginBottom}
                            />

                            <TextField
                              label="Waga Brutto"
                              value={field.wagaBrutto}
                              onChange={(e) =>
                                handleFieldsChange(
                                  "wagaBrutto",
                                  e.target.value,
                                  index,
                                )
                              }
                              fullWidth
                              className={classes.marginBottom}
                            />

                            {/* Waluta */}
                            <FormControl
                              component="fieldset"
                              fullWidth
                              className={classes.marginBottom}
                            >
                              <FormLabel component="legend">Waluta*</FormLabel>
                              <RadioGroup
                                value={field.waluta}
                                onChange={(e) =>
                                  handleFieldsChange(
                                    "waluta",
                                    e.target.value,
                                    index,
                                  )
                                }
                              >
                                {["EUR", "USD", "ZŁ", "GBP", "WIELE"].map(
                                  (val) => (
                                    <FormControlLabel
                                      key={val}
                                      value={val}
                                      control={<Radio />}
                                      label={val}
                                    />
                                  ),
                                )}
                              </RadioGroup>
                            </FormControl>

                            {/* Gwarant */}
                            <FormControl
                              component="fieldset"
                              fullWidth
                              className={classes.marginBottom}
                            >
                              <FormLabel component="legend">Gwarant*</FormLabel>
                              <RadioGroup
                                value={field.gwarant}
                                onChange={(e) =>
                                  handleFieldsChange(
                                    "gwarant",
                                    e.target.value,
                                    index,
                                  )
                                }
                              >
                                <FormControlLabel
                                  value="SGS"
                                  control={<Radio />}
                                  label="SGS"
                                />
                                <FormControlLabel
                                  value="Oregon"
                                  control={<Radio />}
                                  label="Oregon"
                                />
                              </RadioGroup>
                            </FormControl>

                            {/* Urząd tranzytowy */}
                            <TextField
                              label="Przewidywany urząd tranzytowy"
                              name={`urzad_tranzytowy_${index}`}
                              value={field.urzad_tranzytowy}
                              onChange={(e) =>
                                handleFieldsChange(
                                  "urzad_tranzytowy",
                                  e.target.value,
                                  index,
                                )
                              }
                              fullWidth
                              select
                              className={classes.marginBottom}
                            >
                              {urzadTranzytowyList.map((urzad) => (
                                <MenuItem key={urzad.id} value={urzad.name}>
                                  {urzad.name}
                                </MenuItem>
                              ))}
                            </TextField>

                            {/* Urząd przeznaczenia */}
                            <TextField
                              label="Urząd celny przeznaczenia"
                              name={`urzad_przeznaczenia_${index}`}
                              value={field.urzad_przeznaczenia}
                              onChange={(e) =>
                                handleFieldsChange(
                                  "urzad_przeznaczenia",
                                  e.target.value,
                                  index,
                                )
                              }
                              fullWidth
                              select
                              className={classes.marginBottom}
                            >
                              {urzadPrzeznaczeniaList.map((urzad) => (
                                <MenuItem key={urzad.id} value={urzad.name}>
                                  {urzad.name}
                                </MenuItem>
                              ))}
                            </TextField>

                            {/* Status realizacji */}
                            <FormControl
                              fullWidth
                              className={classes.marginBottom}
                            >
                              <InputLabel>Status realizacji</InputLabel>
                              <Select
                                value={field.statusRealizacji}
                                onChange={(e) =>
                                  handleFieldsChange(
                                    "statusRealizacji",
                                    e.target.value,
                                    index,
                                  )
                                }
                              >
                                <MenuItem value="Do przygotowania">
                                  Do przygotowania
                                </MenuItem>
                              </Select>
                            </FormControl>

                            {/* Przycisk "-" */}
                            {index > 0 && (
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => removeFields(index)}
                              >
                                -
                              </Button>
                            )}
                          </div>
                        ))}

                        {/* Przycisk "+" */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={addFields}
                        >
                          +
                        </Button>
                      </>
                    )}

                    {formData.rodzaj === "Wyprowadzenie ze składu" && (
                      <>
                        {/* Globalny Eksporter */}
                        <div
                          ref={exporterDropdownRef}
                          style={{ position: "relative" }}
                          className={`${classes.marginBottom} ${
                            isFetching ? classes.fadeOut : classes.fadeIn
                          }`}
                        >
                          <TextField
                            required
                            label="Eksporter"
                            name="eksporter"
                            value={formData.eksporter}
                            onChange={handleChange}
                            autoComplete="off"
                            fullWidth
                            disabled={isFetching}
                            onClick={() => handleListOpen("eksporter")}
                            onFocus={() => handleListOpen("eksporter")}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <span style={{ cursor: "pointer" }}>▼</span>
                              ),
                            }}
                          />
                          {listOpen && listType === "eksporter" && (
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 1300,
                                width: "120%",
                                maxHeight: 450,
                                backgroundColor: "white",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                marginTop: "4px",
                                left: "-10%",
                              }}
                            >
                              <div
                                style={{
                                  padding: "8px",
                                  backgroundColor: "#f5f5f5",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                <TextField
                                  placeholder="Szukaj eksportera..."
                                  value={exporterSearchText}
                                  onChange={(e) =>
                                    filterExporters(e.target.value)
                                  }
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  autoFocus
                                  InputProps={{
                                    style: {
                                      fontSize: "14px",
                                      backgroundColor: "white",
                                    },
                                  }}
                                />
                              </div>
                              <div
                                style={{ maxHeight: 350, overflowY: "auto" }}
                              >
                                {exporterFilteredList.map((exporter, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      padding: "8px 16px",
                                      cursor: "pointer",
                                      borderBottom: "1px solid #f0f0f0",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.backgroundColor =
                                        "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.target.style.backgroundColor =
                                        "transparent")
                                    }
                                    onClick={() => {
                                      setFormData((prevData) => ({
                                        ...prevData,
                                        eksporter: exporter,
                                      }));
                                      setExporterSearchText("");
                                      setListOpen(false);
                                    }}
                                  >
                                    {exporter}
                                  </div>
                                ))}
                                {exporterFilteredList.length === 0 && (
                                  <div
                                    style={{
                                      padding: "16px",
                                      textAlign: "center",
                                      color: "#999",
                                    }}
                                  >
                                    Brak wyników
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Mapped pola Importer + Nr Faktury + Status */}
                        {formData.fields.map((field, index) => (
                          <div
                            key={index}
                            style={{
                              borderBottom: "1px solid #ccc",
                              paddingBottom: 16,
                              marginBottom: 24,
                            }}
                          >
                            {/* Importer */}
                            <TextField
                              label={`Importer ${index + 1}`}
                              name={`importer${index}`}
                              value={field.importer || ""}
                              autoComplete="off"
                              onChange={(e) =>
                                handleFieldsChange(
                                  "importer",
                                  e.target.value,
                                  index,
                                )
                              }
                              fullWidth
                              style={{ marginBottom: "16px" }}
                            />

                            {/* Nr Faktury */}
                            <TextField
                              type="text"
                              label="Podaj ilość faktur"
                              value={
                                field.invoiceNumbers.length === 0
                                  ? ""
                                  : String(field.invoiceNumbers.length)
                              }
                              onChange={(e) => {
                                let input = e.target.value;

                                // Pozwól na puste pole
                                if (input === "") {
                                  handleInvoiceCountChange(index, 0); // lub może: handleInvoiceCountChange(index, null);
                                  return;
                                }

                                // Usuń wiodące zera
                                input = input.replace(/^0+/, "");

                                // Jeśli pozostało coś po usunięciu zer i to jest liczba — ustaw
                                const count = parseInt(input, 10);
                                if (!isNaN(count)) {
                                  handleInvoiceCountChange(index, count);
                                }
                              }}
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                              }}
                              fullWidth
                              style={{ marginBottom: "16px" }}
                            />
                            {field.invoiceNumbers.map((invoice, invoiceIdx) => (
                              <div
                                key={invoiceIdx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "8px",
                                }}
                              >
                                <TextField
                                  required
                                  label={`Nr. Faktury ${invoiceIdx + 1}`}
                                  name={`nrFaktury${index}_${invoiceIdx}`}
                                  value={invoice}
                                  autoComplete="off"
                                  onChange={(e) => {
                                    const updatedFields = [...formData.fields];
                                    updatedFields[index].invoiceNumbers[
                                      invoiceIdx
                                    ] = e.target.value;
                                    setFormData({
                                      ...formData,
                                      fields: updatedFields,
                                    });
                                  }}
                                  fullWidth
                                />
                                <InvoiceInfoTooltip />
                              </div>
                            ))}

                            {/* Status realizacji */}
                            <FormControl
                              fullWidth
                              className={classes.marginBottom}
                            >
                              <InputLabel id={`select-list-label-${index}`}>
                                Status realizacji {index + 1}*
                              </InputLabel>
                              <Select
                                required
                                labelId={`select-list-label-${index}`}
                                id={`select-list-${index}`}
                                value={field.statusRealizacji || ""}
                                onChange={(e) =>
                                  handleFieldsChange(
                                    "statusRealizacji",
                                    e.target.value,
                                    index,
                                  )
                                }
                                name={`statusRealizacji${index}`}
                              >
                                <MenuItem value="Do przygotowania">
                                  Do przygotowania
                                </MenuItem>
                              </Select>
                            </FormControl>

                            {/* Przycisk "-" */}
                            {index > 0 && (
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => removeFields(index)}
                                className={classes.marginBottom}
                              >
                                -
                              </Button>
                            )}
                          </div>
                        ))}

                        {/* Przycisk "+" */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={addFields}
                          className={classes.marginBottom}
                        >
                          +
                        </Button>
                      </>
                    )}

                    {formData.rodzaj === "Wprowadzenie na skład" && (
                      <>
                        {/* Globalny Importer */}
                        <div
                          ref={importerDropdownRef}
                          style={{ position: "relative" }}
                          className={`${classes.marginBottom} ${
                            isFetching ? classes.fadeOut : classes.fadeIn
                          }`}
                        >
                          <TextField
                            required
                            label="Importer"
                            name="importer"
                            value={formData.importer}
                            onChange={handleChange}
                            autoComplete="off"
                            fullWidth
                            disabled={isFetching}
                            onClick={() => handleListOpen("importer")}
                            onFocus={() => handleListOpen("importer")}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <span style={{ cursor: "pointer" }}>▼</span>
                              ),
                            }}
                          />
                          {listOpen && listType === "importer" && (
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 1300,
                                width: "120%",
                                maxHeight: 450,
                                backgroundColor: "white",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                marginTop: "4px",
                                left: "-10%",
                              }}
                            >
                              <div
                                style={{
                                  padding: "8px",
                                  backgroundColor: "#f5f5f5",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                <TextField
                                  placeholder="Szukaj importera..."
                                  value={importerSearchText}
                                  onChange={(e) =>
                                    filterImporters(e.target.value)
                                  }
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  autoFocus
                                  InputProps={{
                                    style: {
                                      fontSize: "14px",
                                      backgroundColor: "white",
                                    },
                                  }}
                                />
                              </div>
                              <div
                                style={{ maxHeight: 350, overflowY: "auto" }}
                              >
                                {importerFilteredList.map((importer, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      padding: "8px 16px",
                                      cursor: "pointer",
                                      borderBottom: "1px solid #f0f0f0",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.backgroundColor =
                                        "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.target.style.backgroundColor =
                                        "transparent")
                                    }
                                    onClick={() => {
                                      setFormData((prevData) => ({
                                        ...prevData,
                                        importer: importer,
                                      }));
                                      setImporterSearchText("");
                                      setListOpen(false);
                                    }}
                                  >
                                    {importer}
                                  </div>
                                ))}
                                {importerFilteredList.length === 0 && (
                                  <div
                                    style={{
                                      padding: "16px",
                                      textAlign: "center",
                                      color: "#999",
                                    }}
                                  >
                                    Brak wyników
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Mapowane sekcje eksportera + faktury + statusu */}
                        {formData.fields.map((field, index) => (
                          <div
                            key={index}
                            style={{
                              borderBottom: "1px solid #ccc",
                              paddingBottom: 16,
                              marginBottom: 24,
                            }}
                          >
                            {/* Eksporter Fields */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "16px",
                                justifyContent: "space-between",
                              }}
                            >
                              {isManBusSelected ? (
                                <div
                                  style={{
                                    position: "relative",
                                    width: "100%",
                                    marginRight: "8px",
                                  }}
                                  data-man-exporter-dropdown={index}
                                >
                                  <TextField
                                    required
                                    label={`Eksporter ${index + 1}`}
                                    name={`eksporter${index}`}
                                    value={field.eksporter}
                                    onChange={handleChange}
                                    autoComplete="off"
                                    fullWidth
                                    disabled={isFetching}
                                    onClick={() =>
                                      openManExporterDropdown(index)
                                    }
                                    onFocus={() =>
                                      openManExporterDropdown(index)
                                    }
                                    InputProps={{
                                      readOnly: true,
                                      endAdornment: (
                                        <span style={{ cursor: "pointer" }}>
                                          ▼
                                        </span>
                                      ),
                                    }}
                                  />
                                  {isManExporterDropdownOpen(index) && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        zIndex: 1300,
                                        width: "120%",
                                        maxHeight: 450,
                                        backgroundColor: "white",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                        marginTop: "4px",
                                        left: "-10%",
                                      }}
                                    >
                                      <div
                                        style={{
                                          padding: "8px",
                                          backgroundColor: "#f5f5f5",
                                          borderBottom: "1px solid #eee",
                                        }}
                                      >
                                        <TextField
                                          placeholder="Szukaj eksportera..."
                                          value={getManExporterSearchText(
                                            index,
                                          )}
                                          onChange={(e) =>
                                            updateManExporterSearchText(
                                              index,
                                              e.target.value,
                                            )
                                          }
                                          fullWidth
                                          size="small"
                                          variant="outlined"
                                          autoFocus
                                          InputProps={{
                                            style: {
                                              fontSize: "14px",
                                              backgroundColor: "white",
                                            },
                                          }}
                                        />
                                      </div>
                                      <div
                                        style={{
                                          maxHeight: 350,
                                          overflowY: "auto",
                                        }}
                                      >
                                        {getFilteredManExporters(index).map(
                                          (exporter, exporterIndex) => (
                                            <div
                                              key={exporterIndex}
                                              style={{
                                                padding: "8px 16px",
                                                cursor: "pointer",
                                                borderBottom:
                                                  "1px solid #f0f0f0",
                                              }}
                                              onMouseEnter={(e) =>
                                                (e.target.style.backgroundColor =
                                                  "#f5f5f5")
                                              }
                                              onMouseLeave={(e) =>
                                                (e.target.style.backgroundColor =
                                                  "transparent")
                                              }
                                              onClick={() => {
                                                handleFieldsChange(
                                                  "eksporter",
                                                  exporter,
                                                  index,
                                                );
                                                updateManExporterSearchText(
                                                  index,
                                                  "",
                                                );
                                                closeManExporterDropdown(index);
                                              }}
                                            >
                                              {exporter}
                                            </div>
                                          ),
                                        )}
                                        {getFilteredManExporters(index)
                                          .length === 0 && (
                                          <div
                                            style={{
                                              padding: "16px",
                                              textAlign: "center",
                                              color: "#999",
                                            }}
                                          >
                                            Brak wyników
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <TextField
                                  label={`Eksporter ${index + 1}`}
                                  name={`eksporter${index}`}
                                  value={field.eksporter || ""}
                                  autoComplete="off"
                                  onChange={(e) =>
                                    handleFieldsChange(
                                      "eksporter",
                                      e.target.value,
                                      index,
                                    )
                                  }
                                  fullWidth
                                  style={{ marginRight: "8px" }}
                                />
                              )}
                            </div>

                            {/* Nr faktury */}
                            <TextField
                              type="text"
                              label="Podaj ilość faktur"
                              value={
                                field.invoiceNumbers.length === 0
                                  ? ""
                                  : String(field.invoiceNumbers.length)
                              }
                              onChange={(e) => {
                                let input = e.target.value;

                                // Pozwól na puste pole
                                if (input === "") {
                                  handleInvoiceCountChange(index, 0); // lub może: handleInvoiceCountChange(index, null);
                                  return;
                                }

                                // Usuń wiodące zera
                                input = input.replace(/^0+/, "");

                                // Jeśli pozostało coś po usunięciu zer i to jest liczba — ustaw
                                const count = parseInt(input, 10);
                                if (!isNaN(count)) {
                                  handleInvoiceCountChange(index, count);
                                }
                              }}
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                              }}
                              fullWidth
                              style={{ marginBottom: "16px" }}
                            />
                            {field.invoiceNumbers.map((invoice, invoiceIdx) => (
                              <div
                                key={invoiceIdx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "8px",
                                }}
                              >
                                <TextField
                                  required
                                  label={`Nr. Faktury ${invoiceIdx + 1}`}
                                  name={`nrFaktury${index}_${invoiceIdx}`}
                                  value={invoice}
                                  autoComplete="off"
                                  onChange={(e) => {
                                    const updatedFields = [...formData.fields];
                                    updatedFields[index].invoiceNumbers[
                                      invoiceIdx
                                    ] = e.target.value;
                                    setFormData({
                                      ...formData,
                                      fields: updatedFields,
                                    });
                                  }}
                                  fullWidth
                                />
                                <InvoiceInfoTooltip />
                              </div>
                            ))}

                            {/* Status realizacji */}
                            <FormControl
                              fullWidth
                              className={classes.marginBottom}
                            >
                              <InputLabel id={`select-list-label-${index}`}>
                                Status realizacji {index + 1}*
                              </InputLabel>
                              <Select
                                required
                                labelId={`select-list-label-${index}`}
                                id={`select-list-${index}`}
                                value={field.statusRealizacji || ""}
                                onChange={(e) =>
                                  handleFieldsChange(
                                    "statusRealizacji",
                                    e.target.value,
                                    index,
                                  )
                                }
                                name={`statusRealizacji${index}`}
                              >
                                <MenuItem value="Do przygotowania">
                                  Do przygotowania
                                </MenuItem>
                              </Select>
                            </FormControl>

                            {/* Przycisk "-" */}
                            {index > 0 && (
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => removeFields(index)}
                                className={classes.marginBottom}
                              >
                                -
                              </Button>
                            )}
                          </div>
                        ))}

                        {/* Przycisk "+" */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={addFields}
                          className={classes.marginBottom}
                        >
                          +
                        </Button>
                      </>
                    )}

                    {formData.rodzaj === "Uszlachetnianie czynne" && (
                      <>
                        {/* Globalny Importer */}
                        <div
                          ref={importerDropdownRef}
                          style={{ position: "relative" }}
                          className={`${classes.marginBottom} ${
                            isFetching ? classes.fadeOut : classes.fadeIn
                          }`}
                        >
                          <TextField
                            required
                            label="Importer"
                            name="importer"
                            value={formData.importer}
                            onChange={handleChange}
                            autoComplete="off"
                            fullWidth
                            disabled={isFetching}
                            onClick={() => handleListOpen("importer")}
                            onFocus={() => handleListOpen("importer")}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <span style={{ cursor: "pointer" }}>▼</span>
                              ),
                            }}
                          />
                          {listOpen && listType === "importer" && (
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 1300,
                                width: "120%",
                                maxHeight: 450,
                                backgroundColor: "white",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                marginTop: "4px",
                                left: "-10%",
                              }}
                            >
                              <div
                                style={{
                                  padding: "8px",
                                  backgroundColor: "#f5f5f5",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                <TextField
                                  placeholder="Szukaj importera..."
                                  value={importerSearchText}
                                  onChange={(e) =>
                                    filterImporters(e.target.value)
                                  }
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  autoFocus
                                  InputProps={{
                                    style: {
                                      fontSize: "14px",
                                      backgroundColor: "white",
                                    },
                                  }}
                                />
                              </div>
                              <div
                                style={{ maxHeight: 350, overflowY: "auto" }}
                              >
                                {importerFilteredList.map((importer, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      padding: "8px 16px",
                                      cursor: "pointer",
                                      borderBottom: "1px solid #f0f0f0",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.backgroundColor =
                                        "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.target.style.backgroundColor =
                                        "transparent")
                                    }
                                    onClick={() => {
                                      setFormData((prevData) => ({
                                        ...prevData,
                                        importer: importer,
                                      }));
                                      setImporterSearchText("");
                                      setListOpen(false);
                                    }}
                                  >
                                    {importer}
                                  </div>
                                ))}
                                {importerFilteredList.length === 0 && (
                                  <div
                                    style={{
                                      padding: "16px",
                                      textAlign: "center",
                                      color: "#999",
                                    }}
                                  >
                                    Brak wyników
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Mapped Eksporter + Faktura + Status */}
                        {formData.fields.map((field, index) => (
                          <div
                            key={index}
                            style={{
                              borderBottom: "1px solid #ccc",
                              paddingBottom: 16,
                              marginBottom: 24,
                            }}
                          >
                            {/* Eksporter Fields */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "16px",
                                justifyContent: "space-between",
                              }}
                            >
                              {isManBusSelected ? (
                                <div
                                  style={{
                                    position: "relative",
                                    width: "100%",
                                    marginRight: "8px",
                                  }}
                                  data-man-exporter-dropdown={index}
                                >
                                  <TextField
                                    required
                                    label={`Eksporter ${index + 1}`}
                                    name={`eksporter${index}`}
                                    value={field.eksporter}
                                    onChange={handleChange}
                                    autoComplete="off"
                                    fullWidth
                                    disabled={isFetching}
                                    onClick={() =>
                                      openManExporterDropdown(index)
                                    }
                                    onFocus={() =>
                                      openManExporterDropdown(index)
                                    }
                                    InputProps={{
                                      readOnly: true,
                                      endAdornment: (
                                        <span style={{ cursor: "pointer" }}>
                                          ▼
                                        </span>
                                      ),
                                    }}
                                  />
                                  {isManExporterDropdownOpen(index) && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        zIndex: 1300,
                                        width: "120%",
                                        maxHeight: 450,
                                        backgroundColor: "white",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                        marginTop: "4px",
                                        left: "-10%",
                                      }}
                                    >
                                      <div
                                        style={{
                                          padding: "8px",
                                          backgroundColor: "#f5f5f5",
                                          borderBottom: "1px solid #eee",
                                        }}
                                      >
                                        <TextField
                                          placeholder="Szukaj eksportera..."
                                          value={getManExporterSearchText(
                                            index,
                                          )}
                                          onChange={(e) =>
                                            updateManExporterSearchText(
                                              index,
                                              e.target.value,
                                            )
                                          }
                                          fullWidth
                                          size="small"
                                          variant="outlined"
                                          autoFocus
                                          InputProps={{
                                            style: {
                                              fontSize: "14px",
                                              backgroundColor: "white",
                                            },
                                          }}
                                        />
                                      </div>
                                      <div
                                        style={{
                                          maxHeight: 350,
                                          overflowY: "auto",
                                        }}
                                      >
                                        {getFilteredManExporters(index).map(
                                          (exporter, exporterIndex) => (
                                            <div
                                              key={exporterIndex}
                                              style={{
                                                padding: "8px 16px",
                                                cursor: "pointer",
                                                borderBottom:
                                                  "1px solid #f0f0f0",
                                              }}
                                              onMouseEnter={(e) =>
                                                (e.target.style.backgroundColor =
                                                  "#f5f5f5")
                                              }
                                              onMouseLeave={(e) =>
                                                (e.target.style.backgroundColor =
                                                  "transparent")
                                              }
                                              onClick={() => {
                                                handleFieldsChange(
                                                  "eksporter",
                                                  exporter,
                                                  index,
                                                );
                                                updateManExporterSearchText(
                                                  index,
                                                  "",
                                                );
                                                closeManExporterDropdown(index);
                                              }}
                                            >
                                              {exporter}
                                            </div>
                                          ),
                                        )}
                                        {getFilteredManExporters(index)
                                          .length === 0 && (
                                          <div
                                            style={{
                                              padding: "16px",
                                              textAlign: "center",
                                              color: "#999",
                                            }}
                                          >
                                            Brak wyników
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <TextField
                                  label={`Eksporter ${index + 1}`}
                                  name={`eksporter${index}`}
                                  value={field.eksporter || ""}
                                  autoComplete="off"
                                  onChange={(e) =>
                                    handleFieldsChange(
                                      "eksporter",
                                      e.target.value,
                                      index,
                                    )
                                  }
                                  fullWidth
                                  style={{ marginRight: "8px" }}
                                />
                              )}
                            </div>

                            {/* Nr Faktury */}
                            <TextField
                              type="text"
                              label="Podaj ilość faktur"
                              value={
                                field.invoiceNumbers.length === 0
                                  ? ""
                                  : String(field.invoiceNumbers.length)
                              }
                              onChange={(e) => {
                                let input = e.target.value;

                                // Pozwól na puste pole
                                if (input === "") {
                                  handleInvoiceCountChange(index, 0); // lub może: handleInvoiceCountChange(index, null);
                                  return;
                                }

                                // Usuń wiodące zera
                                input = input.replace(/^0+/, "");

                                // Jeśli pozostało coś po usunięciu zer i to jest liczba — ustaw
                                const count = parseInt(input, 10);
                                if (!isNaN(count)) {
                                  handleInvoiceCountChange(index, count);
                                }
                              }}
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                              }}
                              fullWidth
                              style={{ marginBottom: "16px" }}
                            />
                            {field.invoiceNumbers.map((invoice, invoiceIdx) => (
                              <div
                                key={invoiceIdx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "8px",
                                }}
                              >
                                <TextField
                                  required
                                  label={`Nr. Faktury ${invoiceIdx + 1}`}
                                  name={`nrFaktury${index}_${invoiceIdx}`}
                                  value={invoice}
                                  autoComplete="off"
                                  onChange={(e) => {
                                    const updatedFields = [...formData.fields];
                                    updatedFields[index].invoiceNumbers[
                                      invoiceIdx
                                    ] = e.target.value;
                                    setFormData({
                                      ...formData,
                                      fields: updatedFields,
                                    });
                                  }}
                                  fullWidth
                                />
                                <InvoiceInfoTooltip />
                              </div>
                            ))}

                            {/* Status realizacji */}
                            <FormControl
                              fullWidth
                              className={classes.marginBottom}
                            >
                              <InputLabel id={`select-list-label-${index}`}>
                                Status realizacji {index + 1}*
                              </InputLabel>
                              <Select
                                required
                                labelId={`select-list-label-${index}`}
                                id={`select-list-${index}`}
                                value={field.statusRealizacji || ""}
                                onChange={(e) =>
                                  handleFieldsChange(
                                    "statusRealizacji",
                                    e.target.value,
                                    index,
                                  )
                                }
                                name={`statusRealizacji${index}`}
                              >
                                <MenuItem value="Do przygotowania">
                                  Do przygotowania
                                </MenuItem>
                              </Select>
                            </FormControl>

                            {/* Przycisk "-" */}
                            {index > 0 && (
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => removeFields(index)}
                                className={classes.marginBottom}
                              >
                                -
                              </Button>
                            )}
                          </div>
                        ))}

                        {/* Przycisk "+" */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={addFields}
                          className={classes.marginBottom}
                        >
                          +
                        </Button>
                      </>
                    )}

                    {formData.rodzaj === "Uszlachetnianie bierne" && (
                      <>
                        {/* Globalny Eksporter */}
                        <div
                          ref={exporterDropdownRef}
                          style={{ position: "relative" }}
                          className={`${classes.marginBottom} ${
                            isFetching ? classes.fadeOut : classes.fadeIn
                          }`}
                        >
                          <TextField
                            required
                            label="Eksporter"
                            name="eksporter"
                            value={formData.eksporter}
                            onChange={handleChange}
                            autoComplete="off"
                            fullWidth
                            disabled={isFetching}
                            onClick={() => handleListOpen("eksporter")}
                            onFocus={() => handleListOpen("eksporter")}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <span style={{ cursor: "pointer" }}>▼</span>
                              ),
                            }}
                          />
                          {listOpen && listType === "eksporter" && (
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 1300,
                                width: "120%",
                                maxHeight: 450,
                                backgroundColor: "white",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                marginTop: "4px",
                                left: "-10%",
                              }}
                            >
                              <div
                                style={{
                                  padding: "8px",
                                  backgroundColor: "#f5f5f5",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                <TextField
                                  placeholder="Szukaj eksportera..."
                                  value={exporterSearchText}
                                  onChange={(e) =>
                                    filterExporters(e.target.value)
                                  }
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  autoFocus
                                  InputProps={{
                                    style: {
                                      fontSize: "14px",
                                      backgroundColor: "white",
                                    },
                                  }}
                                />
                              </div>
                              <div
                                style={{ maxHeight: 350, overflowY: "auto" }}
                              >
                                {exporterFilteredList.map((exporter, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      padding: "8px 16px",
                                      cursor: "pointer",
                                      borderBottom: "1px solid #f0f0f0",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.backgroundColor =
                                        "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.target.style.backgroundColor =
                                        "transparent")
                                    }
                                    onClick={() => {
                                      setFormData((prevData) => ({
                                        ...prevData,
                                        eksporter: exporter,
                                      }));
                                      setExporterSearchText("");
                                      setListOpen(false);
                                    }}
                                  >
                                    {exporter}
                                  </div>
                                ))}
                                {exporterFilteredList.length === 0 && (
                                  <div
                                    style={{
                                      padding: "16px",
                                      textAlign: "center",
                                      color: "#999",
                                    }}
                                  >
                                    Brak wyników
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Mapowane pola: Importer + Faktura + Status */}
                        {formData.fields.map((field, index) => (
                          <div
                            key={index}
                            style={{
                              borderBottom: "1px solid #ccc",
                              paddingBottom: 16,
                              marginBottom: 24,
                            }}
                          >
                            {/* Importer */}
                            <TextField
                              label={`Importer ${index + 1}`}
                              name={`importer${index}`}
                              value={field.importer || ""}
                              autoComplete="off"
                              onChange={(e) =>
                                handleFieldsChange(
                                  "importer",
                                  e.target.value,
                                  index,
                                )
                              }
                              fullWidth
                              style={{ marginBottom: "16px" }}
                            />

                            {/* Nr Faktury */}
                            <TextField
                              type="text"
                              label="Podaj ilość faktur"
                              value={
                                field.invoiceNumbers.length === 0
                                  ? ""
                                  : String(field.invoiceNumbers.length)
                              }
                              onChange={(e) => {
                                let input = e.target.value;

                                // Pozwól na puste pole
                                if (input === "") {
                                  handleInvoiceCountChange(index, 0); // lub może: handleInvoiceCountChange(index, null);
                                  return;
                                }

                                // Usuń wiodące zera
                                input = input.replace(/^0+/, "");

                                // Jeśli pozostało coś po usunięciu zer i to jest liczba — ustaw
                                const count = parseInt(input, 10);
                                if (!isNaN(count)) {
                                  handleInvoiceCountChange(index, count);
                                }
                              }}
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                              }}
                              fullWidth
                              style={{ marginBottom: "16px" }}
                            />
                            {field.invoiceNumbers.map((invoice, invoiceIdx) => (
                              <div
                                key={invoiceIdx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "8px",
                                }}
                              >
                                <TextField
                                  required
                                  label={`Nr. Faktury ${invoiceIdx + 1}`}
                                  name={`nrFaktury${index}_${invoiceIdx}`}
                                  value={invoice}
                                  autoComplete="off"
                                  onChange={(e) => {
                                    const updatedFields = [...formData.fields];
                                    updatedFields[index].invoiceNumbers[
                                      invoiceIdx
                                    ] = e.target.value;
                                    setFormData({
                                      ...formData,
                                      fields: updatedFields,
                                    });
                                  }}
                                  fullWidth
                                />
                                <InvoiceInfoTooltip />
                              </div>
                            ))}

                            {/* Status realizacji */}
                            <FormControl
                              fullWidth
                              className={classes.marginBottom}
                            >
                              <InputLabel id={`select-list-label-${index}`}>
                                Status realizacji {index + 1}*
                              </InputLabel>
                              <Select
                                required
                                labelId={`select-list-label-${index}`}
                                id={`select-list-${index}`}
                                value={field.statusRealizacji || ""}
                                onChange={(e) =>
                                  handleFieldsChange(
                                    "statusRealizacji",
                                    e.target.value,
                                    index,
                                  )
                                }
                                name={`statusRealizacji${index}`}
                              >
                                <MenuItem value="Do przygotowania">
                                  Do przygotowania
                                </MenuItem>
                              </Select>
                            </FormControl>

                            {/* Przycisk "-" */}
                            {index > 0 && (
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => removeFields(index)}
                                className={classes.marginBottom}
                              >
                                -
                              </Button>
                            )}
                          </div>
                        ))}

                        {/* Przycisk "+" */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={addFields}
                          className={classes.marginBottom}
                        >
                          +
                        </Button>
                      </>
                    )}

                    {formData.rodzaj === "Powrotny przywóz" && (
                      <>
                        {/* Globalny Importer */}
                        <div
                          ref={importerDropdownRef}
                          style={{ position: "relative" }}
                          className={`${classes.marginBottom} ${
                            isFetching ? classes.fadeOut : classes.fadeIn
                          }`}
                        >
                          <TextField
                            required
                            label="Importer"
                            name="importer"
                            value={formData.importer}
                            onChange={handleChange}
                            autoComplete="off"
                            fullWidth
                            disabled={isFetching}
                            onClick={() => handleListOpen("importer")}
                            onFocus={() => handleListOpen("importer")}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <span style={{ cursor: "pointer" }}>▼</span>
                              ),
                            }}
                          />
                          {listOpen && listType === "importer" && (
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 1300,
                                width: "120%",
                                maxHeight: 450,
                                backgroundColor: "white",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                marginTop: "4px",
                                left: "-10%",
                              }}
                            >
                              <div
                                style={{
                                  padding: "8px",
                                  backgroundColor: "#f5f5f5",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                <TextField
                                  placeholder="Szukaj importera..."
                                  value={importerSearchText}
                                  onChange={(e) =>
                                    filterImporters(e.target.value)
                                  }
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  autoFocus
                                  InputProps={{
                                    style: {
                                      fontSize: "14px",
                                      backgroundColor: "white",
                                    },
                                  }}
                                />
                              </div>
                              <div
                                style={{ maxHeight: 350, overflowY: "auto" }}
                              >
                                {importerFilteredList.map((importer, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      padding: "8px 16px",
                                      cursor: "pointer",
                                      borderBottom: "1px solid #f0f0f0",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.backgroundColor =
                                        "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.target.style.backgroundColor =
                                        "transparent")
                                    }
                                    onClick={() => {
                                      setFormData((prevData) => ({
                                        ...prevData,
                                        importer: importer,
                                      }));
                                      setImporterSearchText("");
                                      setListOpen(false);
                                    }}
                                  >
                                    {importer}
                                  </div>
                                ))}
                                {importerFilteredList.length === 0 && (
                                  <div
                                    style={{
                                      padding: "16px",
                                      textAlign: "center",
                                      color: "#999",
                                    }}
                                  >
                                    Brak wyników
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Mapowane sekcje */}
                        {formData.fields.map((field, index) => (
                          <div
                            key={index}
                            style={{
                              borderBottom: "1px solid #ccc",
                              paddingBottom: 16,
                              marginBottom: 24,
                            }}
                          >
                            {/* Eksporter */}
                            {isManBusSelected ? (
                              <div
                                style={{
                                  position: "relative",
                                  width: "100%",
                                  marginBottom: "16px",
                                }}
                                data-man-exporter-dropdown={index}
                              >
                                <TextField
                                  required
                                  label={`Eksporter ${index + 1}`}
                                  name={`eksporter${index}`}
                                  value={field.eksporter || ""}
                                  onChange={handleChange}
                                  autoComplete="off"
                                  fullWidth
                                  disabled={isFetching}
                                  onClick={() => openManExporterDropdown(index)}
                                  onFocus={() => openManExporterDropdown(index)}
                                  InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                      <span style={{ cursor: "pointer" }}>
                                        ▼
                                      </span>
                                    ),
                                  }}
                                />
                                {isManExporterDropdownOpen(index) && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      zIndex: 1300,
                                      width: "120%",
                                      maxHeight: 450,
                                      backgroundColor: "white",
                                      border: "1px solid #ccc",
                                      borderRadius: "4px",
                                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                      marginTop: "4px",
                                      left: "-10%",
                                    }}
                                  >
                                    <div
                                      style={{
                                        padding: "8px",
                                        backgroundColor: "#f5f5f5",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      <TextField
                                        placeholder="Szukaj eksportera MAN..."
                                        value={getManExporterSearchText(index)}
                                        onChange={(e) =>
                                          updateManExporterSearchText(
                                            index,
                                            e.target.value,
                                          )
                                        }
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        autoFocus
                                        InputProps={{
                                          style: {
                                            fontSize: "14px",
                                            backgroundColor: "white",
                                          },
                                        }}
                                      />
                                    </div>
                                    <div
                                      style={{
                                        maxHeight: 350,
                                        overflowY: "auto",
                                      }}
                                    >
                                      {getFilteredManExporters(index).map(
                                        (exporter, exporterIndex) => (
                                          <div
                                            key={exporterIndex}
                                            style={{
                                              padding: "8px 16px",
                                              cursor: "pointer",
                                              borderBottom: "1px solid #f0f0f0",
                                            }}
                                            onMouseEnter={(e) => {
                                              e.target.style.backgroundColor =
                                                "#f0f0f0";
                                            }}
                                            onMouseLeave={(e) => {
                                              e.target.style.backgroundColor =
                                                "transparent";
                                            }}
                                            onClick={() => {
                                              handleFieldsChange(
                                                "eksporter",
                                                exporter,
                                                index,
                                              );
                                              updateManExporterSearchText(
                                                index,
                                                "",
                                              );
                                              closeManExporterDropdown(index);
                                            }}
                                          >
                                            {exporter}
                                          </div>
                                        ),
                                      )}
                                      {getFilteredManExporters(index).length ===
                                        0 && (
                                        <div
                                          style={{
                                            padding: "16px",
                                            textAlign: "center",
                                            color: "#999",
                                          }}
                                        >
                                          Brak wyników
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <TextField
                                label={`Eksporter ${index + 1}`}
                                name={`eksporter${index}`}
                                value={field.eksporter || ""}
                                autoComplete="off"
                                onChange={(e) =>
                                  handleFieldsChange(
                                    "eksporter",
                                    e.target.value,
                                    index,
                                  )
                                }
                                fullWidth
                                style={{ marginBottom: "16px" }}
                              />
                            )}

                            {/* Nr Faktury */}
                            <TextField
                              type="text"
                              label="Podaj ilość faktur"
                              value={
                                field.invoiceNumbers.length === 0
                                  ? ""
                                  : String(field.invoiceNumbers.length)
                              }
                              onChange={(e) => {
                                let input = e.target.value;

                                // Pozwól na puste pole
                                if (input === "") {
                                  handleInvoiceCountChange(index, 0); // lub może: handleInvoiceCountChange(index, null);
                                  return;
                                }

                                // Usuń wiodące zera
                                input = input.replace(/^0+/, "");

                                // Jeśli pozostało coś po usunięciu zer i to jest liczba — ustaw
                                const count = parseInt(input, 10);
                                if (!isNaN(count)) {
                                  handleInvoiceCountChange(index, count);
                                }
                              }}
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                              }}
                              fullWidth
                              style={{ marginBottom: "16px" }}
                            />
                            {field.invoiceNumbers.map((invoice, invoiceIdx) => (
                              <div
                                key={invoiceIdx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "8px",
                                }}
                              >
                                <TextField
                                  required
                                  label={`Nr. Faktury ${invoiceIdx + 1}`}
                                  name={`nrFaktury${index}_${invoiceIdx}`}
                                  value={invoice}
                                  autoComplete="off"
                                  onChange={(e) => {
                                    const updatedFields = [...formData.fields];
                                    updatedFields[index].invoiceNumbers[
                                      invoiceIdx
                                    ] = e.target.value;
                                    setFormData({
                                      ...formData,
                                      fields: updatedFields,
                                    });
                                  }}
                                  fullWidth
                                />
                                <InvoiceInfoTooltip />
                              </div>
                            ))}

                            {/* Status realizacji */}
                            <FormControl
                              fullWidth
                              className={classes.marginBottom}
                            >
                              <InputLabel id={`select-list-label-${index}`}>
                                Status realizacji {index + 1}*
                              </InputLabel>
                              <Select
                                required
                                labelId={`select-list-label-${index}`}
                                id={`select-list-${index}`}
                                value={field.statusRealizacji || ""}
                                onChange={(e) =>
                                  handleFieldsChange(
                                    "statusRealizacji",
                                    e.target.value,
                                    index,
                                  )
                                }
                                name={`statusRealizacji${index}`}
                              >
                                <MenuItem value="Do przygotowania">
                                  Do przygotowania
                                </MenuItem>
                              </Select>
                            </FormControl>

                            {/* Przycisk "-" */}
                            {index > 0 && (
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => removeFields(index)}
                                className={classes.marginBottom}
                              >
                                -
                              </Button>
                            )}
                          </div>
                        ))}

                        {/* Przycisk "+" */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={addFields}
                          className={classes.marginBottom}
                        >
                          +
                        </Button>
                      </>
                    )}

                    {formData.rodzaj === "Powrotny wywóz" && (
                      <>
                        {/* Globalny Eksporter */}
                        <div
                          ref={exporterDropdownRef}
                          style={{ position: "relative" }}
                          className={`${classes.marginBottom} ${
                            isFetching ? classes.fadeOut : classes.fadeIn
                          }`}
                        >
                          <TextField
                            required
                            label="Eksporter"
                            name="eksporter"
                            value={formData.eksporter}
                            onChange={handleChange}
                            autoComplete="off"
                            fullWidth
                            disabled={isFetching}
                            onClick={() => handleListOpen("eksporter")}
                            onFocus={() => handleListOpen("eksporter")}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <span style={{ cursor: "pointer" }}>▼</span>
                              ),
                            }}
                          />
                          {listOpen && listType === "eksporter" && (
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 1300,
                                width: "120%",
                                maxHeight: 450,
                                backgroundColor: "white",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                marginTop: "4px",
                                left: "-10%",
                              }}
                            >
                              <div
                                style={{
                                  padding: "8px",
                                  backgroundColor: "#f5f5f5",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                <TextField
                                  placeholder="Szukaj eksportera..."
                                  value={exporterSearchText}
                                  onChange={(e) =>
                                    filterExporters(e.target.value)
                                  }
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  autoFocus
                                  InputProps={{
                                    style: {
                                      fontSize: "14px",
                                      backgroundColor: "white",
                                    },
                                  }}
                                />
                              </div>
                              <div
                                style={{ maxHeight: 350, overflowY: "auto" }}
                              >
                                {exporterFilteredList.map((exporter, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      padding: "8px 16px",
                                      cursor: "pointer",
                                      borderBottom: "1px solid #f0f0f0",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.backgroundColor =
                                        "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.target.style.backgroundColor =
                                        "transparent")
                                    }
                                    onClick={() => {
                                      setFormData((prevData) => ({
                                        ...prevData,
                                        eksporter: exporter,
                                      }));
                                      setExporterSearchText("");
                                      setListOpen(false);
                                    }}
                                  >
                                    {exporter}
                                  </div>
                                ))}
                                {exporterFilteredList.length === 0 && (
                                  <div
                                    style={{
                                      padding: "16px",
                                      textAlign: "center",
                                      color: "#999",
                                    }}
                                  >
                                    Brak wyników
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Mapowane sekcje Importer + Faktura + Status */}
                        {formData.fields.map((field, index) => (
                          <div
                            key={index}
                            style={{
                              borderBottom: "1px solid #ccc",
                              paddingBottom: 16,
                              marginBottom: 24,
                            }}
                          >
                            {/* Importer */}
                            <TextField
                              label={`Importer ${index + 1}`}
                              name={`importer${index}`}
                              value={field.importer || ""}
                              autoComplete="off"
                              onChange={(e) =>
                                handleFieldsChange(
                                  "importer",
                                  e.target.value,
                                  index,
                                )
                              }
                              fullWidth
                              style={{ marginBottom: "16px" }}
                            />

                            {/* Nr Faktury */}
                            <TextField
                              type="text"
                              label="Podaj ilość faktur"
                              value={
                                field.invoiceNumbers.length === 0
                                  ? ""
                                  : String(field.invoiceNumbers.length)
                              }
                              onChange={(e) => {
                                let input = e.target.value;

                                // Pozwól na puste pole
                                if (input === "") {
                                  handleInvoiceCountChange(index, 0); // lub może: handleInvoiceCountChange(index, null);
                                  return;
                                }

                                // Usuń wiodące zera
                                input = input.replace(/^0+/, "");

                                // Jeśli pozostało coś po usunięciu zer i to jest liczba — ustaw
                                const count = parseInt(input, 10);
                                if (!isNaN(count)) {
                                  handleInvoiceCountChange(index, count);
                                }
                              }}
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                              }}
                              fullWidth
                              style={{ marginBottom: "16px" }}
                            />
                            {field.invoiceNumbers.map((invoice, invoiceIdx) => (
                              <div
                                key={invoiceIdx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "8px",
                                }}
                              >
                                <TextField
                                  required
                                  label={`Nr. Faktury ${invoiceIdx + 1}`}
                                  name={`nrFaktury${index}_${invoiceIdx}`}
                                  value={invoice}
                                  autoComplete="off"
                                  onChange={(e) => {
                                    const updatedFields = [...formData.fields];
                                    updatedFields[index].invoiceNumbers[
                                      invoiceIdx
                                    ] = e.target.value;
                                    setFormData({
                                      ...formData,
                                      fields: updatedFields,
                                    });
                                  }}
                                  fullWidth
                                />
                                <InvoiceInfoTooltip />
                              </div>
                            ))}

                            {/* Status realizacji */}
                            <FormControl
                              fullWidth
                              className={classes.marginBottom}
                            >
                              <InputLabel id={`select-list-label-${index}`}>
                                Status realizacji {index + 1}*
                              </InputLabel>
                              <Select
                                required
                                labelId={`select-list-label-${index}`}
                                id={`select-list-${index}`}
                                value={field.statusRealizacji || ""}
                                onChange={(e) =>
                                  handleFieldsChange(
                                    "statusRealizacji",
                                    e.target.value,
                                    index,
                                  )
                                }
                                name={`statusRealizacji${index}`}
                              >
                                <MenuItem value="Do przygotowania">
                                  Do przygotowania
                                </MenuItem>
                              </Select>
                            </FormControl>

                            {/* Przycisk "-" */}
                            {index > 0 && (
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => removeFields(index)}
                                className={classes.marginBottom}
                              >
                                -
                              </Button>
                            )}
                          </div>
                        ))}

                        {/* Przycisk "+" */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={addFields}
                          className={classes.marginBottom}
                        >
                          +
                        </Button>
                      </>
                    )}

                    {formData.rodzaj === "Eksport - wpis do rejestru" && (
                      <>
                        {/* Eksporter (globalny) */}
                        <FormControl fullWidth className={classes.marginBottom}>
                          <InputLabel id="eksporter-label">
                            Eksporter*
                          </InputLabel>
                          <Select
                            required
                            labelId="eksporter-label"
                            id="eksporter-select"
                            value={formData.eksporter}
                            onChange={handleChange}
                            name="eksporter"
                            disabled={isFetching}
                            className={
                              isFetching ? classes.fadeOut : classes.fadeIn
                            }
                          >
                            <MenuItem value="AUTO1 EUROPEAN CARS B.V EORI NL856915361">
                              AUTO1 European Cars B.V EORI NL856915361
                            </MenuItem>
                            <MenuItem value="AUTO1 CAR EXPORT S.R.L EORI IT04927470239">
                              AUTO1 CAR EXPORT S.R.L EORI IT04927470239
                            </MenuItem>
                            <MenuItem value="AUTO1 CAR TRADE S.R.L EORI IT04927470239">
                              AUTO1 CAR TRADE S.R.L EORI IT04927470239
                            </MenuItem>
                          </Select>
                        </FormControl>

                        {/* Mapowane sekcje importer + faktura + status */}
                        {formData.fields.map((field, index) => (
                          <div
                            key={index}
                            style={{
                              borderBottom: "1px solid #ccc",
                              paddingBottom: 16,
                              marginBottom: 24,
                            }}
                          >
                            {/* Importer */}
                            <TextField
                              label={`Importer ${index + 1}`}
                              name={`importer${index}`}
                              value={field.importer || ""}
                              onChange={(e) =>
                                handleFieldsChange(
                                  "importer",
                                  e.target.value,
                                  index,
                                )
                              }
                              fullWidth
                              autoComplete="off"
                              className={classes.marginBottom}
                            />

                            {/* Nr Faktury */}
                            <TextField
                              type="text"
                              label="Podaj ilość faktur"
                              value={
                                field.invoiceNumbers.length === 0
                                  ? ""
                                  : String(field.invoiceNumbers.length)
                              }
                              onChange={(e) => {
                                let input = e.target.value;

                                // Pozwól na puste pole
                                if (input === "") {
                                  handleInvoiceCountChange(index, 0); // lub może: handleInvoiceCountChange(index, null);
                                  return;
                                }

                                // Usuń wiodące zera
                                input = input.replace(/^0+/, "");

                                // Jeśli pozostało coś po usunięciu zer i to jest liczba — ustaw
                                const count = parseInt(input, 10);
                                if (!isNaN(count)) {
                                  handleInvoiceCountChange(index, count);
                                }
                              }}
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                              }}
                              fullWidth
                              style={{ marginBottom: "16px" }}
                            />
                            {field.invoiceNumbers.map((invoice, invoiceIdx) => (
                              <div
                                key={invoiceIdx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "8px",
                                }}
                              >
                                <TextField
                                  required
                                  label={`Nr. Faktury ${invoiceIdx + 1}`}
                                  name={`nrFaktury${index}_${invoiceIdx}`}
                                  value={invoice}
                                  autoComplete="off"
                                  onChange={(e) => {
                                    const updatedFields = [...formData.fields];
                                    updatedFields[index].invoiceNumbers[
                                      invoiceIdx
                                    ] = e.target.value;
                                    setFormData({
                                      ...formData,
                                      fields: updatedFields,
                                    });
                                  }}
                                  fullWidth
                                />
                                <InvoiceInfoTooltip />
                              </div>
                            ))}

                            {/* Status realizacji */}
                            <FormControl
                              fullWidth
                              className={classes.marginBottom}
                            >
                              <InputLabel id={`select-list-label-${index}`}>
                                Status realizacji {index + 1}*
                              </InputLabel>
                              <Select
                                required
                                labelId={`select-list-label-${index}`}
                                id={`select-list-${index}`}
                                value={field.statusRealizacji || ""}
                                onChange={(e) =>
                                  handleFieldsChange(
                                    "statusRealizacji",
                                    e.target.value,
                                    index,
                                  )
                                }
                                name={`statusRealizacji${index}`}
                              >
                                <MenuItem value="Do przygotowania">
                                  Do przygotowania
                                </MenuItem>
                              </Select>
                            </FormControl>

                            {/* Przycisk "-" */}
                            {index > 0 && (
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => removeFields(index)}
                                className={classes.marginBottom}
                              >
                                -
                              </Button>
                            )}
                          </div>
                        ))}

                        {/* Przycisk "+" */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={addFields}
                          className={classes.marginBottom}
                        >
                          +
                        </Button>
                      </>
                    )}

                    {(formData.rodzaj === "Import - wpis do rejestru" ||
                      formData.rodzaj === "Symulacja sad") && (
                      <>
                        <div
                          ref={importerDropdownRef}
                          style={{ position: "relative" }}
                          className={`${classes.marginBottom} ${
                            isFetching ? classes.fadeOut : classes.fadeIn
                          }`}
                        >
                          <TextField
                            required
                            label="Importer"
                            name="importer"
                            value={formData.importer}
                            onChange={handleChange}
                            autoComplete="off"
                            fullWidth
                            disabled={isFetching}
                            onClick={() => handleListOpen("importer")}
                            onFocus={() => handleListOpen("importer")}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <span style={{ cursor: "pointer" }}>▼</span>
                              ),
                            }}
                          />
                          {listOpen && listType === "importer" && (
                            <div
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                right: 0,
                                backgroundColor: "white",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                                zIndex: 1000,
                                maxHeight: "450px",
                                overflowY: "auto",
                                width: "120%",
                              }}
                            >
                              <TextField
                                placeholder="Szukaj importera..."
                                value={importerSearchText}
                                onChange={(e) =>
                                  filterImporters(e.target.value)
                                }
                                fullWidth
                                size="small"
                                autoFocus
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                }}
                                InputProps={{
                                  style: { fontSize: "14px" },
                                }}
                              />
                              {importerFilteredList.length > 0 ? (
                                importerFilteredList.map((importer, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      padding: "12px 16px",
                                      cursor: "pointer",
                                      borderBottom:
                                        index < importerFilteredList.length - 1
                                          ? "1px solid #f0f0f0"
                                          : "none",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.backgroundColor =
                                        "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.target.style.backgroundColor = "white")
                                    }
                                    onClick={() => {
                                      handleChange({
                                        target: {
                                          name: "importer",
                                          value: importer,
                                        },
                                      });
                                      handleListClose();
                                    }}
                                  >
                                    {importer}
                                  </div>
                                ))
                              ) : (
                                <div
                                  style={{
                                    padding: "12px 16px",
                                    color: "#999",
                                    fontStyle: "italic",
                                  }}
                                >
                                  Brak wyników
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {formData.fields.map((field, index) => (
                          <div key={index}>
                            {/* Eksporter Fields */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "16px",
                                justifyContent: "space-between",
                              }}
                            >
                              {isManBusSelected ? (
                                <div
                                  style={{
                                    position: "relative",
                                    width: "100%",
                                    marginRight: "8px",
                                  }}
                                  data-man-exporter-dropdown={index}
                                >
                                  <TextField
                                    required
                                    label={`Eksporter ${index + 1}`}
                                    name={`eksporter${index}`}
                                    value={field.eksporter}
                                    onChange={handleChange}
                                    autoComplete="off"
                                    fullWidth
                                    disabled={isFetching}
                                    onClick={() =>
                                      openManExporterDropdown(index)
                                    }
                                    onFocus={() =>
                                      openManExporterDropdown(index)
                                    }
                                    InputProps={{
                                      readOnly: true,
                                      endAdornment: (
                                        <span style={{ cursor: "pointer" }}>
                                          ▼
                                        </span>
                                      ),
                                    }}
                                  />
                                  {isManExporterDropdownOpen(index) && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        zIndex: 1300,
                                        width: "120%",
                                        maxHeight: 450,
                                        backgroundColor: "white",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                        marginTop: "4px",
                                        left: "-10%",
                                      }}
                                    >
                                      <div
                                        style={{
                                          padding: "8px",
                                          backgroundColor: "#f5f5f5",
                                          borderBottom: "1px solid #eee",
                                        }}
                                      >
                                        <TextField
                                          placeholder="Szukaj eksportera MAN..."
                                          value={getManExporterSearchText(
                                            index,
                                          )}
                                          onChange={(e) =>
                                            updateManExporterSearchText(
                                              index,
                                              e.target.value,
                                            )
                                          }
                                          fullWidth
                                          size="small"
                                          variant="outlined"
                                          autoFocus
                                          InputProps={{
                                            style: {
                                              fontSize: "14px",
                                              backgroundColor: "white",
                                            },
                                          }}
                                        />
                                      </div>
                                      <div
                                        style={{
                                          maxHeight: 350,
                                          overflowY: "auto",
                                        }}
                                      >
                                        {getFilteredManExporters(index).map(
                                          (exporter, exporterIndex) => (
                                            <div
                                              key={exporterIndex}
                                              style={{
                                                padding: "8px 16px",
                                                cursor: "pointer",
                                                borderBottom:
                                                  "1px solid #f0f0f0",
                                              }}
                                              onMouseEnter={(e) => {
                                                e.target.style.backgroundColor =
                                                  "#f0f0f0";
                                              }}
                                              onMouseLeave={(e) => {
                                                e.target.style.backgroundColor =
                                                  "transparent";
                                              }}
                                              onClick={() => {
                                                handleFieldsChange(
                                                  "eksporter",
                                                  exporter,
                                                  index,
                                                );
                                                updateManExporterSearchText(
                                                  index,
                                                  "",
                                                );
                                                closeManExporterDropdown(index);
                                              }}
                                            >
                                              {exporter}
                                            </div>
                                          ),
                                        )}
                                        {getFilteredManExporters(index)
                                          .length === 0 && (
                                          <div
                                            style={{
                                              padding: "16px",
                                              textAlign: "center",
                                              color: "#999",
                                            }}
                                          >
                                            Brak wyników
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <TextField
                                  label={`Eksporter ${index + 1}`}
                                  name={`eksporter${index}`}
                                  value={field.eksporter}
                                  autoComplete="off"
                                  onChange={(e) =>
                                    handleFieldsChange(
                                      "eksporter",
                                      e.target.value,
                                      index,
                                    )
                                  }
                                  fullWidth
                                  style={{ marginRight: "8px" }}
                                />
                              )}
                            </div>

                            {/* Nr. Tranzytu Fields */}
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                marginBottom: "16px",
                              }}
                            >
                              <TextField
                                label={`Nr. Tranzytu ${index + 1}`}
                                name={`nrTranzytu${index}`}
                                value={field.nrTranzytu}
                                autoComplete="off"
                                onChange={(e) =>
                                  handleFieldsChange(
                                    "nrTranzytu",
                                    e.target.value,
                                    index,
                                  )
                                }
                                fullWidth
                                style={{ marginBottom: "8px" }}
                              />

                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  variant="subtitle1" // zamiast nieistniejącego h8
                                  className={classes.formTitle}
                                  style={{ marginRight: "16px" }} // lub np. 24px
                                >
                                  Data wygaśnięcia T1
                                </Typography>
                                <DatePickerField
                                  label={`Data wygaśnięcia dokumentów ${
                                    index + 1
                                  }`}
                                  name={`expirationDate${index}`}
                                  value={field.expirationDate}
                                  autoComplete="off"
                                  onChange={(date) =>
                                    handleFieldsChange(
                                      "expirationDate",
                                      date,
                                      index,
                                    )
                                  }
                                  fullWidth
                                  format="dd-MM-yyyy"
                                  style={{ flex: 1 }}
                                  error={!!expirationDateErrors[index]}
                                  helperText={expirationDateErrors[index]}
                                />
                              </div>

                              <TextField
                                type="text"
                                label="Podaj ilość faktur"
                                value={
                                  field.invoiceNumbers.length === 0
                                    ? ""
                                    : String(field.invoiceNumbers.length)
                                }
                                onChange={(e) => {
                                  let input = e.target.value;

                                  // Pozwól na puste pole
                                  if (input === "") {
                                    handleInvoiceCountChange(index, 0); // lub może: handleInvoiceCountChange(index, null);
                                    return;
                                  }

                                  // Usuń wiodące zera
                                  input = input.replace(/^0+/, "");

                                  // Jeśli pozostało coś po usunięciu zer i to jest liczba — ustaw
                                  const count = parseInt(input, 10);
                                  if (!isNaN(count)) {
                                    handleInvoiceCountChange(index, count);
                                  }
                                }}
                                inputProps={{
                                  inputMode: "numeric",
                                  pattern: "[0-9]*",
                                }}
                                fullWidth
                                style={{ marginBottom: "16px" }}
                              />
                              {field.invoiceNumbers.map(
                                (invoice, invoiceIdx) => (
                                  <div
                                    key={invoiceIdx}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <TextField
                                      required
                                      label={`Nr. Faktury ${invoiceIdx + 1}`}
                                      name={`nrFaktury${index}_${invoiceIdx}`}
                                      value={invoice}
                                      autoComplete="off"
                                      onChange={(e) => {
                                        const updatedFields = [
                                          ...formData.fields,
                                        ];
                                        updatedFields[index].invoiceNumbers[
                                          invoiceIdx
                                        ] = e.target.value;
                                        setFormData({
                                          ...formData,
                                          fields: updatedFields,
                                        });
                                      }}
                                      fullWidth
                                    />
                                    <InvoiceInfoTooltip />
                                  </div>
                                ),
                              )}

                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "16px",
                                  justifyContent: "space-between",
                                }}
                              >
                                <FormControl fullWidth>
                                  <InputLabel id={`select-list-label-${index}`}>
                                    Status realizacji {index + 1}*
                                  </InputLabel>
                                  <Select
                                    required
                                    labelId={`select-list-label-${index}`}
                                    id={`select-list-${index}`}
                                    value={statusRealizacjiFields[index]} // Use statusRealizacjiFields[index] for value
                                    onChange={(e) =>
                                      handleStatusRealizacjiChange(
                                        e.target.value,
                                        index,
                                      )
                                    }
                                    name={`statusRealizacji${index}`}
                                    className={classes.marginBottom}
                                  >
                                    <MenuItem value="Do przygotowania">
                                      Do przygotowania
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              </div>
                            </div>
                            {/* Nr. Faktury Field */}
                            {index > 0 && (
                              <Button
                                variant="contained"
                                color="secondary"
                                className={classes.marginBottom}
                                onClick={() => removeFields(index)}
                              >
                                -
                              </Button>
                            )}
                          </div>
                        ))}

                        {/* Common Add Fields button */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={addFields}
                          className={classes.marginBottom}
                        >
                          +
                        </Button>
                      </>
                    )}

                    {formData.rodzaj === "Eksport PL i import inny kraj" && (
                      <>
                        {/* Globalny Eksporter */}
                        <div style={{ position: "relative" }}>
                          <TextField
                            required
                            label="Eksporter"
                            name="eksporter"
                            value={formData.eksporter}
                            onChange={handleChange}
                            autoComplete="off"
                            fullWidth
                            className={`${classes.marginBottom} ${
                              isFetching ? classes.fadeOut : classes.fadeIn
                            }`}
                            disabled={isFetching}
                            onClick={() => handleListOpen("eksporter")}
                            InputProps={{
                              readOnly: true,
                              style: { cursor: "pointer" },
                            }}
                          />
                          {listOpen && listType === "eksporter" && (
                            <div
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                right: 0,
                                backgroundColor: "white",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                                zIndex: 1000,
                                maxHeight: "450px",
                                overflowY: "auto",
                                width: "120%",
                              }}
                            >
                              <TextField
                                placeholder="Szukaj eksportera..."
                                value={exporterSearchText}
                                onChange={(e) =>
                                  filterExporters(e.target.value)
                                }
                                fullWidth
                                size="small"
                                autoFocus
                                style={{
                                  padding: "8px",
                                  borderBottom: "1px solid #eee",
                                }}
                                InputProps={{
                                  style: { fontSize: "14px" },
                                }}
                              />
                              {exporterFilteredList.length > 0 ? (
                                exporterFilteredList.map((exporter, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      padding: "12px 16px",
                                      cursor: "pointer",
                                      borderBottom:
                                        index < exporterFilteredList.length - 1
                                          ? "1px solid #f0f0f0"
                                          : "none",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.backgroundColor =
                                        "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.target.style.backgroundColor = "white")
                                    }
                                    onClick={() => {
                                      handleChange({
                                        target: {
                                          name: "eksporter",
                                          value: exporter,
                                        },
                                      });
                                      handleListClose();
                                    }}
                                  >
                                    {exporter}
                                  </div>
                                ))
                              ) : (
                                <div
                                  style={{
                                    padding: "12px 16px",
                                    color: "#999",
                                    fontStyle: "italic",
                                  }}
                                >
                                  Brak wyników
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Mapowane sekcje Importer + Faktura + Status */}
                        {formData.fields.map((field, index) => (
                          <div
                            key={index}
                            style={{
                              borderBottom: "1px solid #ccc",
                              paddingBottom: 16,
                              marginBottom: 24,
                            }}
                          >
                            {/* Importer */}
                            <TextField
                              label={`Importer ${index + 1}`}
                              name={`importer${index}`}
                              value={field.importer || ""}
                              autoComplete="off"
                              onChange={(e) =>
                                handleFieldsChange(
                                  "importer",
                                  e.target.value,
                                  index,
                                )
                              }
                              fullWidth
                              style={{ marginBottom: "16px" }}
                            />

                            {/* Nr Faktury */}
                            <TextField
                              type="text"
                              label="Podaj ilość faktur"
                              value={
                                field.invoiceNumbers.length === 0
                                  ? ""
                                  : String(field.invoiceNumbers.length)
                              }
                              onChange={(e) => {
                                let input = e.target.value;

                                // Pozwól na puste pole
                                if (input === "") {
                                  handleInvoiceCountChange(index, 0); // lub może: handleInvoiceCountChange(index, null);
                                  return;
                                }

                                // Usuń wiodące zera
                                input = input.replace(/^0+/, "");

                                // Jeśli pozostało coś po usunięciu zer i to jest liczba — ustaw
                                const count = parseInt(input, 10);
                                if (!isNaN(count)) {
                                  handleInvoiceCountChange(index, count);
                                }
                              }}
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                              }}
                              fullWidth
                              style={{ marginBottom: "16px" }}
                            />
                            {field.invoiceNumbers.map((invoice, invoiceIdx) => (
                              <div
                                key={invoiceIdx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "8px",
                                }}
                              >
                                <TextField
                                  required
                                  label={`Nr. Faktury ${invoiceIdx + 1}`}
                                  name={`nrFaktury${index}_${invoiceIdx}`}
                                  value={invoice}
                                  autoComplete="off"
                                  onChange={(e) => {
                                    const updatedFields = [...formData.fields];
                                    updatedFields[index].invoiceNumbers[
                                      invoiceIdx
                                    ] = e.target.value;
                                    setFormData({
                                      ...formData,
                                      fields: updatedFields,
                                    });
                                  }}
                                  fullWidth
                                />
                                <InvoiceInfoTooltip />
                              </div>
                            ))}

                            {/* Status realizacji */}
                            <FormControl
                              fullWidth
                              className={classes.marginBottom}
                            >
                              <InputLabel id={`select-list-label-${index}`}>
                                Status realizacji {index + 1}*
                              </InputLabel>
                              <Select
                                required
                                labelId={`select-list-label-${index}`}
                                id={`select-list-${index}`}
                                value={field.statusRealizacji || ""}
                                onChange={(e) =>
                                  handleFieldsChange(
                                    "statusRealizacji",
                                    e.target.value,
                                    index,
                                  )
                                }
                                name={`statusRealizacji${index}`}
                              >
                                <MenuItem value="Do przygotowania">
                                  Do przygotowania
                                </MenuItem>
                              </Select>
                            </FormControl>

                            {/* Przycisk "-" */}
                            {index > 0 && (
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => removeFields(index)}
                                className={classes.marginBottom}
                              >
                                -
                              </Button>
                            )}
                          </div>
                        ))}

                        {/* Przycisk "+" */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={addFields}
                          className={classes.marginBottom}
                        >
                          +
                        </Button>
                      </>
                    )}

                    {formData.rodzaj === "Odprawa czasowa import" && (
                      <>
                        {/* Globalny Importer */}
                        <div
                          ref={importerDropdownRef}
                          style={{ position: "relative" }}
                          className={`${classes.marginBottom} ${
                            isFetching ? classes.fadeOut : classes.fadeIn
                          }`}
                        >
                          <TextField
                            required
                            label="Importer"
                            name="importer"
                            value={formData.importer}
                            onChange={handleChange}
                            autoComplete="off"
                            fullWidth
                            disabled={isFetching}
                            onClick={() => handleListOpen("importer")}
                            onFocus={() => handleListOpen("importer")}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <span style={{ cursor: "pointer" }}>▼</span>
                              ),
                            }}
                          />
                          {listOpen && listType === "importer" && (
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 1300,
                                width: "120%",
                                maxHeight: 450,
                                backgroundColor: "white",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                marginTop: "4px",
                                left: "-10%",
                              }}
                            >
                              <div
                                style={{
                                  padding: "8px",
                                  backgroundColor: "#f5f5f5",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                <TextField
                                  placeholder="Szukaj importera..."
                                  value={importerSearchText}
                                  onChange={(e) =>
                                    filterImporters(e.target.value)
                                  }
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  autoFocus
                                  InputProps={{
                                    style: {
                                      fontSize: "14px",
                                      backgroundColor: "white",
                                    },
                                  }}
                                />
                              </div>
                              <div
                                style={{ maxHeight: 350, overflowY: "auto" }}
                              >
                                {importerFilteredList.map((importer, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      padding: "8px 16px",
                                      cursor: "pointer",
                                      borderBottom: "1px solid #f0f0f0",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.backgroundColor =
                                        "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.target.style.backgroundColor =
                                        "transparent")
                                    }
                                    onClick={() => {
                                      setFormData((prevData) => ({
                                        ...prevData,
                                        importer: importer,
                                      }));
                                      setImporterSearchText("");
                                      setListOpen(false);
                                    }}
                                  >
                                    {importer}
                                  </div>
                                ))}
                                {importerFilteredList.length === 0 && (
                                  <div
                                    style={{
                                      padding: "16px",
                                      textAlign: "center",
                                      color: "#999",
                                    }}
                                  >
                                    Brak wyników
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Mapowane sekcje */}
                        {formData.fields.map((field, index) => (
                          <div
                            key={index}
                            style={{
                              borderBottom: "1px solid #ccc",
                              paddingBottom: 16,
                              marginBottom: 24,
                            }}
                          >
                            {/* Eksporter */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "16px",
                                justifyContent: "space-between",
                              }}
                            >
                              {isManBusSelected ? (
                                <div
                                  style={{
                                    position: "relative",
                                    width: "100%",
                                    marginRight: "8px",
                                  }}
                                  data-man-exporter-dropdown={index}
                                >
                                  <TextField
                                    required
                                    label={`Eksporter ${index + 1}`}
                                    name={`eksporter${index}`}
                                    value={field.eksporter || ""}
                                    onChange={handleChange}
                                    autoComplete="off"
                                    fullWidth
                                    disabled={isFetching}
                                    onClick={() =>
                                      openManExporterDropdown(index)
                                    }
                                    onFocus={() =>
                                      openManExporterDropdown(index)
                                    }
                                    InputProps={{
                                      readOnly: true,
                                      endAdornment: (
                                        <span style={{ cursor: "pointer" }}>
                                          ▼
                                        </span>
                                      ),
                                    }}
                                  />
                                  {isManExporterDropdownOpen(index) && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        zIndex: 1300,
                                        width: "120%",
                                        maxHeight: 450,
                                        backgroundColor: "white",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                        marginTop: "4px",
                                        left: "-10%",
                                      }}
                                    >
                                      <div
                                        style={{
                                          padding: "8px",
                                          backgroundColor: "#f5f5f5",
                                          borderBottom: "1px solid #eee",
                                        }}
                                      >
                                        <TextField
                                          placeholder="Szukaj eksportera MAN..."
                                          value={getManExporterSearchText(
                                            index,
                                          )}
                                          onChange={(e) =>
                                            updateManExporterSearchText(
                                              index,
                                              e.target.value,
                                            )
                                          }
                                          fullWidth
                                          size="small"
                                          variant="outlined"
                                          autoFocus
                                          InputProps={{
                                            style: {
                                              fontSize: "14px",
                                              backgroundColor: "white",
                                            },
                                          }}
                                        />
                                      </div>
                                      <div
                                        style={{
                                          maxHeight: 350,
                                          overflowY: "auto",
                                        }}
                                      >
                                        {getFilteredManExporters(index).map(
                                          (exporter, exporterIndex) => (
                                            <div
                                              key={exporterIndex}
                                              style={{
                                                padding: "8px 16px",
                                                cursor: "pointer",
                                                borderBottom:
                                                  "1px solid #f0f0f0",
                                              }}
                                              onMouseEnter={(e) => {
                                                e.target.style.backgroundColor =
                                                  "#f0f0f0";
                                              }}
                                              onMouseLeave={(e) => {
                                                e.target.style.backgroundColor =
                                                  "transparent";
                                              }}
                                              onClick={() => {
                                                handleFieldsChange(
                                                  "eksporter",
                                                  exporter,
                                                  index,
                                                );
                                                updateManExporterSearchText(
                                                  index,
                                                  "",
                                                );
                                                closeManExporterDropdown(index);
                                              }}
                                            >
                                              {exporter}
                                            </div>
                                          ),
                                        )}
                                        {getFilteredManExporters(index)
                                          .length === 0 && (
                                          <div
                                            style={{
                                              padding: "16px",
                                              textAlign: "center",
                                              color: "#999",
                                            }}
                                          >
                                            Brak wyników
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <TextField
                                  label={`Eksporter ${index + 1}`}
                                  name={`eksporter${index}`}
                                  value={field.eksporter || ""}
                                  autoComplete="off"
                                  onChange={(e) =>
                                    handleFieldsChange(
                                      "eksporter",
                                      e.target.value,
                                      index,
                                    )
                                  }
                                  fullWidth
                                  style={{ marginRight: "8px" }}
                                />
                              )}
                            </div>

                            {/* Nr Faktury */}
                            <TextField
                              type="text"
                              label="Podaj ilość faktur"
                              value={
                                field.invoiceNumbers.length === 0
                                  ? ""
                                  : String(field.invoiceNumbers.length)
                              }
                              onChange={(e) => {
                                let input = e.target.value;

                                // Pozwól na puste pole
                                if (input === "") {
                                  handleInvoiceCountChange(index, 0); // lub może: handleInvoiceCountChange(index, null);
                                  return;
                                }

                                // Usuń wiodące zera
                                input = input.replace(/^0+/, "");

                                // Jeśli pozostało coś po usunięciu zer i to jest liczba — ustaw
                                const count = parseInt(input, 10);
                                if (!isNaN(count)) {
                                  handleInvoiceCountChange(index, count);
                                }
                              }}
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                              }}
                              fullWidth
                              style={{ marginBottom: "16px" }}
                            />
                            {field.invoiceNumbers.map((invoice, invoiceIdx) => (
                              <div
                                key={invoiceIdx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "8px",
                                }}
                              >
                                <TextField
                                  required
                                  label={`Nr. Faktury ${invoiceIdx + 1}`}
                                  name={`nrFaktury${index}_${invoiceIdx}`}
                                  value={invoice}
                                  autoComplete="off"
                                  onChange={(e) => {
                                    const updatedFields = [...formData.fields];
                                    updatedFields[index].invoiceNumbers[
                                      invoiceIdx
                                    ] = e.target.value;
                                    setFormData({
                                      ...formData,
                                      fields: updatedFields,
                                    });
                                  }}
                                  fullWidth
                                />
                                <InvoiceInfoTooltip />
                              </div>
                            ))}

                            <div
                              key={index}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "16px",
                                justifyContent: "space-between",
                              }}
                            >
                              <FormControl fullWidth>
                                <InputLabel id={`select-list-label-${index}`}>
                                  Status realizacji {index + 1}*
                                </InputLabel>
                                <Select
                                  required
                                  labelId={`select-list-label-${index}`}
                                  id={`select-list-${index}`}
                                  value={statusRealizacjiFields[index]} // Use statusRealizacjiFields[index] for value
                                  onChange={(e) =>
                                    handleStatusRealizacjiChange(
                                      e.target.value,
                                      index,
                                    )
                                  }
                                  name={`statusRealizacji${index}`}
                                  className={classes.marginBottom}
                                >
                                  <MenuItem value="Do przygotowania">
                                    Do przygotowania
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </div>
                            {/* Nr. Faktury Field */}
                            {index > 0 && (
                              <Button
                                variant="contained"
                                color="secondary"
                                className={classes.marginBottom}
                                onClick={() => removeFields(index)}
                              >
                                -
                              </Button>
                            )}
                          </div>
                        ))}

                        {/* Common Add Fields button */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={addFields}
                          className={classes.marginBottom}
                        >
                          +
                        </Button>
                      </>
                    )}

                    {formData.rodzaj === "Odprawa czasowa eksport" && (
                      <>
                        <div
                          ref={exporterDropdownRef}
                          style={{ position: "relative" }}
                          className={`${classes.marginBottom} ${
                            isFetching ? classes.fadeOut : classes.fadeIn
                          }`}
                        >
                          <TextField
                            required
                            label="Eksporter"
                            name="eksporter"
                            value={formData.eksporter}
                            onChange={handleChange}
                            autoComplete="off"
                            fullWidth
                            disabled={isFetching}
                            onClick={() => handleListOpen("eksporter")}
                            onFocus={() => handleListOpen("eksporter")}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <span style={{ cursor: "pointer" }}>▼</span>
                              ),
                            }}
                          />
                          {listOpen && listType === "eksporter" && (
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 1300,
                                width: "120%",
                                maxHeight: 450,
                                backgroundColor: "white",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                marginTop: "4px",
                                left: "-10%",
                              }}
                            >
                              <div
                                style={{
                                  padding: "8px",
                                  backgroundColor: "#f5f5f5",
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                <TextField
                                  placeholder="Szukaj eksportera..."
                                  value={exporterSearchText}
                                  onChange={(e) =>
                                    filterExporters(e.target.value)
                                  }
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  autoFocus
                                  InputProps={{
                                    style: {
                                      fontSize: "14px",
                                      backgroundColor: "white",
                                    },
                                  }}
                                />
                              </div>
                              <div
                                style={{ maxHeight: 350, overflowY: "auto" }}
                              >
                                {exporterFilteredList.map((exporter, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      padding: "8px 16px",
                                      cursor: "pointer",
                                      borderBottom: "1px solid #f0f0f0",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.backgroundColor =
                                        "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.target.style.backgroundColor =
                                        "transparent")
                                    }
                                    onClick={() => {
                                      setFormData((prevData) => ({
                                        ...prevData,
                                        eksporter: exporter,
                                      }));
                                      setExporterSearchText("");
                                      setListOpen(false);
                                    }}
                                  >
                                    {exporter}
                                  </div>
                                ))}
                                {exporterFilteredList.length === 0 && (
                                  <div
                                    style={{
                                      padding: "16px",
                                      textAlign: "center",
                                      color: "#999",
                                    }}
                                  >
                                    Brak wyników
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Mapowane sekcje Importer + Faktura + Status */}
                        {formData.fields.map((field, index) => (
                          <div
                            key={index}
                            style={{
                              borderBottom: "1px solid #ccc",
                              paddingBottom: 16,
                              marginBottom: 24,
                            }}
                          >
                            {/* Importer */}
                            <TextField
                              label={`Importer ${index + 1}`}
                              name={`importer${index}`}
                              value={field.importer || ""}
                              autoComplete="off"
                              onChange={(e) =>
                                handleFieldsChange(
                                  "importer",
                                  e.target.value,
                                  index,
                                )
                              }
                              fullWidth
                              style={{ marginBottom: "16px" }}
                            />

                            {/* Nr Faktury */}
                            <TextField
                              type="text"
                              label="Podaj ilość faktur"
                              value={
                                field.invoiceNumbers.length === 0
                                  ? ""
                                  : String(field.invoiceNumbers.length)
                              }
                              onChange={(e) => {
                                let input = e.target.value;

                                // Pozwól na puste pole
                                if (input === "") {
                                  handleInvoiceCountChange(index, 0); // lub może: handleInvoiceCountChange(index, null);
                                  return;
                                }

                                // Usuń wiodące zera
                                input = input.replace(/^0+/, "");

                                // Jeśli pozostało coś po usunięciu zer i to jest liczba — ustaw
                                const count = parseInt(input, 10);
                                if (!isNaN(count)) {
                                  handleInvoiceCountChange(index, count);
                                }
                              }}
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                              }}
                              fullWidth
                              style={{ marginBottom: "16px" }}
                            />
                            {field.invoiceNumbers.map((invoice, invoiceIdx) => (
                              <div
                                key={invoiceIdx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "8px",
                                }}
                              >
                                <TextField
                                  required
                                  label={`Nr. Faktury ${invoiceIdx + 1}`}
                                  name={`nrFaktury${index}_${invoiceIdx}`}
                                  value={invoice}
                                  autoComplete="off"
                                  onChange={(e) => {
                                    const updatedFields = [...formData.fields];
                                    updatedFields[index].invoiceNumbers[
                                      invoiceIdx
                                    ] = e.target.value;
                                    setFormData({
                                      ...formData,
                                      fields: updatedFields,
                                    });
                                  }}
                                  fullWidth
                                />
                                <InvoiceInfoTooltip />
                              </div>
                            ))}

                            {/* Status realizacji */}
                            <FormControl
                              fullWidth
                              className={classes.marginBottom}
                            >
                              <InputLabel id={`select-list-label-${index}`}>
                                Status realizacji {index + 1}*
                              </InputLabel>
                              <Select
                                required
                                labelId={`select-list-label-${index}`}
                                id={`select-list-${index}`}
                                value={field.statusRealizacji || ""}
                                onChange={(e) =>
                                  handleFieldsChange(
                                    "statusRealizacji",
                                    e.target.value,
                                    index,
                                  )
                                }
                                name={`statusRealizacji${index}`}
                              >
                                <MenuItem value="Do przygotowania">
                                  Do przygotowania
                                </MenuItem>
                              </Select>
                            </FormControl>

                            {/* Przycisk "-" */}
                            {index > 0 && (
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => removeFields(index)}
                                className={classes.marginBottom}
                              >
                                -
                              </Button>
                            )}
                          </div>
                        ))}

                        {/* Przycisk "+" */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={addFields}
                          className={classes.marginBottom}
                        >
                          +
                        </Button>
                      </>
                    )}

                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">
                        Czy zostały dostarczone dokumenty przed przybyciem*
                      </FormLabel>
                      <RadioGroup
                        aria-label="Czy zostały dostarczone dokumenty przed przybyciem?"
                        name="dokPrzedPrzybyciem"
                        value={formData.dokPrzedPrzybyciem}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value="Tak"
                          control={<Radio color="primary" />}
                          label="Tak"
                        />
                        <FormControlLabel
                          value="Nie"
                          control={<Radio color="primary" />}
                          label="Nie"
                        />
                      </RadioGroup>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel id="select-list-label">
                        Osoba przygotowująca odprawe
                      </InputLabel>
                      <Select
                        labelId="select-list-label"
                        id="select-list"
                        value={formData.osobaPrzygotowujacaOdprawe} // Update this line to use the correct state field
                        onChange={handleChange} // Update the onChange event handler to use the handleChange function
                        name="osobaPrzygotowujacaOdprawe" // Update this line to use the correct state field name
                        className={classes.marginBottom} // Apply marginBottom style to the TextField
                        disabled={isTemplateBuilderMode}
                      >
                        <MenuItem value="" style={{ fontWeight: "bold" }}>
                          Brak przypisania osoby
                        </MenuItem>
                        {uzytkownicyList.map((item) => (
                          <MenuItem key={item.id} value={item.name}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {isTemplateBuilderMode && <TemplateDynamicFieldTooltip />}
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="select-list-label">
                        Osoba wysyłająca odprawe
                      </InputLabel>
                      <Select
                        labelId="select-list-label"
                        id="select-list"
                        value={formData.osobaWysylajacaOdprawe} // Update this line to use the correct state field
                        onChange={handleChange} // Update the onChange event handler to use the handleChange function
                        name="osobaWysylajacaOdprawe" // Update this line to use the correct state field name
                        className={classes.marginBottom} // Apply marginBottom style to the TextField
                        disabled={isTemplateBuilderMode}
                      >
                        <MenuItem value="" style={{ fontWeight: "bold" }}>
                          Brak przypisania osoby
                        </MenuItem>
                        {uzytkownicyList.map((item) => (
                          <MenuItem key={item.id} value={item.name}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {isTemplateBuilderMode && <TemplateDynamicFieldTooltip />}
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="select-list-label">
                        Osoba monitorująca odprawe
                      </InputLabel>
                      <Select
                        labelId="select-list-label"
                        id="select-list"
                        value={formData.osobaMonitorujacaOdprawe} // Update this line to use the correct state field
                        onChange={handleChange} // Update the onChange event handler to use the handleChange function
                        name="osobaMonitorujacaOdprawe" // Update this line to use the correct state field name
                        className={classes.marginBottom} // Apply marginBottom style to the TextField
                        disabled={isTemplateBuilderMode}
                      >
                        <MenuItem value="" style={{ fontWeight: "bold" }}>
                          Brak przypisania osoby
                        </MenuItem>
                        {uzytkownicyList.map((item) => (
                          <MenuItem key={item.id} value={item.name}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {isTemplateBuilderMode && <TemplateDynamicFieldTooltip />}
                    </FormControl>
                    <TextField
                      label="Nr. Rejestracyjny Pojazdu/Kuriera"
                      name="nrRejestracyjnyPojazdyKurier"
                      value={formData.nrRejestracyjnyPojazdyKurier}
                      onChange={handleChange}
                      autoComplete="off"
                      disabled={isTemplateBuilderMode}
                      fullWidth // Use fullWidth for TextField to match the width of the Paper container
                      className={classes.marginBottom} // Apply marginBottom style to the TextField
                    />
                    {isTemplateBuilderMode && <TemplateDynamicFieldTooltip />}
                    {formData.rodzaj === "Tranzyt" ? (
                      <FormControl fullWidth className={classes.marginBottom}>
                        <InputLabel id="select-list-label">
                          Urząd celny wyjścia*
                        </InputLabel>
                        <Select
                          labelId="select-list-label"
                          id="select-list"
                          value={formData.lokalizacjaTowaru}
                          onChange={handleChange}
                          name="lokalizacjaTowaru"
                          className={classes.marginBottom}
                          required
                        >
                          {lokalizacjaList.map((item) => (
                            <MenuItem key={item.id} value={item.name}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <FormControl fullWidth className={classes.marginBottom}>
                        <InputLabel id="select-list-label">
                          Lokalizacja towaru
                        </InputLabel>
                        <Select
                          labelId="select-list-label"
                          id="select-list"
                          value={formData.lokalizacjaTowaru}
                          onChange={handleChange}
                          name="lokalizacjaTowaru"
                          className={classes.marginBottom}
                        >
                          {lokalizacjaList.map((item) => (
                            <MenuItem key={item.id} value={item.name}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    <label>
                      Wybierz adresata dokumentów
                      <br />
                    </label>
                    <label>
                      Automat
                      <Checkbox
                        checked={automatChecked}
                        onChange={handleCheckboxChange}
                        //disabled={!!formData.adresatDokumentow} // Disable the checkbox when TextField has input
                      />
                    </label>
                    <TextField
                      label="Wpisz mail/numer tel. Jeśli trzeba wysłać numer własny/dokumenty celne ręcznie"
                      name="adresatDokumentow"
                      value={formData.adresatDokumentow}
                      onChange={handleChange}
                      fullWidth
                      autoComplete="off"
                      InputLabelProps={{
                        style: {
                          fontSize: "0.9rem",
                          transform: "translateY(-3px)",
                        }, // Przesunięcie etykiety do góry
                      }}
                      className={classes.marginBottom}
                      disabled={automatChecked}
                    />

                    <TextField
                      label="Adres email do ticketu"
                      name="emailKorespondencyjny"
                      value={formData.emailKorespondencyjny}
                      onChange={handleChange}
                      autoComplete="off"
                      fullWidth // Use fullWidth for TextField to match the width of the Paper container
                      className={classes.marginBottom} // Apply marginBottom style to the TextField
                      multiline // Ustawia TextField jako wielowierszowy
                      rows={4} // Liczba wierszy na start (możesz dostosować do swoich potrzeb)
                    />
                    <label>
                      <br />
                      Oznacz braki w odprawie
                      <br />
                    </label>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.brakCertyfikatyBox}
                            onChange={handleCheckboxChangeUwagi}
                            name="brakCertyfikatyBox"
                          />
                        }
                        label="Certyfikaty"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.brakIncotermsBox}
                            onChange={handleCheckboxChangeUwagi}
                            name="brakIncotermsBox"
                          />
                        }
                        label="Dane o INCOTERMS + miejscowość"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.DSKBox}
                            onChange={handleCheckboxChangeUwagi}
                            name="DSKBox"
                          />
                        }
                        label="DSK"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.brakEksportZKrajuWywozuBox}
                            onChange={handleCheckboxChangeUwagi}
                            name="brakEksportZKrajuWywozuBox"
                          />
                        }
                        label="Eksport z kraju wywozu"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.eoriBox}
                            onChange={handleCheckboxChangeUwagi}
                            name="eoriBox"
                          />
                        }
                        label="EORI"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.fakturaBox}
                            onChange={handleCheckboxChangeUwagi}
                            name="fakturaBox"
                          />
                        }
                        label="Faktura"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.brakFakturyEdytowalnejBox}
                            onChange={handleCheckboxChangeUwagi}
                            name="brakFakturyEdytowalnejBox"
                          />
                        }
                        label="Faktura edytowalna"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.informacjeOTowarzeBox}
                            onChange={handleCheckboxChangeUwagi}
                            name="informacjeOTowarzeBox"
                          />
                        }
                        label="Informacje o Towarze"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.kosztyTransportuBox}
                            onChange={handleCheckboxChangeUwagi}
                            name="kosztyTransportuBox"
                          />
                        }
                        label="Koszty Transportu"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.listPrzewozowyBox}
                            onChange={handleCheckboxChangeUwagi}
                            name="listPrzewozowyBox"
                          />
                        }
                        label="List Przewozowy"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.brakPackingListEdytowalnegoBox}
                            onChange={handleCheckboxChangeUwagi}
                            name="brakPackingListEdytowalnegoBox"
                          />
                        }
                        label="Packing list edytowalny"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.brakExcelBox}
                            onChange={handleCheckboxChangeUwagi}
                            name="brakExcelBox"
                          />
                        }
                        label="Plik Excel z danymi do odprawy"
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.swiadectwoBox}
                            onChange={handleCheckboxChangeUwagi}
                            name="swiadectwoBox"
                          />
                        }
                        label="Świadectwo"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.T1Box}
                            onChange={handleCheckboxChangeUwagi}
                            name="T1Box"
                          />
                        }
                        label="T1"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.brakUpowaznieniaBox}
                            onChange={handleCheckboxChangeUwagi}
                            name="brakUpowaznieniaBox"
                          />
                        }
                        label="Upoważnienie"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.brakInneBox}
                            onChange={handleCheckboxChangeUwagi}
                            name="brakInneBox"
                          />
                        }
                        label="Inny"
                      />
                    </FormGroup>
                    {formData.brakInneBox && (
                      <TextField
                        label="Opisz inny brak"
                        name="brakInne"
                        value={formData.brakInne}
                        onChange={handleChange}
                        fullWidth
                        className={classes.marginBottom}
                        inputProps={{ maxLength: 255 }}
                      />
                    )}
                    <Grid item xs={12}>
                      {formData.rodzajTransportu !== "Magazyn Warszawa" && (
                        <Button
                          type={isTemplateBuilderMode ? "button" : "submit"}
                          variant="contained"
                          color="primary"
                          fullWidth
                          disabled={
                            isTemplateBuilderMode
                              ? templateSaveInProgress ||
                                typeof onTemplateSave !== "function"
                              : isSubmitting
                          }
                          onClick={
                            isTemplateBuilderMode
                              ? () => onTemplateSave && onTemplateSave()
                              : undefined
                          }
                        >
                          {isTemplateBuilderMode
                            ? templateSaveInProgress
                              ? "Zapisywanie szablonu..."
                              : "Zapisz szablon"
                            : isSubmitting
                              ? "Zapisywanie..."
                              : "Zapisz"}
                        </Button>
                      )}
                    </Grid>
                  </>
                )}
              </Grid>
              {isMwNowyHandlingMulti && (
                <>
                  <Grid item xs={12}>
                    <MwNowyHandlingTable
                      rowCount={Number(mwNowyHandlingCount)}
                      companies={magazynWarszawaCompaniesList}
                      value={mwNowyHandlingRows}
                      onChange={setMwNowyHandlingRows}
                      onAddCompany={handleAddMagazynWarszawaCompany}
                      revealErrorsSignal={mwNowyHandlingRevealSignal}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      fullWidth
                      style={{ maxWidth: 400, margin: "16px auto 0", display: "flex" }}
                      disabled={isSubmitting}
                      onClick={() => submitMwNowyHandling("multiple")}
                    >
                      {isSubmitting ? "Zapisywanie..." : "Zapisz"}
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </form>
        </Paper>
      </main>
      <ToastContainer position="top-center" />
    </div>
  );
};

export default Form;
